/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-05-14.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description General Game Backend Client error class
 *
 * @property {ERROR_CODE} code Error code
 * @property {mixed} [parentError] Error which caused this error
 *
 * @extends Error
 * @memberof module:error
 */
class GBCError extends Error {
  /**
   * @constructor
   * @param {ERROR_CODE} code Error code
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(code, message, parentError) {
    super(message);
    this.code = code;
    this.parentError = parentError;
    this.thisFileName = this.fileName;
    this.thisLineNumber = this.lineNumber;
    this.thisColumnNumber = this.columnNumber;
  }

  toString() {
    try {
      const code = this.code || 'No error code';
      const msg = this.message || 'No error message';
      const file = this.thisFileName;
      const line = this.thisLineNumber;
      const column = this.thisColumnNumber;
      const detailParts = [
        file ? `file: ${file}` : null,
        line ? `line: ${line}` : null,
        column ? `column: ${column}` : null,
      ].filter((e) => !!e);
      const details = detailParts.length
        ? ` [[${detailParts.join(' ')}]]]`
        : '';
      let parentString = null;
      if (this.parentError) {
        parentString =
          typeof this.parentError.toString === 'function'
            ? this.parentError.toString()
            : 'Parent error does not have a toString function';
      }
      let stackString = null;
      if (this.stack) {
        stackString =
          typeof this.stack.toString === 'function'
            ? this.stack.toString()
            : 'Stack does not have a toString function';
      }
      const parentPart = parentString
        ? `with parent error:\n${parentString}`
        : `with stack:\n${stackString || 'No stack available'}`;
      return `GbcError: [${code}] ${msg}${details} ${parentPart}`;
    } catch (error) {
      return `Failed to execute toString on error due to error: ${error}`;
    }
  }

  get fileName() {
    return this.parentError
      ? this.parentError.fileName
      : this.thisFileName || 'unknown';
  }

  get lineNumber() {
    return this.parentError
      ? this.parentError.lineNumber
      : this.thisLineNumber || 'n/a';
  }

  get columnNumber() {
    return this.parentError
      ? this.parentError.columnNumber
      : this.thisColumnNumber || 'n/a';
  }
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-07-04.
 * Copyright © Softgames 2019 - present
 */

/**
 * @constant PROVIDER
 * @type {Object}
 * @property {String} FACEBOOK_INSTANT_GAME Facebook instant game
 * @property {String} ODNOKLASSNIKI Odnoklassniki
 * @property {String} TWITCH Twitch
 * @property {String} LINE Line
 *
 * @memberof module:enums
 */
const PROVIDER = {
  FACEBOOK_INSTANT_GAME: 'fbig',
  ODNOKLASSNIKI: 'ok',
  TWITCH: 'twitch',
  LINE: 'line',
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-05-27.
 * Copyright © Softgames 2019 - present
 */

/**
 * @constant ENVIRONMENT
 *
 * @description
 * Only used for LINE
 *
 *
 * @type {Object}
 * @property {String} SANDBOX
 * @property {String} STAGING
 * @property {String} PRODUCTION
 *
 * @memberof module:enums
 */
const ENVIRONMENT = {
  SANDBOX: 'SANDBOX',
  STAGING: 'STAGING',
  PRODUCTION: 'PRODUCTION',
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-05-27.
 * Copyright © Softgames 2019 - present
 */

/**
 * @constant ERROR_CODE
 * @type {Object}
 * @property {String} UNKNOWN unknown error
 * @property {String} INTERNAL_ERROR Internal server error
 * @property {String} UNKNOWN_GRAPHQL_ERROR Identify that request has been send to the game backend but response with unknown error.
 * @property {String} BAD_RESPONSE Identify that client got bad response from server. This can means that response from server has been changed.
 * @property {String} CONNECTION_NOT_INITIALIZED Identify problem with connection do the game backend.
 * @property {String} WRONG_CREDENTIALS Given credentials are invalid, register again to get new credentials
 * @property {String} INVALID_PARAMETER Identify that validation of the input parameters has failed. We should check error message for details.
 * @property {String} TIMEOUT Identify problem with a timeout.
 * @property {String} INVALID_CALL Identifies errors that are caused by an invalid time of calling a function or an unnecessary call. Check message for details.
 * @property {String} NOT_FOUND Identify problem with request to the game backend that requested resource cannot be found. For example when we send invalid leaderboard.
 * @property {String} NOT_SUPPORTED Identify problem with request to the game backend that a used functionality is not supported for the current environment.
 * @property {String} INVALID_CONFIG Identify problem with request to the game backend that a configuration for the game in GB is not how it needs to be for the function to work. Check the documentation and ADM for the configuration of your game.
 * @property {String} FORBIDDEN Identify problem with request to the game backend that action is forbidden. For example when post score is sent to leaderboard which is disabled for user.
 * @property {String} NOT_AUTHORIZED Identify problem with not authorized request to the game backend. For example when invalid authToken will be used for login method.
 * @property {String} PURCHASE_PROVIDER_INSUFFICIENT_BALANCE We got the response from the purchase provider's api that there was an insufficient balance
 * @property {String} PURCHASE_PROVIDER_INVALID_AMOUNT We got the response from the purchase provider's api that there was an invalid amount requested to be purchased
 * @property {String} PURCHASE_PROVIDER_UNKNOWN_ITEM We got the response from the purchase provider's api that there was an unknown item requested to be purchased
 * @property {String} PURCHASE_PROVIDER_INVALID_USER_INFORMATION We got the response from the purchase provider's api that there was invalid user information
 * @property {String} PURCHASE_PROVIDER_RECEIPT_ERROR We got the response from the purchase provider's api that there was a problem with the receipt generation
 * @property {String} PURCHASE_PROVIDER_OS_ERROR We got the response from the purchase provider's api that the provided OS was invalid
 * @property {String} BOT_SERVICE_ERROR Identify problem with response from bot service
 * @property {String} WEBSOCKET_ERROR Error caused by a Websocket error event
 *
 * @memberof module:enums
 */
const ERROR_CODE = {
  UNKNOWN: 'Unknown',
  INTERNAL_ERROR: 'InternalError',
  UNKNOWN_GRAPHQL_ERROR: 'UnknownGraphqlError',
  BAD_RESPONSE: 'BadResponse',
  CONNECTION_NOT_INITIALIZED: 'ConnectionNotInitialized',
  WRONG_CREDENTIALS: 'WrongCredentials',
  INVALID_PARAMETER: 'InvalidParameter',
  TIMEOUT: 'timeout',
  INVALID_CALL: 'InvalidCall',
  NOT_FOUND: 'NotFound',
  NOT_SUPPORTED: 'NotSupported',
  INVALID_CONFIG: 'InvalidConfig',
  FORBIDDEN: 'Forbidden',
  NOT_AUTHORIZED: 'NotAuthorized',
  PURCHASE_PROVIDER_INSUFFICIENT_BALANCE: 'PurchaseProviderInsufficientBalance',
  PURCHASE_PROVIDER_INVALID_AMOUNT: 'PurchaseProviderInvalidAmount',
  PURCHASE_PROVIDER_UNKNOWN_ITEM: 'PurchaseProviderUnknownItem',
  PURCHASE_PROVIDER_INVALID_USER_INFORMATION:
    'PurchaseProviderInvalidUserInformation',
  PURCHASE_PROVIDER_RECEIPT_ERROR: 'PurchaseProviderReceiptError',
  PURCHASE_PROVIDER_OS_ERROR: 'PurchaseProviderOsError',
  PURCHASE_UNKNOWN_ERROR: 'PurchaseProviderUnknownError',
  PURCHASE_PROVIDER_CANCELED: 'PurchaseProviderCanceled',
  BOT_SERVICE_ERROR: 'BotServiceError',
  WEBSOCKET_ERROR: 'WebsocketError',
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-05-27.
 * Copyright © Softgames 2019 - present
 */

/**
 * @constant OS
 *
 * @description
 * Only used for LINE
 *
 * @type {Object}
 * @property {String} IOS Used for Apple's iOS
 * @property {String} AOS Used for Android
 *
 * @memberof module:enums
 */
const OS = {
  IOS: 'IOS',
  AOS: 'AOS',
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-05-27.
 * Copyright © Softgames 2019 - present
 */

/**
 * @constant SEVERITY
 *
 * @description
 * Possible Severities for logging
 *
 * @type {Object}
 * @property {String} DEBUG
 * @property {String} INFO
 * @property {String} NOTICE
 * @property {String} WARN
 * @property {String} ERROR
 * @property {String} FATAL
 * @property {String} DISABLE
 *
 * @memberof module:enums
 */
const SEVERITY$3 = {
  DEBUG: -1,
  INFO: 0,
  NOTICE: 1,
  WARN: 2,
  ERROR: 3,
  FATAL: 4,
  DISABLE: 5,
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

var enums = /*#__PURE__*/Object.freeze({
  __proto__: null,
  PROVIDER: PROVIDER,
  ENVIRONMENT: ENVIRONMENT,
  ERROR_CODE: ERROR_CODE,
  OS: OS,
  SEVERITY: SEVERITY$3
});

/**
 * Created by René Simon<rene.simon@softgames.de> on 2020-02-12.
 * Copyright © Softgames 2020 - present
 */

/**
 * @class
 *
 * @description  Required configuration not done on server side
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCConfigurationError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(message, parentError) {
    super(ERROR_CODE.INVALID_CONFIG, message, parentError);
  }
}

function isNonEmptyString$3(str) {
  return !!str && typeof str === 'string' && !!str.trim();
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-05-14.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description No connection error class. When is problem with connection to the game backend.
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCErrorNoConnection extends GBCError {
  /**
   * @constructor
   * @param  {string} message Descriptive text of the error
   */
  constructor(message) {
    const msg = isNonEmptyString$3(message)
      ? message
      : 'Connection not established';
    super(ERROR_CODE.CONNECTION_NOT_INITIALIZED, msg);
  }
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-05-14.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description  Validation error class. When function parameters validation will fail.
 *
 * @property {mixed} validExample A valid example for the property that is invalid
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCErrorValidationFailed extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} validExample A valid example for the property that is invalid
   */
  constructor(message, validExample) {
    super(ERROR_CODE.INVALID_PARAMETER, message);
    this.validExample = validExample;
  }

  /**
   * @description
   * Returns the valid example given on construction
   *
   * @return {mixed}
   */
  getValidExample() {
    return this.validExample;
  }
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2020-04-27.
 * Copyright © Softgames 2020 - present
 */

/**
 * @class
 *
 * @description Bad response from server
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCBadResponseError extends GBCError {
  /**
   * @constructor
   * @param  {string} message Descriptive text of the error
   */
  constructor(message) {
    const msg = isNonEmptyString$3(message) ? message : 'Bad response error';
    super(ERROR_CODE.BAD_RESPONSE, msg);
  }
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-10-30.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description Internal error
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCInternalError extends GBCError {
  /**
   * @constructor
   * @param  {string} message Descriptive text of the error
   */
  constructor(message) {
    const msg = isNonEmptyString$3(message) ? message : 'Some internal error';
    super(ERROR_CODE.INTERNAL_ERROR, msg);
  }
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-05-14.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description  General GraphQL error class.
 *
 * @property {number} messageId Id of the message send/received to/from Game Backend
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCGraphQLError extends GBCError {
  /**
   * @constructor
   * @param {ERROR_CODE} code Error code
   * @param {string} message Descriptive text of the error
   * @param {number} messageId Id of the message send/received to/from Game Backend
   */
  constructor(code, message, messageId) {
    super(code, message);
    this.messageId = messageId;
  }

  /**
   * @description
   * Returns the message ID provided on construction
   *
   * @return {number}
   */
  getMessageId() {
    return this.messageId;
  }
}

/**
 * Created by Bartosz Wróbel<bbartosz.wrobel@softgames.de> on 2020-04-26.
 * Copyright © Softgames 2020 - present
 */

/**
 * @class
 *
 * @description  Identifies errors that are caused by a query execution timeout. Check message for details.
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCTimeoutError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(message, parentError) {
    super(ERROR_CODE.TIMEOUT, message, parentError);
  }
}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2019-07-19.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description  Identifies errors that are caused by an invalid time of calling a function or an unnecessary call. Check message for details.
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCInvalidCallError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(message, parentError) {
    super(ERROR_CODE.INVALID_CALL, message, parentError);
  }
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-05-14.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description
 * Known GraphQL error class. When graphQL request will fail and we have defined error code.
 *
 * @extends GBCGraphQLError
 * @memberof module:error
 */
class GBCKnownGraphQLError extends GBCGraphQLError {}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2019-07-19.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description  Identify problem with not authorized request to the game backend. For example when invalid authToken will be used for login method.
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCNotAuthorizedError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(message, parentError) {
    super(ERROR_CODE.NOT_AUTHORIZED, message, parentError);
  }
}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2020-02-12.
 * Copyright © Softgames 2020 - present
 */

/**
 * @class
 *
 * @description  Something went wrong in the payment process
 *
 * @property {boolean} canceled - true if the payment process was canceled by user
 * @property {boolean} insufficientBalance - true if the the user did not have enough money
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCPaymentError extends GBCError {
  /**
   * @constructor
   * @param {ERROR_CODE} code Error code
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(code, message, parentError) {
    super(code, message, parentError);
    this.canceled = code === ERROR_CODE.PURCHASE_PROVIDER_CANCELED;
    this.insufficientBalance =
      code === ERROR_CODE.PURCHASE_PROVIDER_INSUFFICIENT_BALANCE;
  }
}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2019-07-19.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description  Unknown error
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCUnknownError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(message, parentError) {
    super(ERROR_CODE.UNKNOWN, message, parentError);
  }
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-05-14.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description
 * Unknown GraphQL error class. When graphQL request will fail and we don't have defiend error code.
 *
 * @extends GBCGraphQLError
 * @memberof module:error
 */
class GBCUnknownGraphQLError extends GBCGraphQLError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {number} messageId Id of the message send/received to/from Game Backend
   */
  constructor(message, messageId) {
    super(ERROR_CODE.UNKNOWN_GRAPHQL_ERROR, message, messageId);
  }
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-06-24.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description Bot service error. When is problem with bot service.
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCBotServiceError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   */
  constructor(message) {
    const msg = isNonEmptyString$3(message) ? message : 'Bot service error';
    super(ERROR_CODE.BOT_SERVICE_ERROR, msg);
  }
}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2019-07-19.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description  Error caused by a Websocket error event
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCWebsocketError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} errorEvent Error event that was causing this error
   */
  constructor(message, errorEvent) {
    super(ERROR_CODE.WEBSOCKET_ERROR, message);
    this.errorEvent = errorEvent;
  }
}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2019-07-19.
 * Copyright © Softgames 2019 - present
 */

/**
 * @class
 *
 * @description  Given credentials are invalid, register again to get new credentials
 *
 * @extends GBCError
 * @memberof module:error
 */
class GBCWrongCredentialsError extends GBCError {
  /**
   * @constructor
   * @param {string} message Descriptive text of the error
   * @param {mixed} [parentError=] Error that was causing this error
   */
  constructor(message, parentError) {
    super(ERROR_CODE.WRONG_CREDENTIALS, message, parentError);
  }
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-06.
 * Copyright © Softgames 2019 - present
 */

var errors = /*#__PURE__*/Object.freeze({
  __proto__: null,
  GBCError: GBCError,
  GBCConfigurationError: GBCConfigurationError,
  GBCErrorNoConnection: GBCErrorNoConnection,
  GBCErrorValidationFailed: GBCErrorValidationFailed,
  GBCBadResponseError: GBCBadResponseError,
  GBCInternalError: GBCInternalError,
  GBCGraphQLError: GBCGraphQLError,
  GBCTimeoutError: GBCTimeoutError,
  GBCInvalidCallError: GBCInvalidCallError,
  GBCKnownGraphQLError: GBCKnownGraphQLError,
  GBCNotAuthorizedError: GBCNotAuthorizedError,
  GBCPaymentError: GBCPaymentError,
  GBCUnknownError: GBCUnknownError,
  GBCUnknownGraphQLError: GBCUnknownGraphQLError,
  GBCBotServiceError: GBCBotServiceError,
  GBCWebsocketError: GBCWebsocketError,
  GBCWrongCredentialsError: GBCWrongCredentialsError
});

function QueuePromise(
  promiseFlag,
  assertion,
  timeoutMs,
  name = 'Undefined',
) {
  let timeoutId;

  const p1 = promiseFlag.assertState(assertion);
  const p2 = new Promise((resolve, reject) => {
    timeoutId = setTimeout(
      () =>
        reject(
          new GBCError(
            ERROR_CODE.TIMEOUT,
            `Timed out after ${timeoutMs} ms because flag "${promiseFlag.name}" did not reach asserted state. Promise queue name ${name}`,
          ),
        ),
      timeoutMs,
    );
  });
  const all = Promise.race([p1, p2]);

  (async () => {
    try {
      await all;
    } catch (error) {
      // [RS] No need to be handled here, because it will be handled by the receivers of the promise
      // [RS] But we need a catch clause here to not have an uncaught promise rejection
    } finally {
      clearTimeout(timeoutId);
    }
  })();

  return all;
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2020-01-17.
 * Copyright © Softgames 2020 - present
 */

/**
 * @memberof module:base
 * @constant SEVERITY
 * @type {Object}
 * @property {Number} DEBUG - -1
 * @property {Number} INFO - 0
 * @property {Number} NOTICE - 1
 * @property {Number} WARN - 2
 * @property {Number} ERROR - 3
 * @property {Number} FATAL - 4
 * @property {Number} DISABLE - 5
 */
const SEVERITY$1$1 = {
  DEBUG: -1,
  INFO: 0,
  NOTICE: 1,
  WARN: 2,
  ERROR: 3,
  FATAL: 4,
  DISABLE: 5,
};

const allowedSeverities$2 = Object.values(SEVERITY$1$1);

function isValidSeverity$1(severity) {
  return allowedSeverities$2.includes(severity);
}

function isString$4(str) {
  return typeof str === 'string';
}

function isNonEmptyString$2(str) {
  return !!str && isString$4(str) && !!str.trim();
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2020-01-17.
 * Copyright © Softgames 2020 - present
 */

const SEVERITY$2 = SEVERITY$1$1;

let NAME$2 = null;

const consoleExists$1 =
  typeof console !== 'undefined' &&
  !!console &&
  typeof console.log === 'function';

function getLogFunction$1(target, ...alternatives) {
  if (typeof console[target] === 'function') {
    return console[target].bind(console);
  }
  if (!alternatives || !alternatives.length) {
    return console.log.bind(console);
  }
  return getLogFunction$1(...alternatives);
}

function getLog$2(logLevel) {
  return {
    log:
      !consoleExists$1 || logLevel > SEVERITY$2.INFO
        ? () => {}
        : getLogFunction$1('info'),
    debug:
      !consoleExists$1 || logLevel > SEVERITY$2.DEBUG
        ? () => {}
        : getLogFunction$1('debug'),
    info:
      !consoleExists$1 || logLevel > SEVERITY$2.INFO
        ? () => {}
        : getLogFunction$1('info'),
    notice:
      !consoleExists$1 || logLevel > SEVERITY$2.NOTICE
        ? () => {}
        : getLogFunction$1('notice', 'info'),
    warn:
      !consoleExists$1 || logLevel > SEVERITY$2.WARN
        ? () => {}
        : getLogFunction$1('warn', 'notice', 'info'),
    error:
      !consoleExists$1 || logLevel > SEVERITY$2.ERROR
        ? () => {}
        : getLogFunction$1('error'),
    fatal:
      !consoleExists$1 || logLevel > SEVERITY$2.FATAL
        ? () => {}
        : getLogFunction$1('fatal', 'error'),
  };
}

let currentLogger$1 = getLog$2(SEVERITY$2.DISABLE);

/**
 * @memberof module:base
 * @function setLogSeverity
 * @description
 * Sets the  project's severity to log.
 * The given severity and all higher severities are logged.
 *
 * @param {Object} options
 * @param {Number} options.severity - Severity of type SEVERITY
 * @example
 * import { setLogSeverity, SEVERITY } from '@sgorg/fe-logger';
 *
 * setLogSeverity({ severity: SEVERITY.WARN });
 * // After this all warn, error and fatal logs are printed in the console.
 *
 * @returns {void}
 */
function setLogSeverity$1({ severity } = {}) {
  if (!isValidSeverity$1(severity)) {
    throw new Error(
      `Severity must be on of ${allowedSeverities$2.join(
        ', ',
      )}. Suggest to use SEVERITY enum.`,
    );
  }
  currentLogger$1 = getLog$2(severity);
}

/**
 * @memberof module:base
 * @function init
 * @description
 * Initializes all loggers for the project.
 * Sets the project name and the project's severity to log (see also setLogSeverity).
 *
 * @param {Object} options
 * @param {String} options.name - Name of the logger
 * @param {Number} options.severity - Severity of type SEVERITY
 * @example
 * import { init, SEVERITY } from '@sgorg/fe-logger';
 *
 * init({ name: 'client', severity: SEVERITY.DEBUG });
 *
 * @returns {void}
 */
function init$2({ name, severity = SEVERITY$2.DISABLE } = {}) {
  if (!isNonEmptyString$2(name)) {
    throw new Error(`Name must be a non empty string`);
  }
  setLogSeverity$1({ severity });
  NAME$2 = name;
}

function getLogHandler$1(severity, name) {
  if (typeof currentLogger$1[severity] !== 'function') {
    throw new Error(
      `There is no log method with name "${severity}" for generating logger "${name}"`,
    );
  }
  return (...args) => {
    try {
      currentLogger$1[severity](`[${NAME$2}][${severity}]${name}`, ...args);
    } catch (error) {
      // [RS] Executing a log never generates an error
    }
  };
}

/**
 * @memberof module:base
 * @function getLogger
 * @description
 * Creates a new logger instance. By default all logs are disabled. Check init or setLogSeverity to change that.
 *
 * @param {Object} options
 * @param {String} options.name - Name of the logger
 * @example
 * import { getLogger } from '@sgorg/fe-logger';
 *
 * const log = getLogger({ name: 'utils' });
 *
 * @returns {module:base.Logger}
 */
function getLogger$2({ name } = {}) {
  if (!isNonEmptyString$2(name)) {
    throw new Error(`Name must be non-empty string`);
  }
  return Object.keys(currentLogger$1).reduce(
    (m, severity) => ({
      ...m,
      [severity]: getLogHandler$1(severity, name),
    }),
    {},
  );
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

const NAME$1 = 'GBC';

init$2({ name: NAME$1, severity: SEVERITY$2.DISABLE });

function setLogLevel$1(logLevel) {
  setLogSeverity$1({ severity: logLevel });
}

function getLogger$1(name) {
  return getLogger$2({ name });
}

function Event(eventName) {
  const log = getLogger$1(`${eventName}Event`);
  let handlers = {};
  let nextId = 0;

  function on(handler) {
    const id = nextId;
    nextId += 1;
    handlers[id] = handler;
    return () => delete handlers[id];
  }

  function emit(...args) {
    Object.values(handlers).forEach((f) => {
      try {
        const p = f(...args);
        if (p instanceof Promise) {
          p.catch((error) => {
            log.error(
              `Uncaught promise rejection while executing event listener provided by the library user.`,
              error,
            );
          });
        }
      } catch (error) {
        log.error(
          `Error while executing event listener provided by the library user.`,
          error,
        );
      }
    });
  }

  function once(handler) {
    const off = on((...args) => {
      handler(...args);
      off();
    });
    return off;
  }

  function clear() {
    handlers = {};
  }

  return {
    on,
    emit,
    once,
    clear,
    get listeners() {
      return Object.values(handlers);
    },
  };
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-09.
 * Copyright © Softgames 2019 - present
 */

const onLogin = new Event('onLogin');
const onClose = new Event('onClose');
const onError = new Event('onError');
const onMessage = new Event('onMessage');
const onWssError = new Event('onWssError');

var bind = function bind(fn, thisArg) {
  return function wrap() {
    var args = new Array(arguments.length);
    for (var i = 0; i < args.length; i++) {
      args[i] = arguments[i];
    }
    return fn.apply(thisArg, args);
  };
};

/*global toString:true*/

// utils is a library of generic helper functions non-specific to axios

var toString = Object.prototype.toString;

/**
 * Determine if a value is an Array
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is an Array, otherwise false
 */
function isArray$1(val) {
  return toString.call(val) === '[object Array]';
}

/**
 * Determine if a value is undefined
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if the value is undefined, otherwise false
 */
function isUndefined(val) {
  return typeof val === 'undefined';
}

/**
 * Determine if a value is a Buffer
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a Buffer, otherwise false
 */
function isBuffer(val) {
  return val !== null && !isUndefined(val) && val.constructor !== null && !isUndefined(val.constructor)
    && typeof val.constructor.isBuffer === 'function' && val.constructor.isBuffer(val);
}

/**
 * Determine if a value is an ArrayBuffer
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is an ArrayBuffer, otherwise false
 */
function isArrayBuffer(val) {
  return toString.call(val) === '[object ArrayBuffer]';
}

/**
 * Determine if a value is a FormData
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is an FormData, otherwise false
 */
function isFormData(val) {
  return (typeof FormData !== 'undefined') && (val instanceof FormData);
}

/**
 * Determine if a value is a view on an ArrayBuffer
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a view on an ArrayBuffer, otherwise false
 */
function isArrayBufferView(val) {
  var result;
  if ((typeof ArrayBuffer !== 'undefined') && (ArrayBuffer.isView)) {
    result = ArrayBuffer.isView(val);
  } else {
    result = (val) && (val.buffer) && (val.buffer instanceof ArrayBuffer);
  }
  return result;
}

/**
 * Determine if a value is a String
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a String, otherwise false
 */
function isString$3(val) {
  return typeof val === 'string';
}

/**
 * Determine if a value is a Number
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a Number, otherwise false
 */
function isNumber$2(val) {
  return typeof val === 'number';
}

/**
 * Determine if a value is an Object
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is an Object, otherwise false
 */
function isObject$1(val) {
  return val !== null && typeof val === 'object';
}

/**
 * Determine if a value is a plain Object
 *
 * @param {Object} val The value to test
 * @return {boolean} True if value is a plain Object, otherwise false
 */
function isPlainObject$1(val) {
  if (toString.call(val) !== '[object Object]') {
    return false;
  }

  var prototype = Object.getPrototypeOf(val);
  return prototype === null || prototype === Object.prototype;
}

/**
 * Determine if a value is a Date
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a Date, otherwise false
 */
function isDate(val) {
  return toString.call(val) === '[object Date]';
}

/**
 * Determine if a value is a File
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a File, otherwise false
 */
function isFile(val) {
  return toString.call(val) === '[object File]';
}

/**
 * Determine if a value is a Blob
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a Blob, otherwise false
 */
function isBlob(val) {
  return toString.call(val) === '[object Blob]';
}

/**
 * Determine if a value is a Function
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a Function, otherwise false
 */
function isFunction$1(val) {
  return toString.call(val) === '[object Function]';
}

/**
 * Determine if a value is a Stream
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a Stream, otherwise false
 */
function isStream(val) {
  return isObject$1(val) && isFunction$1(val.pipe);
}

/**
 * Determine if a value is a URLSearchParams object
 *
 * @param {Object} val The value to test
 * @returns {boolean} True if value is a URLSearchParams object, otherwise false
 */
function isURLSearchParams(val) {
  return typeof URLSearchParams !== 'undefined' && val instanceof URLSearchParams;
}

/**
 * Trim excess whitespace off the beginning and end of a string
 *
 * @param {String} str The String to trim
 * @returns {String} The String freed of excess whitespace
 */
function trim(str) {
  return str.replace(/^\s*/, '').replace(/\s*$/, '');
}

/**
 * Determine if we're running in a standard browser environment
 *
 * This allows axios to run in a web worker, and react-native.
 * Both environments support XMLHttpRequest, but not fully standard globals.
 *
 * web workers:
 *  typeof window -> undefined
 *  typeof document -> undefined
 *
 * react-native:
 *  navigator.product -> 'ReactNative'
 * nativescript
 *  navigator.product -> 'NativeScript' or 'NS'
 */
function isStandardBrowserEnv() {
  if (typeof navigator !== 'undefined' && (navigator.product === 'ReactNative' ||
                                           navigator.product === 'NativeScript' ||
                                           navigator.product === 'NS')) {
    return false;
  }
  return (
    typeof window !== 'undefined' &&
    typeof document !== 'undefined'
  );
}

/**
 * Iterate over an Array or an Object invoking a function for each item.
 *
 * If `obj` is an Array callback will be called passing
 * the value, index, and complete array for each item.
 *
 * If 'obj' is an Object callback will be called passing
 * the value, key, and complete object for each property.
 *
 * @param {Object|Array} obj The object to iterate
 * @param {Function} fn The callback to invoke for each item
 */
function forEach(obj, fn) {
  // Don't bother if no value provided
  if (obj === null || typeof obj === 'undefined') {
    return;
  }

  // Force an array if not already something iterable
  if (typeof obj !== 'object') {
    /*eslint no-param-reassign:0*/
    obj = [obj];
  }

  if (isArray$1(obj)) {
    // Iterate over array values
    for (var i = 0, l = obj.length; i < l; i++) {
      fn.call(null, obj[i], i, obj);
    }
  } else {
    // Iterate over object keys
    for (var key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        fn.call(null, obj[key], key, obj);
      }
    }
  }
}

/**
 * Accepts varargs expecting each argument to be an object, then
 * immutably merges the properties of each object and returns result.
 *
 * When multiple objects contain the same key the later object in
 * the arguments list will take precedence.
 *
 * Example:
 *
 * ```js
 * var result = merge({foo: 123}, {foo: 456});
 * console.log(result.foo); // outputs 456
 * ```
 *
 * @param {Object} obj1 Object to merge
 * @returns {Object} Result of all merge properties
 */
function merge(/* obj1, obj2, obj3, ... */) {
  var result = {};
  function assignValue(val, key) {
    if (isPlainObject$1(result[key]) && isPlainObject$1(val)) {
      result[key] = merge(result[key], val);
    } else if (isPlainObject$1(val)) {
      result[key] = merge({}, val);
    } else if (isArray$1(val)) {
      result[key] = val.slice();
    } else {
      result[key] = val;
    }
  }

  for (var i = 0, l = arguments.length; i < l; i++) {
    forEach(arguments[i], assignValue);
  }
  return result;
}

/**
 * Extends object a by mutably adding to it the properties of object b.
 *
 * @param {Object} a The object to be extended
 * @param {Object} b The object to copy properties from
 * @param {Object} thisArg The object to bind function to
 * @return {Object} The resulting value of object a
 */
function extend(a, b, thisArg) {
  forEach(b, function assignValue(val, key) {
    if (thisArg && typeof val === 'function') {
      a[key] = bind(val, thisArg);
    } else {
      a[key] = val;
    }
  });
  return a;
}

/**
 * Remove byte order marker. This catches EF BB BF (the UTF-8 BOM)
 *
 * @param {string} content with BOM
 * @return {string} content value without BOM
 */
function stripBOM(content) {
  if (content.charCodeAt(0) === 0xFEFF) {
    content = content.slice(1);
  }
  return content;
}

var utils = {
  isArray: isArray$1,
  isArrayBuffer: isArrayBuffer,
  isBuffer: isBuffer,
  isFormData: isFormData,
  isArrayBufferView: isArrayBufferView,
  isString: isString$3,
  isNumber: isNumber$2,
  isObject: isObject$1,
  isPlainObject: isPlainObject$1,
  isUndefined: isUndefined,
  isDate: isDate,
  isFile: isFile,
  isBlob: isBlob,
  isFunction: isFunction$1,
  isStream: isStream,
  isURLSearchParams: isURLSearchParams,
  isStandardBrowserEnv: isStandardBrowserEnv,
  forEach: forEach,
  merge: merge,
  extend: extend,
  trim: trim,
  stripBOM: stripBOM
};

function encode(val) {
  return encodeURIComponent(val).
    replace(/%3A/gi, ':').
    replace(/%24/g, '$').
    replace(/%2C/gi, ',').
    replace(/%20/g, '+').
    replace(/%5B/gi, '[').
    replace(/%5D/gi, ']');
}

/**
 * Build a URL by appending params to the end
 *
 * @param {string} url The base of the url (e.g., http://www.google.com)
 * @param {object} [params] The params to be appended
 * @returns {string} The formatted url
 */
var buildURL = function buildURL(url, params, paramsSerializer) {
  /*eslint no-param-reassign:0*/
  if (!params) {
    return url;
  }

  var serializedParams;
  if (paramsSerializer) {
    serializedParams = paramsSerializer(params);
  } else if (utils.isURLSearchParams(params)) {
    serializedParams = params.toString();
  } else {
    var parts = [];

    utils.forEach(params, function serialize(val, key) {
      if (val === null || typeof val === 'undefined') {
        return;
      }

      if (utils.isArray(val)) {
        key = key + '[]';
      } else {
        val = [val];
      }

      utils.forEach(val, function parseValue(v) {
        if (utils.isDate(v)) {
          v = v.toISOString();
        } else if (utils.isObject(v)) {
          v = JSON.stringify(v);
        }
        parts.push(encode(key) + '=' + encode(v));
      });
    });

    serializedParams = parts.join('&');
  }

  if (serializedParams) {
    var hashmarkIndex = url.indexOf('#');
    if (hashmarkIndex !== -1) {
      url = url.slice(0, hashmarkIndex);
    }

    url += (url.indexOf('?') === -1 ? '?' : '&') + serializedParams;
  }

  return url;
};

function InterceptorManager() {
  this.handlers = [];
}

/**
 * Add a new interceptor to the stack
 *
 * @param {Function} fulfilled The function to handle `then` for a `Promise`
 * @param {Function} rejected The function to handle `reject` for a `Promise`
 *
 * @return {Number} An ID used to remove interceptor later
 */
InterceptorManager.prototype.use = function use(fulfilled, rejected) {
  this.handlers.push({
    fulfilled: fulfilled,
    rejected: rejected
  });
  return this.handlers.length - 1;
};

/**
 * Remove an interceptor from the stack
 *
 * @param {Number} id The ID that was returned by `use`
 */
InterceptorManager.prototype.eject = function eject(id) {
  if (this.handlers[id]) {
    this.handlers[id] = null;
  }
};

/**
 * Iterate over all the registered interceptors
 *
 * This method is particularly useful for skipping over any
 * interceptors that may have become `null` calling `eject`.
 *
 * @param {Function} fn The function to call for each interceptor
 */
InterceptorManager.prototype.forEach = function forEach(fn) {
  utils.forEach(this.handlers, function forEachHandler(h) {
    if (h !== null) {
      fn(h);
    }
  });
};

var InterceptorManager_1 = InterceptorManager;

/**
 * Transform the data for a request or a response
 *
 * @param {Object|String} data The data to be transformed
 * @param {Array} headers The headers for the request or response
 * @param {Array|Function} fns A single function or Array of functions
 * @returns {*} The resulting transformed data
 */
var transformData = function transformData(data, headers, fns) {
  /*eslint no-param-reassign:0*/
  utils.forEach(fns, function transform(fn) {
    data = fn(data, headers);
  });

  return data;
};

var isCancel = function isCancel(value) {
  return !!(value && value.__CANCEL__);
};

var normalizeHeaderName = function normalizeHeaderName(headers, normalizedName) {
  utils.forEach(headers, function processHeader(value, name) {
    if (name !== normalizedName && name.toUpperCase() === normalizedName.toUpperCase()) {
      headers[normalizedName] = value;
      delete headers[name];
    }
  });
};

/**
 * Update an Error with the specified config, error code, and response.
 *
 * @param {Error} error The error to update.
 * @param {Object} config The config.
 * @param {string} [code] The error code (for example, 'ECONNABORTED').
 * @param {Object} [request] The request.
 * @param {Object} [response] The response.
 * @returns {Error} The error.
 */
var enhanceError = function enhanceError(error, config, code, request, response) {
  error.config = config;
  if (code) {
    error.code = code;
  }

  error.request = request;
  error.response = response;
  error.isAxiosError = true;

  error.toJSON = function toJSON() {
    return {
      // Standard
      message: this.message,
      name: this.name,
      // Microsoft
      description: this.description,
      number: this.number,
      // Mozilla
      fileName: this.fileName,
      lineNumber: this.lineNumber,
      columnNumber: this.columnNumber,
      stack: this.stack,
      // Axios
      config: this.config,
      code: this.code
    };
  };
  return error;
};

/**
 * Create an Error with the specified message, config, error code, request and response.
 *
 * @param {string} message The error message.
 * @param {Object} config The config.
 * @param {string} [code] The error code (for example, 'ECONNABORTED').
 * @param {Object} [request] The request.
 * @param {Object} [response] The response.
 * @returns {Error} The created error.
 */
var createError$1 = function createError(message, config, code, request, response) {
  var error = new Error(message);
  return enhanceError(error, config, code, request, response);
};

/**
 * Resolve or reject a Promise based on response status.
 *
 * @param {Function} resolve A function that resolves the promise.
 * @param {Function} reject A function that rejects the promise.
 * @param {object} response The response.
 */
var settle = function settle(resolve, reject, response) {
  var validateStatus = response.config.validateStatus;
  if (!response.status || !validateStatus || validateStatus(response.status)) {
    resolve(response);
  } else {
    reject(createError$1(
      'Request failed with status code ' + response.status,
      response.config,
      null,
      response.request,
      response
    ));
  }
};

var cookies = (
  utils.isStandardBrowserEnv() ?

  // Standard browser envs support document.cookie
    (function standardBrowserEnv() {
      return {
        write: function write(name, value, expires, path, domain, secure) {
          var cookie = [];
          cookie.push(name + '=' + encodeURIComponent(value));

          if (utils.isNumber(expires)) {
            cookie.push('expires=' + new Date(expires).toGMTString());
          }

          if (utils.isString(path)) {
            cookie.push('path=' + path);
          }

          if (utils.isString(domain)) {
            cookie.push('domain=' + domain);
          }

          if (secure === true) {
            cookie.push('secure');
          }

          document.cookie = cookie.join('; ');
        },

        read: function read(name) {
          var match = document.cookie.match(new RegExp('(^|;\\s*)(' + name + ')=([^;]*)'));
          return (match ? decodeURIComponent(match[3]) : null);
        },

        remove: function remove(name) {
          this.write(name, '', Date.now() - 86400000);
        }
      };
    })() :

  // Non standard browser env (web workers, react-native) lack needed support.
    (function nonStandardBrowserEnv() {
      return {
        write: function write() {},
        read: function read() { return null; },
        remove: function remove() {}
      };
    })()
);

/**
 * Determines whether the specified URL is absolute
 *
 * @param {string} url The URL to test
 * @returns {boolean} True if the specified URL is absolute, otherwise false
 */
var isAbsoluteURL = function isAbsoluteURL(url) {
  // A URL is considered absolute if it begins with "<scheme>://" or "//" (protocol-relative URL).
  // RFC 3986 defines scheme name as a sequence of characters beginning with a letter and followed
  // by any combination of letters, digits, plus, period, or hyphen.
  return /^([a-z][a-z\d\+\-\.]*:)?\/\//i.test(url);
};

/**
 * Creates a new URL by combining the specified URLs
 *
 * @param {string} baseURL The base URL
 * @param {string} relativeURL The relative URL
 * @returns {string} The combined URL
 */
var combineURLs = function combineURLs(baseURL, relativeURL) {
  return relativeURL
    ? baseURL.replace(/\/+$/, '') + '/' + relativeURL.replace(/^\/+/, '')
    : baseURL;
};

/**
 * Creates a new URL by combining the baseURL with the requestedURL,
 * only when the requestedURL is not already an absolute URL.
 * If the requestURL is absolute, this function returns the requestedURL untouched.
 *
 * @param {string} baseURL The base URL
 * @param {string} requestedURL Absolute or relative URL to combine
 * @returns {string} The combined full path
 */
var buildFullPath = function buildFullPath(baseURL, requestedURL) {
  if (baseURL && !isAbsoluteURL(requestedURL)) {
    return combineURLs(baseURL, requestedURL);
  }
  return requestedURL;
};

// Headers whose duplicates are ignored by node
// c.f. https://nodejs.org/api/http.html#http_message_headers
var ignoreDuplicateOf = [
  'age', 'authorization', 'content-length', 'content-type', 'etag',
  'expires', 'from', 'host', 'if-modified-since', 'if-unmodified-since',
  'last-modified', 'location', 'max-forwards', 'proxy-authorization',
  'referer', 'retry-after', 'user-agent'
];

/**
 * Parse headers into an object
 *
 * ```
 * Date: Wed, 27 Aug 2014 08:58:49 GMT
 * Content-Type: application/json
 * Connection: keep-alive
 * Transfer-Encoding: chunked
 * ```
 *
 * @param {String} headers Headers needing to be parsed
 * @returns {Object} Headers parsed into an object
 */
var parseHeaders = function parseHeaders(headers) {
  var parsed = {};
  var key;
  var val;
  var i;

  if (!headers) { return parsed; }

  utils.forEach(headers.split('\n'), function parser(line) {
    i = line.indexOf(':');
    key = utils.trim(line.substr(0, i)).toLowerCase();
    val = utils.trim(line.substr(i + 1));

    if (key) {
      if (parsed[key] && ignoreDuplicateOf.indexOf(key) >= 0) {
        return;
      }
      if (key === 'set-cookie') {
        parsed[key] = (parsed[key] ? parsed[key] : []).concat([val]);
      } else {
        parsed[key] = parsed[key] ? parsed[key] + ', ' + val : val;
      }
    }
  });

  return parsed;
};

var isURLSameOrigin = (
  utils.isStandardBrowserEnv() ?

  // Standard browser envs have full support of the APIs needed to test
  // whether the request URL is of the same origin as current location.
    (function standardBrowserEnv() {
      var msie = /(msie|trident)/i.test(navigator.userAgent);
      var urlParsingNode = document.createElement('a');
      var originURL;

      /**
    * Parse a URL to discover it's components
    *
    * @param {String} url The URL to be parsed
    * @returns {Object}
    */
      function resolveURL(url) {
        var href = url;

        if (msie) {
        // IE needs attribute set twice to normalize properties
          urlParsingNode.setAttribute('href', href);
          href = urlParsingNode.href;
        }

        urlParsingNode.setAttribute('href', href);

        // urlParsingNode provides the UrlUtils interface - http://url.spec.whatwg.org/#urlutils
        return {
          href: urlParsingNode.href,
          protocol: urlParsingNode.protocol ? urlParsingNode.protocol.replace(/:$/, '') : '',
          host: urlParsingNode.host,
          search: urlParsingNode.search ? urlParsingNode.search.replace(/^\?/, '') : '',
          hash: urlParsingNode.hash ? urlParsingNode.hash.replace(/^#/, '') : '',
          hostname: urlParsingNode.hostname,
          port: urlParsingNode.port,
          pathname: (urlParsingNode.pathname.charAt(0) === '/') ?
            urlParsingNode.pathname :
            '/' + urlParsingNode.pathname
        };
      }

      originURL = resolveURL(window.location.href);

      /**
    * Determine if a URL shares the same origin as the current location
    *
    * @param {String} requestURL The URL to test
    * @returns {boolean} True if URL shares the same origin, otherwise false
    */
      return function isURLSameOrigin(requestURL) {
        var parsed = (utils.isString(requestURL)) ? resolveURL(requestURL) : requestURL;
        return (parsed.protocol === originURL.protocol &&
            parsed.host === originURL.host);
      };
    })() :

  // Non standard browser envs (web workers, react-native) lack needed support.
    (function nonStandardBrowserEnv() {
      return function isURLSameOrigin() {
        return true;
      };
    })()
);

var xhr = function xhrAdapter(config) {
  return new Promise(function dispatchXhrRequest(resolve, reject) {
    var requestData = config.data;
    var requestHeaders = config.headers;

    if (utils.isFormData(requestData)) {
      delete requestHeaders['Content-Type']; // Let the browser set it
    }

    var request = new XMLHttpRequest();

    // HTTP basic authentication
    if (config.auth) {
      var username = config.auth.username || '';
      var password = config.auth.password ? unescape(encodeURIComponent(config.auth.password)) : '';
      requestHeaders.Authorization = 'Basic ' + btoa(username + ':' + password);
    }

    var fullPath = buildFullPath(config.baseURL, config.url);
    request.open(config.method.toUpperCase(), buildURL(fullPath, config.params, config.paramsSerializer), true);

    // Set the request timeout in MS
    request.timeout = config.timeout;

    // Listen for ready state
    request.onreadystatechange = function handleLoad() {
      if (!request || request.readyState !== 4) {
        return;
      }

      // The request errored out and we didn't get a response, this will be
      // handled by onerror instead
      // With one exception: request that using file: protocol, most browsers
      // will return status as 0 even though it's a successful request
      if (request.status === 0 && !(request.responseURL && request.responseURL.indexOf('file:') === 0)) {
        return;
      }

      // Prepare the response
      var responseHeaders = 'getAllResponseHeaders' in request ? parseHeaders(request.getAllResponseHeaders()) : null;
      var responseData = !config.responseType || config.responseType === 'text' ? request.responseText : request.response;
      var response = {
        data: responseData,
        status: request.status,
        statusText: request.statusText,
        headers: responseHeaders,
        config: config,
        request: request
      };

      settle(resolve, reject, response);

      // Clean up request
      request = null;
    };

    // Handle browser request cancellation (as opposed to a manual cancellation)
    request.onabort = function handleAbort() {
      if (!request) {
        return;
      }

      reject(createError$1('Request aborted', config, 'ECONNABORTED', request));

      // Clean up request
      request = null;
    };

    // Handle low level network errors
    request.onerror = function handleError() {
      // Real errors are hidden from us by the browser
      // onerror should only fire if it's a network error
      reject(createError$1('Network Error', config, null, request));

      // Clean up request
      request = null;
    };

    // Handle timeout
    request.ontimeout = function handleTimeout() {
      var timeoutErrorMessage = 'timeout of ' + config.timeout + 'ms exceeded';
      if (config.timeoutErrorMessage) {
        timeoutErrorMessage = config.timeoutErrorMessage;
      }
      reject(createError$1(timeoutErrorMessage, config, 'ECONNABORTED',
        request));

      // Clean up request
      request = null;
    };

    // Add xsrf header
    // This is only done if running in a standard browser environment.
    // Specifically not if we're in a web worker, or react-native.
    if (utils.isStandardBrowserEnv()) {
      // Add xsrf header
      var xsrfValue = (config.withCredentials || isURLSameOrigin(fullPath)) && config.xsrfCookieName ?
        cookies.read(config.xsrfCookieName) :
        undefined;

      if (xsrfValue) {
        requestHeaders[config.xsrfHeaderName] = xsrfValue;
      }
    }

    // Add headers to the request
    if ('setRequestHeader' in request) {
      utils.forEach(requestHeaders, function setRequestHeader(val, key) {
        if (typeof requestData === 'undefined' && key.toLowerCase() === 'content-type') {
          // Remove Content-Type if data is undefined
          delete requestHeaders[key];
        } else {
          // Otherwise add header to the request
          request.setRequestHeader(key, val);
        }
      });
    }

    // Add withCredentials to request if needed
    if (!utils.isUndefined(config.withCredentials)) {
      request.withCredentials = !!config.withCredentials;
    }

    // Add responseType to request if needed
    if (config.responseType) {
      try {
        request.responseType = config.responseType;
      } catch (e) {
        // Expected DOMException thrown by browsers not compatible XMLHttpRequest Level 2.
        // But, this can be suppressed for 'json' type as it can be parsed by default 'transformResponse' function.
        if (config.responseType !== 'json') {
          throw e;
        }
      }
    }

    // Handle progress if needed
    if (typeof config.onDownloadProgress === 'function') {
      request.addEventListener('progress', config.onDownloadProgress);
    }

    // Not all browsers support upload events
    if (typeof config.onUploadProgress === 'function' && request.upload) {
      request.upload.addEventListener('progress', config.onUploadProgress);
    }

    if (config.cancelToken) {
      // Handle cancellation
      config.cancelToken.promise.then(function onCanceled(cancel) {
        if (!request) {
          return;
        }

        request.abort();
        reject(cancel);
        // Clean up request
        request = null;
      });
    }

    if (!requestData) {
      requestData = null;
    }

    // Send the request
    request.send(requestData);
  });
};

var DEFAULT_CONTENT_TYPE = {
  'Content-Type': 'application/x-www-form-urlencoded'
};

function setContentTypeIfUnset(headers, value) {
  if (!utils.isUndefined(headers) && utils.isUndefined(headers['Content-Type'])) {
    headers['Content-Type'] = value;
  }
}

function getDefaultAdapter() {
  var adapter;
  if (typeof XMLHttpRequest !== 'undefined') {
    // For browsers use XHR adapter
    adapter = xhr;
  } else if (typeof process !== 'undefined' && Object.prototype.toString.call(process) === '[object process]') {
    // For node use HTTP adapter
    adapter = xhr;
  }
  return adapter;
}

var defaults = {
  adapter: getDefaultAdapter(),

  transformRequest: [function transformRequest(data, headers) {
    normalizeHeaderName(headers, 'Accept');
    normalizeHeaderName(headers, 'Content-Type');
    if (utils.isFormData(data) ||
      utils.isArrayBuffer(data) ||
      utils.isBuffer(data) ||
      utils.isStream(data) ||
      utils.isFile(data) ||
      utils.isBlob(data)
    ) {
      return data;
    }
    if (utils.isArrayBufferView(data)) {
      return data.buffer;
    }
    if (utils.isURLSearchParams(data)) {
      setContentTypeIfUnset(headers, 'application/x-www-form-urlencoded;charset=utf-8');
      return data.toString();
    }
    if (utils.isObject(data)) {
      setContentTypeIfUnset(headers, 'application/json;charset=utf-8');
      return JSON.stringify(data);
    }
    return data;
  }],

  transformResponse: [function transformResponse(data) {
    /*eslint no-param-reassign:0*/
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) { /* Ignore */ }
    }
    return data;
  }],

  /**
   * A timeout in milliseconds to abort a request. If set to 0 (default) a
   * timeout is not created.
   */
  timeout: 0,

  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',

  maxContentLength: -1,
  maxBodyLength: -1,

  validateStatus: function validateStatus(status) {
    return status >= 200 && status < 300;
  }
};

defaults.headers = {
  common: {
    'Accept': 'application/json, text/plain, */*'
  }
};

utils.forEach(['delete', 'get', 'head'], function forEachMethodNoData(method) {
  defaults.headers[method] = {};
});

utils.forEach(['post', 'put', 'patch'], function forEachMethodWithData(method) {
  defaults.headers[method] = utils.merge(DEFAULT_CONTENT_TYPE);
});

var defaults_1 = defaults;

/**
 * Throws a `Cancel` if cancellation has been requested.
 */
function throwIfCancellationRequested(config) {
  if (config.cancelToken) {
    config.cancelToken.throwIfRequested();
  }
}

/**
 * Dispatch a request to the server using the configured adapter.
 *
 * @param {object} config The config that is to be used for the request
 * @returns {Promise} The Promise to be fulfilled
 */
var dispatchRequest = function dispatchRequest(config) {
  throwIfCancellationRequested(config);

  // Ensure headers exist
  config.headers = config.headers || {};

  // Transform request data
  config.data = transformData(
    config.data,
    config.headers,
    config.transformRequest
  );

  // Flatten headers
  config.headers = utils.merge(
    config.headers.common || {},
    config.headers[config.method] || {},
    config.headers
  );

  utils.forEach(
    ['delete', 'get', 'head', 'post', 'put', 'patch', 'common'],
    function cleanHeaderConfig(method) {
      delete config.headers[method];
    }
  );

  var adapter = config.adapter || defaults_1.adapter;

  return adapter(config).then(function onAdapterResolution(response) {
    throwIfCancellationRequested(config);

    // Transform response data
    response.data = transformData(
      response.data,
      response.headers,
      config.transformResponse
    );

    return response;
  }, function onAdapterRejection(reason) {
    if (!isCancel(reason)) {
      throwIfCancellationRequested(config);

      // Transform response data
      if (reason && reason.response) {
        reason.response.data = transformData(
          reason.response.data,
          reason.response.headers,
          config.transformResponse
        );
      }
    }

    return Promise.reject(reason);
  });
};

/**
 * Config-specific merge-function which creates a new config-object
 * by merging two configuration objects together.
 *
 * @param {Object} config1
 * @param {Object} config2
 * @returns {Object} New object resulting from merging config2 to config1
 */
var mergeConfig = function mergeConfig(config1, config2) {
  // eslint-disable-next-line no-param-reassign
  config2 = config2 || {};
  var config = {};

  var valueFromConfig2Keys = ['url', 'method', 'data'];
  var mergeDeepPropertiesKeys = ['headers', 'auth', 'proxy', 'params'];
  var defaultToConfig2Keys = [
    'baseURL', 'transformRequest', 'transformResponse', 'paramsSerializer',
    'timeout', 'timeoutMessage', 'withCredentials', 'adapter', 'responseType', 'xsrfCookieName',
    'xsrfHeaderName', 'onUploadProgress', 'onDownloadProgress', 'decompress',
    'maxContentLength', 'maxBodyLength', 'maxRedirects', 'transport', 'httpAgent',
    'httpsAgent', 'cancelToken', 'socketPath', 'responseEncoding'
  ];
  var directMergeKeys = ['validateStatus'];

  function getMergedValue(target, source) {
    if (utils.isPlainObject(target) && utils.isPlainObject(source)) {
      return utils.merge(target, source);
    } else if (utils.isPlainObject(source)) {
      return utils.merge({}, source);
    } else if (utils.isArray(source)) {
      return source.slice();
    }
    return source;
  }

  function mergeDeepProperties(prop) {
    if (!utils.isUndefined(config2[prop])) {
      config[prop] = getMergedValue(config1[prop], config2[prop]);
    } else if (!utils.isUndefined(config1[prop])) {
      config[prop] = getMergedValue(undefined, config1[prop]);
    }
  }

  utils.forEach(valueFromConfig2Keys, function valueFromConfig2(prop) {
    if (!utils.isUndefined(config2[prop])) {
      config[prop] = getMergedValue(undefined, config2[prop]);
    }
  });

  utils.forEach(mergeDeepPropertiesKeys, mergeDeepProperties);

  utils.forEach(defaultToConfig2Keys, function defaultToConfig2(prop) {
    if (!utils.isUndefined(config2[prop])) {
      config[prop] = getMergedValue(undefined, config2[prop]);
    } else if (!utils.isUndefined(config1[prop])) {
      config[prop] = getMergedValue(undefined, config1[prop]);
    }
  });

  utils.forEach(directMergeKeys, function merge(prop) {
    if (prop in config2) {
      config[prop] = getMergedValue(config1[prop], config2[prop]);
    } else if (prop in config1) {
      config[prop] = getMergedValue(undefined, config1[prop]);
    }
  });

  var axiosKeys = valueFromConfig2Keys
    .concat(mergeDeepPropertiesKeys)
    .concat(defaultToConfig2Keys)
    .concat(directMergeKeys);

  var otherKeys = Object
    .keys(config1)
    .concat(Object.keys(config2))
    .filter(function filterAxiosKeys(key) {
      return axiosKeys.indexOf(key) === -1;
    });

  utils.forEach(otherKeys, mergeDeepProperties);

  return config;
};

/**
 * Create a new instance of Axios
 *
 * @param {Object} instanceConfig The default config for the instance
 */
function Axios(instanceConfig) {
  this.defaults = instanceConfig;
  this.interceptors = {
    request: new InterceptorManager_1(),
    response: new InterceptorManager_1()
  };
}

/**
 * Dispatch a request
 *
 * @param {Object} config The config specific for this request (merged with this.defaults)
 */
Axios.prototype.request = function request(config) {
  /*eslint no-param-reassign:0*/
  // Allow for axios('example/url'[, config]) a la fetch API
  if (typeof config === 'string') {
    config = arguments[1] || {};
    config.url = arguments[0];
  } else {
    config = config || {};
  }

  config = mergeConfig(this.defaults, config);

  // Set config.method
  if (config.method) {
    config.method = config.method.toLowerCase();
  } else if (this.defaults.method) {
    config.method = this.defaults.method.toLowerCase();
  } else {
    config.method = 'get';
  }

  // Hook up interceptors middleware
  var chain = [dispatchRequest, undefined];
  var promise = Promise.resolve(config);

  this.interceptors.request.forEach(function unshiftRequestInterceptors(interceptor) {
    chain.unshift(interceptor.fulfilled, interceptor.rejected);
  });

  this.interceptors.response.forEach(function pushResponseInterceptors(interceptor) {
    chain.push(interceptor.fulfilled, interceptor.rejected);
  });

  while (chain.length) {
    promise = promise.then(chain.shift(), chain.shift());
  }

  return promise;
};

Axios.prototype.getUri = function getUri(config) {
  config = mergeConfig(this.defaults, config);
  return buildURL(config.url, config.params, config.paramsSerializer).replace(/^\?/, '');
};

// Provide aliases for supported request methods
utils.forEach(['delete', 'get', 'head', 'options'], function forEachMethodNoData(method) {
  /*eslint func-names:0*/
  Axios.prototype[method] = function(url, config) {
    return this.request(mergeConfig(config || {}, {
      method: method,
      url: url,
      data: (config || {}).data
    }));
  };
});

utils.forEach(['post', 'put', 'patch'], function forEachMethodWithData(method) {
  /*eslint func-names:0*/
  Axios.prototype[method] = function(url, data, config) {
    return this.request(mergeConfig(config || {}, {
      method: method,
      url: url,
      data: data
    }));
  };
});

var Axios_1 = Axios;

/**
 * A `Cancel` is an object that is thrown when an operation is canceled.
 *
 * @class
 * @param {string=} message The message.
 */
function Cancel(message) {
  this.message = message;
}

Cancel.prototype.toString = function toString() {
  return 'Cancel' + (this.message ? ': ' + this.message : '');
};

Cancel.prototype.__CANCEL__ = true;

var Cancel_1 = Cancel;

/**
 * A `CancelToken` is an object that can be used to request cancellation of an operation.
 *
 * @class
 * @param {Function} executor The executor function.
 */
function CancelToken(executor) {
  if (typeof executor !== 'function') {
    throw new TypeError('executor must be a function.');
  }

  var resolvePromise;
  this.promise = new Promise(function promiseExecutor(resolve) {
    resolvePromise = resolve;
  });

  var token = this;
  executor(function cancel(message) {
    if (token.reason) {
      // Cancellation has already been requested
      return;
    }

    token.reason = new Cancel_1(message);
    resolvePromise(token.reason);
  });
}

/**
 * Throws a `Cancel` if cancellation has been requested.
 */
CancelToken.prototype.throwIfRequested = function throwIfRequested() {
  if (this.reason) {
    throw this.reason;
  }
};

/**
 * Returns an object that contains a new `CancelToken` and a function that, when called,
 * cancels the `CancelToken`.
 */
CancelToken.source = function source() {
  var cancel;
  var token = new CancelToken(function executor(c) {
    cancel = c;
  });
  return {
    token: token,
    cancel: cancel
  };
};

var CancelToken_1 = CancelToken;

/**
 * Syntactic sugar for invoking a function and expanding an array for arguments.
 *
 * Common use case would be to use `Function.prototype.apply`.
 *
 *  ```js
 *  function f(x, y, z) {}
 *  var args = [1, 2, 3];
 *  f.apply(null, args);
 *  ```
 *
 * With `spread` this example can be re-written.
 *
 *  ```js
 *  spread(function(x, y, z) {})([1, 2, 3]);
 *  ```
 *
 * @param {Function} callback
 * @returns {Function}
 */
var spread = function spread(callback) {
  return function wrap(arr) {
    return callback.apply(null, arr);
  };
};

/**
 * Determines whether the payload is an error thrown by Axios
 *
 * @param {*} payload The value to test
 * @returns {boolean} True if the payload is an error thrown by Axios, otherwise false
 */
var isAxiosError = function isAxiosError(payload) {
  return (typeof payload === 'object') && (payload.isAxiosError === true);
};

/**
 * Create an instance of Axios
 *
 * @param {Object} defaultConfig The default config for the instance
 * @return {Axios} A new instance of Axios
 */
function createInstance(defaultConfig) {
  var context = new Axios_1(defaultConfig);
  var instance = bind(Axios_1.prototype.request, context);

  // Copy axios.prototype to instance
  utils.extend(instance, Axios_1.prototype, context);

  // Copy context to instance
  utils.extend(instance, context);

  return instance;
}

// Create the default instance to be exported
var axios$2 = createInstance(defaults_1);

// Expose Axios class to allow class inheritance
axios$2.Axios = Axios_1;

// Factory for creating new instances
axios$2.create = function create(instanceConfig) {
  return createInstance(mergeConfig(axios$2.defaults, instanceConfig));
};

// Expose Cancel & CancelToken
axios$2.Cancel = Cancel_1;
axios$2.CancelToken = CancelToken_1;
axios$2.isCancel = isCancel;

// Expose all/spread
axios$2.all = function all(promises) {
  return Promise.all(promises);
};
axios$2.spread = spread;

// Expose isAxiosError
axios$2.isAxiosError = isAxiosError;

var axios_1 = axios$2;

// Allow use of default import syntax in TypeScript
var default_1 = axios$2;
axios_1.default = default_1;

var axios$1 = axios_1;

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-07-23.
 * Copyright © Softgames 2019 - present
 */

function checkForGraphQlErrors(messageId, response) {
  const { error, errors } = response;
  if (!errors && !error) {
    return;
  }
  const eArray = errors || [error];
  const firstError = eArray[0] || {};
  const { code: primaryCode, message: primaryMsg } = firstError || {};
  const { extensions: { code: secondaryCode } = {}, message: secondaryMsg } =
    primaryMsg || {};
  const serverMsg = secondaryMsg || primaryMsg;
  const msg =
    typeof serverMsg === 'string'
      ? serverMsg
      : 'Received a malformed error from GB Game Server';
  const code = secondaryCode || primaryCode;
  throw code && !!Object.values(ERROR_CODE).find((c) => c === code)
    ? new GBCKnownGraphQLError(code, msg, messageId)
    : new GBCUnknownGraphQLError(msg, messageId);
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-02.
 * Copyright © Softgames 2019 - present
 */

const VERSION = '4.6.0';

const ENCODED_BOTS_CONFIG = 'eyJIT1NUIjoiI3tnYW1lSWR9LmJzLnNicy5zb2Z0Z2FtZXMuZGUiLCJCQVNJQ19BVVRIIjp7IlVTRVIiOiJib3Qtc2VydmljZSIsIlBBU1MiOiJhRHVEb2xsYUgifSwiRU5EUE9JTlRTIjp7IkNPTlZFUlNJT04iOiIvYXBpL3YxL2NvbnZlcnNpb25zL2dhbWUvI3tnYW1lSWR9IiwiR0VUX1NDSEVEVUxFRF9NRVNTQUdFIjoiL2FwaS92MS9zY2hlZHVsZS91c2VyLyN7cGxheWVySWR9L2dhbWUvI3tnYW1lSWR9IiwiQ1JFQVRFX1NDSEVEVUxFRF9NRVNTQUdFIjoiL2FwaS92MS9zY2hlZHVsZS9nYW1lLyN7Z2FtZUlkfT9lbnZpcm9ubWVudD0je2Vudmlyb25tZW50fSIsIkRFTEVURV9TQ0hFRFVMRURfTUVTU0FHRSI6Ii9hcGkvdjEvc2NoZWR1bGUvI3tzY2hlZHVsZUlkfSJ9fQ==';
const ENCODED_GB_CONFIG = 'eyJIT1NUIjoiI3tnYW1lSWR9LmdiLnNicy5zb2Z0Z2FtZXMuZGUiLCJFTkRQT0lOVFMiOnsiV1MiOiIvc29ja2V0L2dhbWUvI3tnYW1lSWR9IiwiSFRUUCI6Ii9ncmFwaHFsL2dhbWUvI3tnYW1lSWR9IiwiUkVTVCI6Ii9yZXN0L2dhbWUvI3tnYW1lSWR9In19';

const GAME_ID_PLACEHOLDER = '#{gameId}';
const PLAYER_ID_PLACEHOLDER = '#{playerId}';
const SCHEDULE_ID_PLACEHOLDER = '#{scheduleId}';
const ENVIRONMENT_PLACEHOLDER = '#{environment}';

const TRACK_BOT_MESSAGE_CONVERSION_REQUEST_TIMEOUT = 10000;
const GET_SCHEDULED_BOT_MESSAGE_REQUEST_TIMEOUT = 10000;
const DELETE_SCHEDULED_BOT_MESSAGE_REQUEST_TIMEOUT = 10000;
const CREATE_SCHEDULED_BOT_MESSAGE_REQUEST_TIMEOUT = 15000;
const CONNECTION_TYPE = {
  WSS: 'WSS',
  HTTPS: 'HTTPS',
};
const WS_STATE = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
};
const GA_TRACKING_ID = 'UA-157820994-3';

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-07-23.
 * Created by Rico Trebeljahr <rico.trebeljahr@softgames.de> on 2019-07-23.
 * Copyright © Softgames 2019 - present
 */

function getLoginHandler(query) {
  let loginInProgress = false;
  const log = getLogger$1('connection');

  async function login({ userId, authToken } = {}) {
    try {
      if (loginInProgress) {
        log.debug('Login already in progress');
        throw new GBCInvalidCallError('Login already in progress');
      }
      loginInProgress = true;
      const trackingData = {
        gameBackendClientVersion: VERSION,
      };
      const result = await query(
        `
            mutation login($authToken: String!, $userId: ID!, $trackingData: TrackingData) {
              login(authToken: $authToken, userId: $userId, trackingData: $trackingData)
            }
          `,
        {
          userId,
          authToken,
          trackingData,
        },
      );
      onLogin.emit();
      log.debug('... login done', { userId, authToken });
      return result;
    } catch (loginError) {
      const saveLoginError =
        loginError ||
        new GBCUnknownError(
          `Caught a loginError which was "${loginError}" on login. Should never happen of course.`,
          loginError,
        );
      // [RS] We got this problem some times. Should be fixed now. Was caused by QueuePromise / By rejection of the openPromiseFlag
      log.error('... login failed', { userId, authToken }, saveLoginError);
      throw saveLoginError;
    } finally {
      loginInProgress = false;
    }
  }
  return {
    login,
    get loginInProgress() {
      return loginInProgress;
    },
  };
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-07-23.
 * Copyright © Softgames 2019 - present
 */

function getExecutionSingleton(exec) {
  let execPromise = null;
  return async (...args) => {
    if (!execPromise) {
      execPromise = exec(...args);
      if (execPromise instanceof Promise) {
        execPromise
          .catch(() => {
            // [RS] No need to be handled here, because it will be handled by the receivers of the promise
            // [RS] But we need a catch clause here to not have an uncaught promise rejection
          })
          .finally(() => {
            execPromise = null;
          });
      } else {
        const result = execPromise;
        execPromise = null;
        return result;
      }
    }
    return execPromise;
  };
}

function PromiseFlag(name, init) {
  let value = init;
  const onChange = new Event('onChange');

  async function assertState(test = () => true) {
    if (test(value)) return Promise.resolve();

    return new Promise((resolve, reject) => {
      const off = onChange.on(({ value: changedValue, error }) => {
        if (error) {
          reject(error);
          off();
        } else if (test(changedValue)) {
          resolve();
          off();
        }
      });
    });
  }

  function rejectAll(error) {
    onChange.emit({ error });
  }

  return {
    assertState,
    rejectAll,
    get name() {
      return name;
    },
    get value() {
      return value;
    },
    set value(val) {
      if (value === val) return;
      value = val;
      onChange.emit({ value: val });
    },
  };
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-07-23.
 * Created by Rico Trebeljahr <rico.trebeljahr@softgames.de> on 2019-07-23.
 * Copyright © Softgames 2019 - present
 */

const MESSAGE_TIMEOUT$1 = 60 * 1000;

async function runQuery({ messageId, url, bearerToken, query, vars = {} }) {
  const options = {
    method: 'post',
    url,
    data: {
      query,
      variables: vars,
      messageId,
    },
    timeout: MESSAGE_TIMEOUT$1,
    responseType: 'json',
  };
  if (bearerToken) {
    options.headers = {
      authorization: `bearer ${bearerToken}`,
    };
  }
  try {
    const { data } = await axios$1(options);
    return data;
  } catch (error) {
    if (error.response) {
      const {
        status,
        data: {
          message = 'Unknown server response',
          error: errorResponse,
          errors,
        } = {},
      } = error.response;
      if (!status) {
        throw new GBCUnknownError(
          `Did not receive http code from server: ${message}`,
        );
      }
      if (status === 401) {
        throw new GBCNotAuthorizedError(message);
      }
      if (errorResponse || errors) {
        const { data = {} } = error.response;
        return data; // [RS] No need to analyze and throw error here since it will be done where this function is called
      }
      throw new GBCUnknownGraphQLError(
        `Server "${url}" response with status code "${status}"`,
        messageId,
      );
    } else if (error.request) {
      throw new GBCUnknownError(`No server response: ${error.message}`);
    } else {
      throw new GBCUnknownError(`Failed to execute request: ${error.message}`);
    }
  }
}

function getHttpsConnection({ url }) {
  const loginHandler = getLoginHandler(queryIntern);
  const loggedInFlag = new PromiseFlag('loggedIn', false);
  let closed = false;
  let validCredentials = false;
  let credentials = null;
  let accessToken = null;
  let nextMessageId = 1;

  async function queryIntern(query, vars = {}, loginLoop = false) {
    try {
      if (closed) {
        throw new Error(`Connection closed`);
      }
      const messageId = nextMessageId;
      nextMessageId += 1;
      const result = await runQuery({
        messageId,
        url,
        bearerToken: accessToken,
        query,
        vars,
        loginLoop: false,
      });
      checkForGraphQlErrors(messageId, result);
      return result;
    } catch (error) {
      if (error.code === ERROR_CODE.NOT_AUTHORIZED) {
        loggedInFlag.value = false;
        if (validCredentials && !loginLoop) {
          return loginAndExec(query, vars);
        }
      }
      throw error;
    }
  }

  async function login() {
    try {
      if (closed) {
        throw new Error(`Connection closed`);
      }
      if (!credentials) {
        throw new Error(`No login credentials found`);
      }
      const { userId, authToken } = credentials;
      const {
        data: { login: receivedAccessToken },
      } = await loginHandler.login({ userId, authToken });
      validCredentials = true;
      accessToken = receivedAccessToken;
      loggedInFlag.value = true;
    } catch (error) {
      loggedInFlag.rejectAll(error);
      onError.emit(error);
      throw error;
    }
  }

  const reLogin = getExecutionSingleton(login);

  async function loginAndExec(query, vars = {}) {
    await reLogin();
    return queryIntern(query, vars, true);
  }

  return {
    query: async (query, vars) => queryIntern(query, vars),
    login: async (newCredentials) => {
      if (validCredentials) {
        throw new GBCInvalidCallError(`You can't change valid credentials`);
      }
      credentials = newCredentials;
      await login();
    },
    loggedInFlag,
    isLoggedIn: () => loggedInFlag.value,
    close: () => {
      closed = true;
      onClose.emit();
    },
    get loginInProgress() {
      return loginHandler.loginInProgress;
    },
    get type() {
      return CONNECTION_TYPE.HTTPS;
    },
  };
}

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var reconnectingWebsocket = createCommonjsModule(function (module) {
// MIT License:
//
// Copyright (c) 2010-2012, Joe Walnes
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

/**
 * This behaves like a WebSocket in every way, except if it fails to connect,
 * or it gets disconnected, it will repeatedly poll until it successfully connects
 * again.
 *
 * It is API compatible, so when you have:
 *   ws = new WebSocket('ws://....');
 * you can replace with:
 *   ws = new ReconnectingWebSocket('ws://....');
 *
 * The event stream will typically look like:
 *  onconnecting
 *  onopen
 *  onmessage
 *  onmessage
 *  onclose // lost connection
 *  onconnecting
 *  onopen  // sometime later...
 *  onmessage
 *  onmessage
 *  etc...
 *
 * It is API compatible with the standard WebSocket API, apart from the following members:
 *
 * - `bufferedAmount`
 * - `extensions`
 * - `binaryType`
 *
 * Latest version: https://github.com/joewalnes/reconnecting-websocket/
 * - Joe Walnes
 *
 * Syntax
 * ======
 * var socket = new ReconnectingWebSocket(url, protocols, options);
 *
 * Parameters
 * ==========
 * url - The url you are connecting to.
 * protocols - Optional string or array of protocols.
 * options - See below
 *
 * Options
 * =======
 * Options can either be passed upon instantiation or set after instantiation:
 *
 * var socket = new ReconnectingWebSocket(url, null, { debug: true, reconnectInterval: 4000 });
 *
 * or
 *
 * var socket = new ReconnectingWebSocket(url);
 * socket.debug = true;
 * socket.reconnectInterval = 4000;
 *
 * debug
 * - Whether this instance should log debug messages. Accepts true or false. Default: false.
 *
 * automaticOpen
 * - Whether or not the websocket should attempt to connect immediately upon instantiation. The socket can be manually opened or closed at any time using ws.open() and ws.close().
 *
 * reconnectInterval
 * - The number of milliseconds to delay before attempting to reconnect. Accepts integer. Default: 1000.
 *
 * maxReconnectInterval
 * - The maximum number of milliseconds to delay a reconnection attempt. Accepts integer. Default: 30000.
 *
 * reconnectDecay
 * - The rate of increase of the reconnect delay. Allows reconnect attempts to back off when problems persist. Accepts integer or float. Default: 1.5.
 *
 * timeoutInterval
 * - The maximum time in milliseconds to wait for a connection to succeed before closing and retrying. Accepts integer. Default: 2000.
 *
 */
(function (global, factory) {
    if (module.exports){
        module.exports = factory();
    } else {
        global.ReconnectingWebSocket = factory();
    }
})(commonjsGlobal, function () {

    if (!('WebSocket' in window)) {
        return;
    }

    function ReconnectingWebSocket(url, protocols, options) {

        // Default settings
        var settings = {

            /** Whether this instance should log debug messages. */
            debug: false,

            /** Whether or not the websocket should attempt to connect immediately upon instantiation. */
            automaticOpen: true,

            /** The number of milliseconds to delay before attempting to reconnect. */
            reconnectInterval: 1000,
            /** The maximum number of milliseconds to delay a reconnection attempt. */
            maxReconnectInterval: 30000,
            /** The rate of increase of the reconnect delay. Allows reconnect attempts to back off when problems persist. */
            reconnectDecay: 1.5,

            /** The maximum time in milliseconds to wait for a connection to succeed before closing and retrying. */
            timeoutInterval: 2000,

            /** The maximum number of reconnection attempts to make. Unlimited if null. */
            maxReconnectAttempts: null
        };
        if (!options) { options = {}; }

        // Overwrite and define settings with options if they exist.
        for (var key in settings) {
            if (typeof options[key] !== 'undefined') {
                this[key] = options[key];
            } else {
                this[key] = settings[key];
            }
        }

        // These should be treated as read-only properties

        /** The URL as resolved by the constructor. This is always an absolute URL. Read only. */
        this.url = url;

        /** The number of attempted reconnects since starting, or the last successful connection. Read only. */
        this.reconnectAttempts = 0;

        /**
         * The current state of the connection.
         * Can be one of: WebSocket.CONNECTING, WebSocket.OPEN, WebSocket.CLOSING, WebSocket.CLOSED
         * Read only.
         */
        this.readyState = WebSocket.CONNECTING;

        /**
         * A string indicating the name of the sub-protocol the server selected; this will be one of
         * the strings specified in the protocols parameter when creating the WebSocket object.
         * Read only.
         */
        this.protocol = null;

        // Private state variables

        var self = this;
        var ws;
        var forcedClose = false;
        var timedOut = false;
        var eventTarget = document.createElement('div');

        // Wire up "on*" properties as event handlers

        eventTarget.addEventListener('open',       function(event) { self.onopen(event); });
        eventTarget.addEventListener('close',      function(event) { self.onclose(event); });
        eventTarget.addEventListener('connecting', function(event) { self.onconnecting(event); });
        eventTarget.addEventListener('message',    function(event) { self.onmessage(event); });
        eventTarget.addEventListener('error',      function(event) { self.onerror(event); });

        // Expose the API required by EventTarget

        this.addEventListener = eventTarget.addEventListener.bind(eventTarget);
        this.removeEventListener = eventTarget.removeEventListener.bind(eventTarget);
        this.dispatchEvent = eventTarget.dispatchEvent.bind(eventTarget);

        /**
         * This function generates an event that is compatible with standard
         * compliant browsers and IE9 - IE11
         *
         * This will prevent the error:
         * Object doesn't support this action
         *
         * http://stackoverflow.com/questions/19345392/why-arent-my-parameters-getting-passed-through-to-a-dispatched-event/19345563#19345563
         * @param s String The name that the event should use
         * @param args Object an optional object that the event will use
         */
        function generateEvent(s, args) {
        	var evt = document.createEvent("CustomEvent");
        	evt.initCustomEvent(s, false, false, args);
        	return evt;
        }
        this.open = function (reconnectAttempt) {
            ws = new WebSocket(self.url, protocols || []);

            if (reconnectAttempt) {
                if (this.maxReconnectAttempts && this.reconnectAttempts > this.maxReconnectAttempts) {
                    return;
                }
            } else {
                eventTarget.dispatchEvent(generateEvent('connecting'));
                this.reconnectAttempts = 0;
            }

            if (self.debug || ReconnectingWebSocket.debugAll) {
                console.debug('ReconnectingWebSocket', 'attempt-connect', self.url);
            }

            var localWs = ws;
            var timeout = setTimeout(function() {
                if (self.debug || ReconnectingWebSocket.debugAll) {
                    console.debug('ReconnectingWebSocket', 'connection-timeout', self.url);
                }
                timedOut = true;
                localWs.close();
                timedOut = false;
            }, self.timeoutInterval);

            ws.onopen = function(event) {
                clearTimeout(timeout);
                if (self.debug || ReconnectingWebSocket.debugAll) {
                    console.debug('ReconnectingWebSocket', 'onopen', self.url);
                }
                self.protocol = ws.protocol;
                self.readyState = WebSocket.OPEN;
                self.reconnectAttempts = 0;
                var e = generateEvent('open');
                e.isReconnect = reconnectAttempt;
                reconnectAttempt = false;
                eventTarget.dispatchEvent(e);
            };

            ws.onclose = function(event) {
                clearTimeout(timeout);
                ws = null;
                if (forcedClose) {
                    self.readyState = WebSocket.CLOSED;
                    eventTarget.dispatchEvent(generateEvent('close'));
                } else {
                    self.readyState = WebSocket.CONNECTING;
                    var e = generateEvent('connecting');
                    e.code = event.code;
                    e.reason = event.reason;
                    e.wasClean = event.wasClean;
                    eventTarget.dispatchEvent(e);
                    if (!reconnectAttempt && !timedOut) {
                        if (self.debug || ReconnectingWebSocket.debugAll) {
                            console.debug('ReconnectingWebSocket', 'onclose', self.url);
                        }
                        eventTarget.dispatchEvent(generateEvent('close'));
                    }

                    var timeout = self.reconnectInterval * Math.pow(self.reconnectDecay, self.reconnectAttempts);
                    setTimeout(function() {
                        self.reconnectAttempts++;
                        self.open(true);
                    }, timeout > self.maxReconnectInterval ? self.maxReconnectInterval : timeout);
                }
            };
            ws.onmessage = function(event) {
                if (self.debug || ReconnectingWebSocket.debugAll) {
                    console.debug('ReconnectingWebSocket', 'onmessage', self.url, event.data);
                }
                var e = generateEvent('message');
                e.data = event.data;
                eventTarget.dispatchEvent(e);
            };
            ws.onerror = function(event) {
                if (self.debug || ReconnectingWebSocket.debugAll) {
                    console.debug('ReconnectingWebSocket', 'onerror', self.url, event);
                }
                eventTarget.dispatchEvent(generateEvent('error'));
            };
        };

        // Whether or not to create a websocket upon instantiation
        if (this.automaticOpen == true) {
            this.open(false);
        }

        /**
         * Transmits data to the server over the WebSocket connection.
         *
         * @param data a text string, ArrayBuffer or Blob to send to the server.
         */
        this.send = function(data) {
            if (ws) {
                if (self.debug || ReconnectingWebSocket.debugAll) {
                    console.debug('ReconnectingWebSocket', 'send', self.url, data);
                }
                return ws.send(data);
            } else {
                throw 'INVALID_STATE_ERR : Pausing to reconnect websocket';
            }
        };

        /**
         * Closes the WebSocket connection or connection attempt, if any.
         * If the connection is already CLOSED, this method does nothing.
         */
        this.close = function(code, reason) {
            // Default CLOSE_NORMAL code
            if (typeof code == 'undefined') {
                code = 1000;
            }
            forcedClose = true;
            if (ws) {
                ws.close(code, reason);
            }
        };

        /**
         * Additional public API method to refresh the connection if still open (close, re-open).
         * For example, if the app suspects bad data / missed heart beats, it can try to refresh.
         */
        this.refresh = function() {
            if (ws) {
                ws.close();
            }
        };
    }

    /**
     * An event listener to be called when the WebSocket connection's readyState changes to OPEN;
     * this indicates that the connection is ready to send and receive data.
     */
    ReconnectingWebSocket.prototype.onopen = function(event) {};
    /** An event listener to be called when the WebSocket connection's readyState changes to CLOSED. */
    ReconnectingWebSocket.prototype.onclose = function(event) {};
    /** An event listener to be called when a connection begins being attempted. */
    ReconnectingWebSocket.prototype.onconnecting = function(event) {};
    /** An event listener to be called when a message is received from the server. */
    ReconnectingWebSocket.prototype.onmessage = function(event) {};
    /** An event listener to be called when an error occurs. */
    ReconnectingWebSocket.prototype.onerror = function(event) {};

    /**
     * Whether all instances of ReconnectingWebSocket should log debug messages.
     * Setting this to true is the equivalent of setting all instances of ReconnectingWebSocket.debug to true.
     */
    ReconnectingWebSocket.debugAll = false;

    ReconnectingWebSocket.CONNECTING = WebSocket.CONNECTING;
    ReconnectingWebSocket.OPEN = WebSocket.OPEN;
    ReconnectingWebSocket.CLOSING = WebSocket.CLOSING;
    ReconnectingWebSocket.CLOSED = WebSocket.CLOSED;

    return ReconnectingWebSocket;
});
});

/**
 * Created by Rico Trebeljahr <rico.trebeljahr@softgames.de> on 2019-07-23.
 * Copyright © Softgames 2019 - present
 */

const HEARTBEAT_TIMEOUT = 60 * 1000;

function getHeartbeat(ws) {
  let heartbeatTimeoutId = null;

  function handle() {
    cancel();
    heartbeatTimeoutId = setTimeout(() => {
      if (ws.readyState === WS_STATE.OPEN) ws.refresh();
      heartbeatTimeoutId = null;
    }, HEARTBEAT_TIMEOUT);
  }

  function cancel() {
    if (heartbeatTimeoutId) clearTimeout(heartbeatTimeoutId);
    heartbeatTimeoutId = null;
  }

  return {
    handle,
    cancel,
  };
}

function SendPromise(fn, timeoutMs, onClose, query) {
  let timeoutId;
  let closeHandlerOff;

  const queryMsg = query.trim().replace(/\s+/g, ' ');

  const p1 = new Promise(fn);
  const p2 = new Promise((resolve, reject) => {
    timeoutId = setTimeout(
      () =>
        reject(
          new GBCTimeoutError(
            `Timed out after ${timeoutMs} ms. Query: ${queryMsg}`,
          ),
        ),
      timeoutMs,
    );
  });
  const p3 = new Promise((resolve, reject) => {
    if (!onClose) {
      closeHandlerOff = () => {};
      return;
    }
    closeHandlerOff = onClose.once(() =>
      reject(new GBCErrorNoConnection(`GB Client lost WS connection to GB.`)),
    );
  });
  const all = Promise.race([p1, p2, p3]);

  (async () => {
    try {
      await all;
    } catch (error) {
      // [RS] the catch will be handled by the returned promise not here
    } finally {
      clearTimeout(timeoutId);
      closeHandlerOff();
    }
  })();

  return all;
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-07-23.
 * Created by Rico Trebeljahr <rico.trebeljahr@softgames.de> on 2019-07-23.
 * Copyright © Softgames 2019 - present
 */

const MESSAGE_TIMEOUT = 60 * 1000;

function getWssQueryHandler({ log, ws, onClose }) {
  const ackTable = {};

  function handleMessage(message) {
    const { messageId } = message;

    if (!messageId || !ackTable[messageId]) {
      return false;
    }

    const { reject, resolve } = ackTable[messageId];
    delete ackTable[messageId];

    try {
      checkForGraphQlErrors(messageId, message);
      resolve(message);
    } catch (error) {
      reject(error);
    }
    return true;
  }

  async function executeQuery({ messageId, query, vars = {} }) {
    log.debug(`Executing query via wss`);
    if (ws.readyState !== WS_STATE.OPEN) {
      throw new GBCErrorNoConnection(
        'Tried to send a message over a closed socket',
      );
    }
    const sendPromise = new SendPromise(
      (resolve, reject) => {
        ackTable[messageId] = { resolve, reject, query, vars };
      },
      MESSAGE_TIMEOUT,
      onClose,
      query,
    );

    ws.send(
      JSON.stringify({
        query,
        messageId,
        vars,
      }),
    );

    return sendPromise;
  }

  return {
    handleMessage,
    executeQuery,
  };
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-07-23.
 * Created by Rico Trebeljahr <rico.trebeljahr@softgames.de> on 2019-07-23.
 * Copyright © Softgames 2019 - present
 */

const OPEN_TIMEOUT = 10 * 1000;
const MAX_AUTOMATIC_RECONNECT_ATTEMPTS = 10; // [RS] Around 2 min of retries

function getWebsocket(url) {
  try {
    return new reconnectingWebsocket(url);
  } catch (error) {
    onWssError.emit(error);
    throw error;
  }
}

function getWssConnection({ url }) {
  const log = getLogger$1('wssConnection');
  const ws = getWebsocket(url);
  const internalCloseEvent = new Event('wssConnectionClosed');
  const onOpen = new Event('onOpen');
  const hb = getHeartbeat(ws);
  const queryHandler = getWssQueryHandler({
    log,
    ws,
    onClose: internalCloseEvent,
  });
  const loginHandler = getLoginHandler(queryIntern);
  const loggedInFlag = new PromiseFlag('loggedIn', false);
  const openFlag = new PromiseFlag('connectionEstablished', false);
  let wssWorkedOnce = false;
  let wssErrorSinceLastSuccess = 0;
  let closed = false;
  let validCredentials = false;
  let closeInProgress = false;
  let credentials = null;
  let nextMessageId = 1;

  ws.onopen = () => {
    try {
      if (closed) {
        return;
      }
      log.debug(`Connection opened`);
      hb.handle();
      wssWorkedOnce = true;
      wssErrorSinceLastSuccess = 0;
      openFlag.value = true;
      onOpen.emit();
    } catch (error) {
      log.error(`Open handling failed`, error);
    }
  };

  ws.onmessage = (event) => {
    try {
      const parsedData = JSON.parse(event.data);
      const { type, heartbeat, data } = parsedData;

      if (heartbeat) {
        hb.handle();
        return;
      }

      if (type === 'PUSH') {
        if (!closed) {
          onMessage.emit(data);
        }
        return;
      }

      if (queryHandler.handleMessage(parsedData)) {
        return;
      }
      throw new GBCUnknownError('Received unrecognized message');
    } catch (error) {
      log.error('Received invalid event data for socket message', {
        event,
        error,
      });
    }
  };

  ws.onclose = () => {
    try {
      log.debug(`Connection closed`);
      const wasOpen = openFlag.value;
      openFlag.value = false;
      loggedInFlag.value = false;
      if (closeInProgress) {
        closed = true;
      }
      hb.cancel();
      internalCloseEvent.emit();
      if (wasOpen) {
        onClose.emit();
      }
    } catch (error) {
      log.error(`Close handling failed`, error);
    }
  };

  ws.onerror = (event) => {
    if (closed) {
      return;
    }
    wssErrorSinceLastSuccess += 1;
    const error = new GBCWebsocketError(
      `Websocket error event triggered`,
      event,
    );
    onError.emit(error);
    if (
      !wssWorkedOnce ||
      wssErrorSinceLastSuccess >= MAX_AUTOMATIC_RECONNECT_ATTEMPTS
    ) {
      // [RS] Otherwise we would not use the features of the reconnecting webservice
      onWssError.emit(error);
    } else {
      log.notice(
        `WSS connection failed. Waiting for reconnect. ${
          MAX_AUTOMATIC_RECONNECT_ATTEMPTS - wssErrorSinceLastSuccess
        } reconnect attempts left.`,
      );
    }
    openFlag.rejectAll(error);
  };

  async function queryIntern(query, vars = {}) {
    if (closed) {
      throw new Error(`Connection closed`);
    }
    await new QueuePromise(openFlag, (val) => !!val, OPEN_TIMEOUT, 'login');
    const messageId = nextMessageId;
    nextMessageId += 1;
    return queryHandler.executeQuery({ messageId, query, vars });
  }

  async function login() {
    try {
      if (closed) {
        throw new Error(`Connection closed`);
      }
      if (!credentials) {
        throw new Error(`No login credentials found`);
      }
      const { userId, authToken } = credentials;
      await loginHandler.login({ userId, authToken });
      validCredentials = true;
      loggedInFlag.value = true;
      onOpen.once(async () => {
        try {
          await login();
        } catch (loginErrorOnOpen) {
          log.error(
            `Failed re-login. You have to manually login again`,
            loginErrorOnOpen,
          );
        }
      });
    } catch (error) {
      loggedInFlag.rejectAll(error);
      onError.emit(error);
      throw error;
    }
  }

  async function close() {
    try {
      if (closed) {
        return;
      }
      log.debug(`Connection manually closing ...`);
      closeInProgress = true;
      ws.close(1000, 'Manual close');
      log.debug(`... connection closed.`);
    } catch (error) {
      log.error(`... failed to manually close connection`, error);
      throw error;
    }
  }

  return {
    query: async (query, vars) => queryIntern(query, vars),
    login: async (newCredentials) => {
      if (validCredentials) {
        throw new GBCInvalidCallError(`You can't change valid credentials`);
      }
      credentials = newCredentials;
      await login();
    },
    loggedInFlag,
    isLoggedIn: () => loggedInFlag.value,
    close,
    get loginInProgress() {
      return loginHandler.loginInProgress;
    },
    get type() {
      return CONNECTION_TYPE.WSS;
    },
  };
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-07-24.
 * Copyright © Softgames 2019 - present
 */

function wait(ms) {
  return new Promise((resolve, reject) => {
    try {
      setTimeout(resolve, ms || 0);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-07-23.
 * Created by Rico Trebeljahr <rico.trebeljahr@softgames.de> on 2019-07-23.
 * Copyright © Softgames 2019 - present
 */

const MAX_WSS_CONNECTION_ATTEMPTS = 3;
const MAX_HTTPS_CONNECTION_ATTEMPTS = 2;
const MAX_CONNECTION_ATTEMPTS =
  MAX_WSS_CONNECTION_ATTEMPTS + MAX_HTTPS_CONNECTION_ATTEMPTS;
const LOGIN_TIMEOUT = 20 * 1000;

function Connection({ url, fallbackUrl }) {
  const log = getLogger$1('connection');
  let failedWssAttempts = 0;
  let queryCounter = 1;
  let credentials = null;
  let firstLoginDone = false;
  let fallbackToHttps = false;
  let currentConnection = null;

  onLogin.on(() => {
    if (currentConnection.type === CONNECTION_TYPE.WSS) {
      failedWssAttempts = 0;
    }
    firstLoginDone = true;
  });

  onWssError.on((error) => {
    // [RS] We need to check this because this event might by received after the connection is already switched
    if (currentConnection.type !== CONNECTION_TYPE.WSS) {
      return;
    }
    log.debug(`Received wssError`, error);
    currentConnection.close().catch((closeError) => {
      log.warn(`Failed to close failed connection.`, closeError);
    });
    currentConnection = null;
    failedWssAttempts += 1;
    if (failedWssAttempts > MAX_WSS_CONNECTION_ATTEMPTS) {
      fallbackToHttps = true;
    }
  });

  async function retrieveConnection(run = 1) {
    try {
      if (!currentConnection) {
        currentConnection = fallbackToHttps
          ? getHttpsConnection({ url: fallbackUrl })
          : getWssConnection({ url });
        await getServerTimestamp(currentConnection);
        if (firstLoginDone) {
          await currentConnection.login(credentials);
        }
      }
      return currentConnection;
    } catch (error) {
      if (run > MAX_CONNECTION_ATTEMPTS) {
        throw error;
      }
      await wait(500);
      return retrieveConnection(run + 1);
    }
  }

  const getConnection = getExecutionSingleton(retrieveConnection);

  async function getServerTimestamp(connection) {
    const conn = connection || (await getConnection());
    const { data: { now } = {} } = await conn.query(`
      query now {now}
    `);
    return now;
  }

  return {
    login: async (newCredentials) => {
      if (firstLoginDone) {
        throw new GBCInvalidCallError(
          `Login credentials already set. If you wan't to change them please close the connection and retry.`,
        );
      }
      const connection = await getConnection();
      if (connection.loginInProgress) {
        throw new GBCInvalidCallError('Login already in progress');
      }
      if (connection.isLoggedIn()) {
        throw new GBCInvalidCallError('Already logged in');
      }
      credentials = newCredentials;
      await connection.login(credentials);
    },
    getServerTimestamp: async () => getServerTimestamp(),
    unauthorizedQuery: async (...args) => {
      const connection = await getConnection();
      return connection.query(...args);
    },
    query: async (...args) => {
      const queryId = queryCounter;
      queryCounter += 1;
      try {
        log.debug(`Executing query ${queryId}. Fetching connection ...`);
        const connection = await getConnection();
        log.debug(
          `... fetched connection. Checking if first login was done ...`,
        );
        if (!firstLoginDone) {
          log.debug(
            `... query failed since query was executed before first login.`,
          );
          throw new GBCInvalidCallError(
            `Query executed before first login to GB was successful`,
          );
        }
        log.debug(
          `... query first login was done. Waiting for login (max: ${LOGIN_TIMEOUT}ms) ...`,
        );
        await new QueuePromise(
          connection.loggedInFlag,
          (val) => !!val,
          LOGIN_TIMEOUT,
          'query',
        );
        log.debug(`... query logged in. Running query ${queryId} ...`);
        const result = await connection.query(...args);
        log.debug(`... finished query ${queryId}.`);
        return result;
      } catch (error) {
        log.debug(`... failed query ${queryId}.`, error);
        throw error;
      }
    },
    close: async () => {
      const connection = await getConnection();
      connection.close();
    },
    isLoggedIn: async () => {
      const connection = await getConnection();
      return connection.isLoggedIn();
    },
  };
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-21.
 * Copyright © Softgames 2019 - present
 */

/* global atob */

const decodedConfigs = new Map();

function decodeConfig(config) {
  try {
    if (!isNonEmptyString$3(config)) {
      throw new Error(`Must be a not empty string`);
    }
    if (!decodedConfigs.has(config)) {
      decodedConfigs.set(config, JSON.parse(atob(config)));
    }
    return decodedConfigs.get(config);
  } catch (error) {
    const saveError = error || new Error(`Caught empty error`);
    saveError.message = `Failed to decode config: ${saveError.message}`;
    throw saveError;
  }
}

function isString$2(value) {
  return Object.prototype.toString.call(value) === '[object String]';
}

function isNumber$1(value) {
  return Object.prototype.toString.call(value) === '[object Number]';
}

function replaceAll(source, needle, replace) {
  if (!isString$2(source)) {
    throw new Error(`source must be a string for replaceAll`);
  }
  return source.split(needle).join(replace);
}

const SPANS = {
  20: '0-20ms',
  50: '21-50ms',
  100: '51-100ms',
  150: '101-150ms',
  200: '151-200ms',
  300: '201-300ms',
  400: '301-400ms',
  500: '401-500ms',
  750: '501-750ms',
  1000: '751-1000ms',
  1500: '1001-1500ms',
  2000: '1501-2000ms',
};

function castDurationToMsGroupLabel(duration) {
  if (!isNumber$1(duration)) {
    throw new Error('"duration" must be a number');
  }
  const key = Object.keys(SPANS).find((k) => duration < Number(k));
  return key ? SPANS[key] : '>2000ms';
}

let config;
let bsConfig;
let currentGameId;

function getConfig() {
  if (!config) {
    const {
      HOST,
      ENDPOINTS: { WS, HTTP, REST },
    } = decodeConfig(ENCODED_GB_CONFIG);
    config = {
      HOST,
      ENDPOINTS: {
        WS,
        HTTP,
        REST,
      },
    };
  }
  return config;
}

function getBsConfig() {
  if (!bsConfig) {
    const {
      HOST,
      BASIC_AUTH: { USER, PASS },
      ENDPOINTS: {
        CONVERSION,
        GET_SCHEDULED_MESSAGE,
        CREATE_SCHEDULED_MESSAGE,
        DELETE_SCHEDULED_MESSAGE,
      },
    } = decodeConfig(ENCODED_BOTS_CONFIG);
    bsConfig = {
      HOST,
      BASIC_AUTH: {
        USER,
        PASS,
      },
      ENDPOINTS: {
        CONVERSION,
        GET_SCHEDULED_MESSAGE,
        CREATE_SCHEDULED_MESSAGE,
        DELETE_SCHEDULED_MESSAGE,
      },
    };
  }
  return bsConfig;
}

function initWithConfig({ gameId }) {
  if (!isNonEmptyString$3(gameId)) {
    throw new GBCErrorValidationFailed(
      'gameId must be an not-empty string',
      'game-slug',
    );
  }
  if (currentGameId && currentGameId !== gameId) {
    throw new GBCErrorValidationFailed(
      `Already initialized with gameId "${currentGameId}"`,
      currentGameId,
    );
  }
  currentGameId = gameId;
}

function getConnectionUrls() {
  if (!currentGameId) {
    throw new GBCInternalError(`GameUrlManager was not initialized`);
  }
  const {
    HOST,
    ENDPOINTS: { WS, HTTP },
  } = getConfig();
  return {
    url: replaceAll(`wss://${HOST}${WS}`, GAME_ID_PLACEHOLDER, currentGameId),
    fallbackUrl: replaceAll(
      `https://${HOST}${HTTP}`,
      GAME_ID_PLACEHOLDER,
      currentGameId,
    ),
  };
}

function getRestUrl({ extension = '' } = {}) {
  if (!currentGameId) {
    throw new GBCInternalError(`GameUrlManager was not initialized`);
  }
  const {
    HOST,
    ENDPOINTS: { REST },
  } = getConfig();
  return (
    replaceAll(`https://${HOST}${REST}`, GAME_ID_PLACEHOLDER, currentGameId) +
    extension
  );
}

function getBotServiceAuth() {
  const {
    BASIC_AUTH: { USER: BASIC_AUTH_USER, PASS: BASIC_AUTH_PASS },
  } = getBsConfig();
  return {
    username: BASIC_AUTH_USER,
    password: BASIC_AUTH_PASS,
  };
}

function getBotServiceUrlForEndpoint(endpoint) {
  if (!currentGameId) {
    throw new GBCInternalError(`GameUrlManager was not initialized`);
  }
  const { HOST } = getBsConfig();
  return replaceAll(
    `https://${HOST}${endpoint}`,
    GAME_ID_PLACEHOLDER,
    currentGameId,
  );
}

function getBotServiceCreateScheduledMessageUrl({ environment }) {
  const {
    ENDPOINTS: { CREATE_SCHEDULED_MESSAGE },
  } = getBsConfig();
  return getBotServiceUrlForEndpoint(CREATE_SCHEDULED_MESSAGE).replace(
    ENVIRONMENT_PLACEHOLDER,
    environment,
  );
}

function getBotServiceDeleteScheduledMessageUrl({ scheduledMessageId }) {
  const {
    ENDPOINTS: { DELETE_SCHEDULED_MESSAGE },
  } = getBsConfig();
  return getBotServiceUrlForEndpoint(DELETE_SCHEDULED_MESSAGE).replace(
    SCHEDULE_ID_PLACEHOLDER,
    scheduledMessageId,
  );
}

function getBotServiceGetScheduledMessageUrl({ playerId }) {
  const {
    ENDPOINTS: { GET_SCHEDULED_MESSAGE },
  } = getBsConfig();
  return getBotServiceUrlForEndpoint(GET_SCHEDULED_MESSAGE).replace(
    PLAYER_ID_PLACEHOLDER,
    playerId,
  );
}

function getBotServiceConversionUrl() {
  const {
    ENDPOINTS: { CONVERSION },
  } = getBsConfig();
  return getBotServiceUrlForEndpoint(CONVERSION);
}

function wrapParameterCheck(f) {
  return async (...args) => {
    if (args.length === 1 && args[0] === null) {
      throw new GBCErrorValidationFailed(
        'The first parameter of a function call must not be null',
        undefined,
      );
    }
    return f(...args);
  };
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/* eslint-disable-next-line import/no-mutable-exports */
let connection = null;

// ToDo: Write article in client to explain how reconnection works! WSS timeout WSS again until HTTPS

function init$1(gameId) {
  initWithConfig({ gameId });
  const { url, fallbackUrl } = getConnectionUrls();
  if (!connection) {
    connection = new Connection({ url, fallbackUrl });
  }
  return connection;
}

function wrapConnectionCheck(f) {
  return wrapParameterCheck(async (...args) => {
    if (!connection) {
      throw new GBCErrorNoConnection(
        `GB Client not initialized yet. This happens on any execution of login or register.`,
      );
    }
    return f(...args);
  });
}

function close$1() {
  if (connection) {
    connection.close();
    connection = null;
  }
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Please read the authentication tutorial.
 * Use this function to close the connection to the Game Backend
 *
 * @tutorial authentication
 * @example
 * import {close} from '@sgorg/game-backend-client';
 * async function closeConnection() {
 *   await close();
 * }
 *
 * @returns {Promise}
 */
const close = async () => close$1();

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to execute previously created and registered scripts on the Game Backend.
 *
 * @example
 * import {executeRuntimeScript} from '@sgorg/game-backend-client';
 * async function executeScript(scriptRoute, args) {
 *   const result = await executeRuntimeScript({route: scriptRoute, args});
 *   console.log(result);
 *   // Prints what ever the script returned
 *   return result;
 * }
 *
 * @param {Object} options
 * @param {String} options.route Route to the script is defined with the registration of the script in the backend
 * @param {Object} [options.args] Arguments that might be required by the script on execution
 * @returns {Promise<mixed>} Script result
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 *
 */
const executeRuntimeScript = wrapConnectionCheck(
  async ({ route, args } = {}) => {
    if (!isString$2(route) || !route.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "route" parameter. "route" should be not empty string',
        'exec-gb-func',
      );
    }

    const { data: { executeRuntimeScript: result } = {} } =
      await connection.query(
        `
    mutation executeRtScript($route: String!, $args: JSON) {
      executeRuntimeScript(route: $route, args: $args)
    }
  `,
        {
          route,
          args,
        },
      );
    return result;
  },
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to fetch the gameConfig from the GB
 *
 * @example
 * import {getGameConfig} from '@sgorg/game-backend-client';
 * async function getConfig() {
 *   const result = await getGameConfig();
 *   console.log(result);
 *   // Prints what ever the config is
 *   return result;
 * }
 *
 * @returns {Promise<Object>} Config
 *
 * @throws {GBCErrorNoConnection}
 *
 */
const getGameConfig = wrapConnectionCheck(async () => {
  const { data: { titleData } = {} } = await connection.query(`
    query config {
      titleData
    }
  `);
  return titleData;
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to fetch timestamp from GB (Server timestamp).
 *
 * @example
 * import {getServerTimestamp} from '@sgorg/game-backend-client';
 * async function fetchAndLofServerTimestamp() {
 *   const time = await getServerTimestamp();
 *   console.log(time);
 *   // returns 1554470890
 *   return time;
 * }
 *
 * @returns {Promise<number>} unix timestamp
 */
const getServerTimestamp = wrapConnectionCheck(async () =>
  connection.getServerTimestamp(),
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-07-19.
 * Copyright © Softgames 2019 - present
 */

/**
 * @function
 * @memberof module:base
 * @description
 * Return the semantic version of the current GB client
 *
 * @example
 * import {getVersion} from '@sgorg/game-backend-client';
 *
 * function printGbClientVersion() {
 *   console.log(`GB Client Version: v${getVersion()}`);
 * }
 *
 * @returns {String}
 */
const getVersion = () => VERSION;

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Convenient method to check if the user is currently connected and logged in to the game backend
 *
 * @example
 * import {isConnected} from '@sgorg/game-backend-client';
 *
 * async function checkConnection() {
 *   return isConnected();
 * }
 *
 * @returns {Promise<Boolean>}
 */
const isConnected = async () => {
  if (!connection) {
    return false;
  }
  return connection.isLoggedIn();
};

function createUUID() {
  // http://www.ietf.org/rfc/rfc4122.txt
  const s = [];
  const hexDigits = '0123456789abcdef';
  for (let i = 0; i < 36; i += 1) {
    s[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
  }
  s[14] = '4'; // bits 12-15 of the time_hi_and_version field to 0010
  s[19] = hexDigits.substr((s[19] && 0x3) || 0x8, 1); // bits 6-7 of the clock_seq_hi_and_reserved to 01
  // eslint-disable-next-line no-multi-assign
  s[8] = s[13] = s[18] = s[23] = '-';

  return s.join('');
}

function isString(str) {
  return typeof str === 'string';
}

function isInteger$1(num) {
  return typeof num === 'number' && Number.isInteger(num);
}

function isNonEmptyString(str) {
  return !!str && isString(str) && !!str.trim();
}

function validateParams(params) {
  const invalidParams = [];
  // [GI] Regex to match cd[0-9]+ and cm[0-9]+
  const regex = /^c([dm])(\d+)$/;
  const validParams = Object.entries(params).reduce((acc, [key, value]) => {
    const match = key.match(regex);
    // Check if the number received in the match is less than the 200 GA limit
    if (match && match[2] && parseInt(match[2], 10) <= 200) {
      // If it is a custom dimension, the value should be a string
      if (match[1] === 'd' && isNonEmptyString(value))
        return { ...acc, [key]: value };
      // If it is a custom metric, the value should be an integer
      if (match[1] === 'm' && isInteger$1(value)) return { ...acc, [key]: value };
    }
    invalidParams.push(key);
    return acc;
  }, {});
  return { validParams, invalidParams };
}

// [GMI] I use require instead of import for axios here because of
// https://stackoverflow.com/questions/38453307/axios-with-node-js-referenceerror-xmlhttprequest-is-not-defined
// If you find a way to use import but make the build process use 'node_modules/axios/lib/axios.js'instead of
// 'node_modules\axios\dist\axios.js', feel free to implement
const axios = require('axios');

function createAxios({ baseUrl }) {
  const client = axios.create({ baseURL: baseUrl });
  return async ({ method, path, body, params, headers }) => {
    try {
      const { data } = await client.request({
        method,
        url: path,
        data: body,
        params,
        headers,
      });
      return data;
    } catch (error) {
      const { message, response, request } = error;
      if (response) {
        const {
          status = 666,
          data: { message: serverMessage = 'Unknown server response' } = {},
        } = response;
        const { responseURL: targetUrl = path } = request || {};
        throw new Error(
          `Server ${targetUrl} responded with ${status} - ${serverMessage}`,
        );
      } else if (request) {
        throw new Error('No server response');
      } else {
        throw new Error(`Failed to execute request: ${message} - ${error}`);
      }
    }
  };
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2020-01-17.
 * Copyright © Softgames 2020 - present
 */

/**
 * @memberof module:base
 * @constant SEVERITY
 * @type {Object}
 * @property {Number} DEBUG - -1
 * @property {Number} INFO - 0
 * @property {Number} NOTICE - 1
 * @property {Number} WARN - 2
 * @property {Number} ERROR - 3
 * @property {Number} FATAL - 4
 * @property {Number} DISABLE - 5
 */
const SEVERITY = {
  DEBUG: -1,
  INFO: 0,
  NOTICE: 1,
  WARN: 2,
  ERROR: 3,
  FATAL: 4,
  DISABLE: 5,
};

const allowedSeverities$1 = Object.values(SEVERITY);

function isValidSeverity(severity) {
  return allowedSeverities$1.includes(severity);
}

function isString$1(str) {
  return typeof str === 'string';
}

function isNonEmptyString$1(str) {
  return !!str && isString$1(str) && !!str.trim();
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2020-01-17.
 * Copyright © Softgames 2020 - present
 */

const SEVERITY$1 = SEVERITY;

let NAME = null;

const consoleExists =
  typeof console !== 'undefined' &&
  !!console &&
  typeof console.log === 'function';

function getLogFunction(target, ...alternatives) {
  if (typeof console[target] === 'function') {
    return console[target].bind(console);
  }
  if (!alternatives || !alternatives.length) {
    return console.log.bind(console);
  }
  return getLogFunction(...alternatives);
}

function getLog(logLevel) {
  return {
    log:
      !consoleExists || logLevel > SEVERITY$1.INFO
        ? () => {}
        : getLogFunction('info'),
    debug:
      !consoleExists || logLevel > SEVERITY$1.DEBUG
        ? () => {}
        : getLogFunction('debug'),
    info:
      !consoleExists || logLevel > SEVERITY$1.INFO
        ? () => {}
        : getLogFunction('info'),
    notice:
      !consoleExists || logLevel > SEVERITY$1.NOTICE
        ? () => {}
        : getLogFunction('notice', 'info'),
    warn:
      !consoleExists || logLevel > SEVERITY$1.WARN
        ? () => {}
        : getLogFunction('warn', 'notice', 'info'),
    error:
      !consoleExists || logLevel > SEVERITY$1.ERROR
        ? () => {}
        : getLogFunction('error'),
    fatal:
      !consoleExists || logLevel > SEVERITY$1.FATAL
        ? () => {}
        : getLogFunction('fatal', 'error'),
  };
}

let currentLogger = getLog(SEVERITY$1.DISABLE);

/**
 * @memberof module:base
 * @function setLogSeverity
 * @description
 * Sets the  project's severity to log.
 * The given severity and all higher severities are logged.
 *
 * @param {Object} options
 * @param {Number} options.severity - Severity of type SEVERITY
 * @example
 * import { setLogSeverity, SEVERITY } from '@sgorg/fe-logger';
 *
 * setLogSeverity({ severity: SEVERITY.WARN });
 * // After this all warn, error and fatal logs are printed in the console.
 *
 * @returns {void}
 */
function setLogSeverity({ severity } = {}) {
  if (!isValidSeverity(severity)) {
    throw new Error(
      `Severity must be on of ${allowedSeverities$1.join(
        ', ',
      )}. Suggest to use SEVERITY enum.`,
    );
  }
  currentLogger = getLog(severity);
}

/**
 * @memberof module:base
 * @function init
 * @description
 * Initializes all loggers for the project.
 * Sets the project name and the project's severity to log (see also setLogSeverity).
 *
 * @param {Object} options
 * @param {String} options.name - Name of the logger
 * @param {Number} options.severity - Severity of type SEVERITY
 * @example
 * import { init, SEVERITY } from '@sgorg/fe-logger';
 *
 * init({ name: 'client', severity: SEVERITY.DEBUG });
 *
 * @returns {void}
 */
function init({ name, severity = SEVERITY$1.DISABLE } = {}) {
  if (!isNonEmptyString$1(name)) {
    throw new Error(`Name must be a non empty string`);
  }
  setLogSeverity({ severity });
  NAME = name;
}

function getLogHandler(severity, name) {
  if (typeof currentLogger[severity] !== 'function') {
    throw new Error(
      `There is no log method with name "${severity}" for generating logger "${name}"`,
    );
  }
  return (...args) => {
    try {
      currentLogger[severity](`[${NAME}][${severity}]${name}`, ...args);
    } catch (error) {
      // [RS] Executing a log never generates an error
    }
  };
}

/**
 * @memberof module:base
 * @function getLogger
 * @description
 * Creates a new logger instance. By default all logs are disabled. Check init or setLogSeverity to change that.
 *
 * @param {Object} options
 * @param {String} options.name - Name of the logger
 * @example
 * import { getLogger } from '@sgorg/fe-logger';
 *
 * const log = getLogger({ name: 'utils' });
 *
 * @returns {module:base.Logger}
 */
function getLogger({ name } = {}) {
  if (!isNonEmptyString$1(name)) {
    throw new Error(`Name must be non-empty string`);
  }
  return Object.keys(currentLogger).reduce(
    (m, severity) => ({
      ...m,
      [severity]: getLogHandler(severity, name),
    }),
    {},
  );
}

init({
  name: 'GA-Client',
  severity: SEVERITY$1.INFO,
});

function getLog$1(name) {
  return getLogger({ name });
}

const log$4 = getLog$1('main');

/**
 * @typedef {Object} Client
 * @property {function} trackEvent
 */

/**
 * @function createClient
 * @description
 * Initialize the Google Analytics client
 *
 * @param {Object} clientOptions
 * @param {String} clientOptions.gaTrackingId - Tracking ID of the Google Analytics property.
 * @param {String} [clientOptions.clientID] - Unique identifier for the client.
 * @example
 * import { createClient } from '@sgorg/ga-client';
 *
 * const client = createClient({gaTrackingId: 'UA-XXXXX-Y'})
 * @returns {Client}
 */
const createClient = (clientOptions) => {
  try {
    const { gaTrackingId, clientID } = clientOptions || {};
    const clientId = clientID || createUUID();
    const axiosClient = createAxios({
      baseUrl: 'https://www.google-analytics.com',
    });

    const tracking = async (options) => {
      try {
        const { category, action, label, value, ...customParams } =
          options || {};
        if (!isNonEmptyString(category)) {
          throw new Error(
            'Invalid tracking data: "category" must be a non empty string',
          );
        }
        if (!isNonEmptyString(action)) {
          throw new Error(
            'Invalid tracking data: "action" must be a non empty string',
          );
        }
        if (label && !isNonEmptyString(label)) {
          throw new Error(
            'Invalid tracking data: "label" must be a non empty string',
          );
        }
        if (value && !isInteger$1(value)) {
          throw new Error(
            'Invalid tracking data: "value" must be an integer number',
          );
        }
        const { validParams, invalidParams } = validateParams(customParams);
        if (invalidParams.length) {
          log$4.notice(
            `The following properties are not allowed as params or have invalid value: ${invalidParams.join(
              ', ',
            )}`,
          );
        }

        const requestOptions = {
          method: 'post',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          },
          path: '/collect',
          params: {
            // API Version.
            v: '1',
            // Tracking ID / Property ID.
            tid: gaTrackingId,
            // Anonymous Client Identifier. Ideally, this should be a UUID that
            // is associated with particular user, device, or browser instance.
            cid: clientId,
            // Event hit type.
            t: 'event',
            // Event category. Can be any string.
            ec: category,
            // Event action. Can be any string.
            ea: action,
            // Event label. Can be any string. Optional.
            ...(label && { el: label }),
            // Event value. Can be any number. Optional.
            ...(value && { ev: value }),
            // Optional and custom parameters.
            ...validParams,
          },
        };
        await axiosClient(requestOptions);
      } catch (error) {
        throw new Error(
          `Error sending your request to Google: ${error.toString()}`,
        );
      }
    };

    return {
      /**
       * @function trackEvent
       * @description
       * Send Track Event to Google Analytics.
       *
       * @param {Object} options
       * @param {String} options.category - Category of the event.
       * @param {String} options.action - Action of the event.
       * @param {String} [options.label] - Label of the event.
       * @param {Number} [options.value] - Value of the event.
       * @param {Number|String} [options.*] - Custom Metrics and Custom Dimensions, with the index e.g. cm2, cd1.
       * @example
       * const trackOptions = {
       *  category: 'testCategory',
       *  action: 'testAction',
       * }
       *
       * client.trackEvent(trackOptions);
       * @returns {Client}
       */
      trackEvent(options) {
        tracking(options)
          .then(() => log$4.debug('Event successfully tracked.'))
          .catch((error) =>
            log$4.notice(`Tracking event failed: ${error.toString()}`),
          );
      },
    };
  } catch (error) {
    log$4.warn(`Error creating client: ${error.toString()}`);
    return {
      trackEvent: () =>
        log$4.notice(
          'There was an error creating the client, therefore your event will not be tracked.',
        ),
    };
  }
};

function isFunction(func) {
  return typeof func === 'function';
}

function createSingleton(construct) {
  if (!isFunction(construct)) {
    throw new Error(`Singleton construct function must be a function`);
  }
  let singleton;
  return () => {
    if (singleton === undefined) {
      singleton = construct();
      if (singleton instanceof Promise) {
        singleton.catch(() => {
          singleton = undefined;
        });
      }
    }
    return singleton;
  };
}

const log$3 = getLogger$1(`gaTracker`);

let trackingEnabled = false;

const getGaClient = createSingleton(() => {
  if (!isNonEmptyString$3(GA_TRACKING_ID)) {
    throw new GBCInternalError('Google Analytics tracking ID must be defined.');
  }
  return createClient({
    gaTrackingId: GA_TRACKING_ID,
  });
});

function setTrackingEnabled(enable) {
  trackingEnabled = !!enable;
}

function isTrackingEnabled() {
  return trackingEnabled;
}

function gaTracker({ category, action, label }) {
  try {
    if (!isTrackingEnabled()) {
      return;
    }
    const gaClient = getGaClient();
    const trackReturn = gaClient.trackEvent({
      category,
      action,
      label,
    });
    if (trackReturn instanceof Promise) {
      trackReturn.catch((error) => {
        log$3.error(`Failed to track event async`, error);
      });
    }
  } catch (error) {
    log$3.error(`Failed to track event`, error);
  }
}

const log$2 = getLogger$1('durationTracker');

function getDurationTacker({ category, gameId }) {
  try {
    if (!isTrackingEnabled()) {
      return () => {};
    }
    if (!isNonEmptyString$3(gameId)) {
      throw new GBCInternalError(`"gameId" must be a string`);
    }
    const trackingBeginTime = Date.now();
    return () => {
      try {
        const trackingDuration = Date.now() - trackingBeginTime;
        const trackOptions = {
          category,
          action: gameId,
          label: castDurationToMsGroupLabel(trackingDuration),
        };
        log$2.debug(`Try to track`, trackOptions);
        gaTracker(trackOptions);
      } catch (trackingError) {
        log$2.error(`Failed to track event`, trackingError);
      }
    };
  } catch (error) {
    log$2.error(`Failed to get tracker`, error);
    return () => {};
  }
}

function wrapDurationTracking(func, { category } = {}) {
  if (!isNonEmptyString$3(category)) {
    throw new GBCInternalError(`"category" must be a string`);
  }
  if (!isFunction(func)) {
    throw new GBCInternalError(`"func" must be a function`);
  }
  return async (...args) => {
    const [{ gameId } = {}] = args || [];
    const track = getDurationTacker({ category, gameId });
    const result = await func(...args);
    track();
    return result;
  };
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

const WRONG_CREDENTIALS_ERROR_CODES = [
  ERROR_CODE.NOT_FOUND,
  ERROR_CODE.NOT_AUTHORIZED,
];

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to login into the game backend with the credentials you received via the register* functions.
 *
 * @example
 * import { loginWithGameBackendCredentials } from '@sgorg/game-backend-client';
 * async function loginWithCredentialsAndLog(){
 *   await loginWithGameBackendCredentials({gameId: 'bb', userId:'1233',authToken:'sdfnsndb2377892rhj3rhf834'})
 *   console.log('Connection established and logged in');
 * }
 *
 * @param {Object} options
 * @param {String} options.gameId game id
 * @param {String} options.userId user id
 * @param {String} options.authToken auth token
 * @returns {Promise}
 *
 * @throws {GBCErrorValidationFailed}
 * @throws {GBCErrorNoConnection}
 * @throws {GBCInvalidCallError}
 * @throws {GBCWrongCredentialsError}
 * @throws {GBCKnownGraphQLError}
 * @throws {GBCUnknownGraphQLError}
 */
const loginWithGameBackendCredentials = wrapParameterCheck(
  wrapDurationTracking(
    async ({ gameId, userId, authToken }) => {
      if (!isString$2(gameId) || !gameId.trim()) {
        throw new GBCErrorValidationFailed(
          'Invalid "gameId" parameter. "gameId" should be not empty string',
          'my-game-slug',
        );
      }
      if (!isString$2(userId) || !userId.trim()) {
        throw new GBCErrorValidationFailed(
          'Invalid "userId" parameter. "userId" should be not empty string',
          '15276121235',
        );
      }
      if (!isString$2(authToken) || !authToken.trim()) {
        throw new GBCErrorValidationFailed(
          'Invalid "authToken" parameter. "authToken" should be not empty string',
          '15276121235',
        );
      }

      const connection = init$1(gameId);
      try {
        await connection.login({ userId, authToken });
      } catch (error) {
        if (!error.code) {
          throw new GBCUnknownError(
            `Failed to login due to unknown error`,
            error,
          );
        }
        if (!WRONG_CREDENTIALS_ERROR_CODES.includes(error.code)) {
          throw error;
        }
        throw new GBCWrongCredentialsError(
          `Please try to register again, to receive new credentials`,
          error,
        );
      }
    },
    {
      category: 'gameClientLoginDuration',
    },
  ),
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to register user into GB. This function can be used only for users from Facebook platform.
 * Result of this function later on can be used to login via GB credentials.
 *
 * @example
 * import {registerFacebookInstantGameSignature} from '@sgorg/game-backend-client';
 *
 * async function register({gameId}) {
 *   const playerInfo = await window.FBInstant.player.getSignedPlayerInfoAsync();
 *   const signature = playerInfo.getSignature();
 *   const {userId, authToken} = await registerFacebookInstantGameSignature({gameId, signature});
 *   console.log({userId, authToken});
 *   //  {
 *   //    userId: '123',
 *   //    authToken: '12jfd85nas7123nfgsadu12',
 *   //  }
 *   return {userId, authToken}; // This data can be stored away and than used for direct login the next visit of the  user
 * }
 *
 * @param {Object} options
 * @param {String} options.gameId
 * @param {String} options.signature
 * @return {Promise<Credentials>}
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const registerWithFacebookInstantGameSignature = wrapParameterCheck(
  wrapDurationTracking(
    async ({ gameId, signature }) => {
      if (!isString$2(gameId) || !gameId.trim()) {
        throw new GBCErrorValidationFailed(
          'Invalid "gameId" parameter. "gameId" should be not empty string',
          'my-game-slug',
        );
      }
      if (!isString$2(signature) || !signature.trim()) {
        throw new GBCErrorValidationFailed(
          'Invalid "signature" parameter. "signature" should be not empty string',
          '567576%ghfhgfh67-hjagj',
        );
      }

      const connection = init$1(gameId);
      const { data: { registerFbInstant: { userId, authToken } = {} } = {} } =
        await connection.unauthorizedQuery(
          `
        mutation registerFbIg($gameId: ID!, $signature: String!) {
          registerFbInstant(gameId: $gameId, signature: $signature) {
            authToken
            userId
          }
        }
      `,
          {
            gameId,
            signature,
          },
        );
      return {
        userId,
        authToken,
      };
    },
    {
      category: 'gameClientRegisterDuration',
    },
  ),
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-05-27.
 * Copyright © Softgames 2019 - present
 */

const allowedEnvironments = Object.values(ENVIRONMENT);

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to register user into GB. This function can be used only for users from Facebook platform.
 * Result of this function later on can be used to login via GB credentials.
 *
 * @example
 * import {registerWithLineAccessToken, ENVIRONMENT} from '@sgorg/game-backend-client';
 *
 * function getEnvironment() {
 *     if (isProduction()) {
 *         return ENVIRONMENT.PRODUCTION;
 *     }
 *     if (isStaging()) {
 *         return ENVIRONMENT.STAGING;
 *     }
 *     return ENVIRONMENT.SANDBOX;
 * }
 *
 * async function register({gameId}) {
 *   const environment = getEnvironment();
 *   const accessToken = await getLineAccessTokenViaSomeWay();
 *   const {userId, authToken} = await registerWithLineAccessToken({gameId, accessToken, environment});
 *   console.log({userId, authToken});
 *   //  {
 *   //    userId: '123',
 *   //    authToken: '12jfd85nas7123ngs3adu12',
 *   //  }
 *   return {userId, authToken}; // This data can be stored away and than used for direct login the next visit of the  user
 * }
 *
 * @param {Object} options
 * @param {String} options.gameId
 * @param {String} options.accessToken
 * @param {ENVIRONMENT} options.environment
 * @return {Promise<Credentials>}
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const registerWithLineAccessToken = wrapParameterCheck(
  wrapDurationTracking(
    async ({ gameId, accessToken, environment } = {}) => {
      if (!isString$2(gameId) || !gameId.trim()) {
        throw new GBCErrorValidationFailed(
          'Invalid "gameId" parameter. "gameId" must be not empty string',
          'my-game-slug',
        );
      }
      if (!isString$2(accessToken) || !accessToken.trim()) {
        throw new GBCErrorValidationFailed(
          'Invalid "accessToken" parameter. "accessToken" must be not empty string',
          '567576token1231eff1sada',
        );
      }
      if (!allowedEnvironments.includes(environment)) {
        throw new GBCErrorValidationFailed(
          `Invalid "environment" parameter. "environment" must be on of "${allowedEnvironments.join(
            '", "',
          )}".`,
          ENVIRONMENT.PRODUCTION,
        );
      }
      const connection = init$1(gameId);
      const { data: { registerLine: { userId, authToken } = {} } = {} } =
        await connection.unauthorizedQuery(
          `
      mutation registerLine($gameId: ID!, $accessToken: String!, $environment: ENVIRONMENT!) {
        registerLine(gameId: $gameId, accessToken: $accessToken, environment: $environment) {
          authToken
          userId
        }
      }
    `,
          {
            gameId,
            accessToken,
            environment,
          },
        );
      return {
        userId,
        authToken,
      };
    },
    {
      category: 'gameClientRegisterDuration',
    },
  ),
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-05-27.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to register user into GB. This function can be used only for users from Facebook platform.
 * Result of this function later on can be used to login via GB credentials.
 *
 * @example
 * import {registerWithLineAccessToken} from '@sgorg/game-backend-client';
 *
 * async function register({gameId}) {
 *   const playerId = await getMobagePlayerId();
 *   const code = await getMobageCode(); // See https://docs.mobage.com/display/JPJSSDK/Login_without_Screen_Transition
 *   const {userId, authToken} = await registerWithMobageCode({gameId, code, playerId});
 *   console.log({userId, authToken});
 *   //  {
 *   //    userId: '123',
 *   //    authToken: '12jfd85nas7123ngs3adu12',
 *   //  }
 *   return {userId, authToken}; // This data can be stored away and than used for direct login the next visit of the  user
 * }
 *
 * @param {Object} options
 * @param {String} options.gameId
 * @param {String} options.code
 * @param {String} options.playerId
 * @return {Promise<Credentials>}
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const registerWithMobageCode = wrapParameterCheck(
  wrapDurationTracking(
    async ({ gameId, code, playerId } = {}) => {
      if (!isString$2(gameId) || !gameId.trim()) {
        throw new GBCErrorValidationFailed(
          'Invalid "gameId" parameter. "gameId" must be not empty string',
          'my-game-slug',
        );
      }
      if (!isString$2(code) || !code.trim()) {
        throw new GBCErrorValidationFailed(
          'Invalid "code" parameter. "code" must be not empty string',
          '567576token1231eff1sada',
        );
      }
      if (
        !isString$2(playerId) ||
        !playerId.trim() ||
        playerId.split(':').length !== 2
      ) {
        throw new GBCErrorValidationFailed(
          'Invalid "playerId" parameter. "playerId" must be not empty string with one colon separating env and id',
          'sb.mbga.jp:123456',
        );
      }
      const connection = init$1(gameId);
      const { data: { registerMobage: { userId, authToken } = {} } = {} } =
        await connection.unauthorizedQuery(
          `
      mutation registerMobage($gameId: ID!, $code: String!, $playerId: ID!) {
        registerMobage(gameId: $gameId, code: $code, playerId: $playerId) {
          authToken
          userId
        }
      }
    `,
          {
            gameId,
            code,
            playerId,
          },
        );
      return {
        userId,
        authToken,
      };
    },
    {
      category: 'gameClientRegisterDuration',
    },
  ),
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2020-01-27.
 * Copyright © Softgames 2020 - present
 */

function jsonParse(encoded, errorMessage) {
  if (!isString$2(encoded) || !encoded.trim()) {
    throw new Error(`No string to parse provided`);
  }
  try {
    return JSON.parse(encoded);
  } catch (error) {
    throw new Error(errorMessage || 'Failed to parse json string');
  }
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-10-30.
 * Copyright © Softgames 2019 - present
 */

/* global window */

const log$1 = getLogger$1(`GadgetIoApi`);

function getGadgetIoClient() {
  const {
    io: {
      RequestParameters: {
        METHOD,
        CONTENT_TYPE,
        POST_DATA,
        AUTHORIZATION,
        REFRESH_INTERVAL,
      },
      AuthorizationType: { SIGNED },
      MethodType: { POST },
      ContentType: { JSON },
      makeRequest,
      encodeValues,
    } = {},
  } = window.gadgets || {};
  if (!makeRequest) {
    throw new Error(`Mobage gadgets not loaded`);
  }
  return {
    request: async ({ url, body }) => {
      log$1.debug(`Requesting ...`, { url, body });
      return new Promise((resolve, reject) => {
        try {
          makeRequest(
            url,
            (response) => {
              log$1.debug(`... received response`, { url, body, response });
              try {
                const { text } = response;
                const data = jsonParse(
                  text,
                  'Invalid response from GadgetIo API',
                );
                resolve(data);
              } catch (error) {
                reject(error);
              }
            },
            {
              [METHOD]: POST,
              [CONTENT_TYPE]: JSON,
              [POST_DATA]: encodeValues(body),
              [AUTHORIZATION]: SIGNED,
              [REFRESH_INTERVAL]: 0,
            },
          );
        } catch (error) {
          log$1.debug(`... received error`, { url, body, error });
          reject(error);
        }
      });
    },
  };
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-10-30.
 * Copyright © Softgames 2019 - present
 */

async function requestViaGadgetIo({ url, body }) {
  const client = getGadgetIoClient();
  return client.request({
    url,
    body,
  });
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-10-30.
 * Copyright © Softgames 2019 - present
 */

const log = getLogger$1(`registerWithMobageGadgetIo`);

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to register user into GB. This function can be used only for users from Mobage platform.
 * Result of this function later on can be used to login via GB credentials.
 *
 * @example
 * import {registerWithMobageGadgetIo, ENVIRONMENT} from '@sgorg/game-backend-client';
 *
 * async function register({gameId, playerId}) {
 *   const {userId, authToken} = await registerWithMobageGadgetIo({playerId, gameId});
 *   console.log({userId, authToken});
 *   //  {
 *   //    userId: '123',
 *   //    authToken: '12jfd85nas7123ngs3adu12',
 *   //  }
 *   return {userId, authToken}; // This data can be stored away and than used for direct login the next visit of the  user
 * }
 *
 * @param {Object} options
 * @param {String} options.gameId
 * @param {String} options.playerId
 * @return {Promise<Credentials>}
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const registerWithMobageGadgetIo = wrapParameterCheck(
  wrapDurationTracking(
    async ({ gameId, playerId } = {}) => {
      try {
        log.debug(`Start ...`);
        if (!isString$2(gameId) || !gameId.trim()) {
          throw new GBCErrorValidationFailed(
            'Invalid "gameId" parameter. "gameId" must be not empty string',
            'my-game-slug',
          );
        }

        if (
          !isString$2(playerId) ||
          !playerId.trim() ||
          playerId.split(':').length !== 2
        ) {
          throw new GBCErrorValidationFailed(
            'Invalid "playerId" parameter. "playerId" must be not empty string with one colon separating env and id',
            'sb.mbga.jp:123456',
          );
        }

        init$1(gameId);
        log.debug(`... initialized ...`);
        const { userId, authToken } = await requestViaGadgetIo({
          url: getRestUrl({ extension: '/mobage/register' }),
          body: {
            gameId,
            playerId,
          },
        });
        log.debug(`... done.`, {
          userId,
          authToken,
        });
        return {
          userId,
          authToken,
        };
      } catch (error) {
        log.debug(`... failed.`, {
          error,
        });
        throw error;
      }
    },
    {
      category: 'gameClientRegisterDuration',
    },
  ),
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-09.
 * Copyright © Softgames 2019 - present
 */

const urlPattern = new RegExp(
  '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$', // fragment locator
  'i',
);

function parseQueryString(queryString) {
  const qs = queryString.split('+').join(' ');
  const params = {};
  let tokens;
  const re = /[?&]?([^=]+)=([^&]*)/g;

  /* eslint-disable-next-line no-cond-assign */
  while ((tokens = re.exec(qs))) {
    params[decodeURIComponent(tokens[1])] = decodeURIComponent(tokens[2]);
  }

  return params;
}

function isUrl(url) {
  return !!urlPattern.test(url);
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to register user into GB. This function can be used only for users from OK platform.
 * Result of this function later on can be used to login via GB credentials.
 *
 * @example
 * import {registerWithOkQueryString} from '@sgorg/game-backend-client';
 *
 * async function register({gameId}) {
 *   const queryString = window.location.search;
 *   const {userId, authToken} = await registerWithOkQueryString({gameId, queryString});
 *   console.log({userId, authToken});
 *   //  {
 *   //    userId: '123',
 *   //    authToken: '12jfd85nas7123nfgsadu12',
 *   //  }
 *   return {userId, authToken}; // This data can be stored away and than used for direct login the next visit of the  user
 * }
 *
 * @param {Object} options
 * @param {String} options.gameId
 * @param {String} options.queryString queryString query string of the ok iframe url the game is running in
 * @return {Promise<Credentials>}
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const registerWithOkQueryString = wrapParameterCheck(
  wrapDurationTracking(
    async ({ gameId, queryString }) => {
      if (!isString$2(gameId) || !gameId.trim()) {
        throw new GBCErrorValidationFailed(
          'Invalid "gameId" parameter. "gameId" should be not empty string',
          'my-game-slug',
        );
      }
      if (!isString$2(queryString) || !queryString.trim()) {
        throw new GBCErrorValidationFailed(
          'Invalid "queryString" parameter. "queryString" should be not empty string',
          '?a=b&c=d',
        );
      }

      const connection = init$1(gameId);
      const urlQuery = parseQueryString(queryString);
      const {
        data: { registerOdnoklassniki: { userId, authToken } = {} } = {},
      } = await connection.unauthorizedQuery(
        `
      mutation registerOk($gameId: ID!, $urlQuery: JSON!) {
        registerOdnoklassniki(gameId: $gameId, gameStartingQS: $urlQuery) {
          authToken
          userId
        }
      }
    `,
        {
          gameId,
          urlQuery,
        },
      );
      return {
        userId,
        authToken,
      };
    },
    {
      category: 'gameClientRegisterDuration',
    },
  ),
);

function isBoolean$1(value) {
  return typeof value === 'boolean';
}

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to enable or to disable Google Analytics Tracking
 *
 * @example
 * import { setGaTracking } from '@sgorg/game-backend-client';
 * async function enableGaTracking(){
 *   setGaTracking({ enable: true });
 * }
 *
 * @param {Object} options
 * @param {Boolean} options.enable Enable Google Analytics Tracking
 * @returns {Promise}
 */
const setGaTracking = ({ enable } = {}) => {
  if (!isBoolean$1(enable)) {
    throw new GBCErrorValidationFailed(`enable must be a boolean.`, true);
  }
  setTrackingEnabled(enable);
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-17.
 * Copyright © Softgames 2019 - present
 */

const allowedSeverities = Object.values(SEVERITY$3);

/**
 * @async
 * @function
 * @memberof module:base
 * @description
 * Use this function to set the logLevel of the GameBackend client
 *
 * @example
 * import { setLogLevel } from '@sgorg/game-backend-client';
 * async function changeLogLevel(severity){
 *   await setLogLevel({severity})
 *   console.log(`Set logLevel to ${severity}`);
 * }
 *
 * @param {Object} options
 * @param {Number} options.severity Integer between -1 and 5. 5 effectively disables logging.
 * @returns {Promise}
 */
const setLogLevel = wrapParameterCheck(async ({ severity }) => {
  if (!allowedSeverities.includes(severity)) {
    throw new GBCErrorValidationFailed(
      `severity must be on of the following integers "${allowedSeverities.join(
        '", "',
      )}".`,
      allowedSeverities[0],
    );
  }
  setLogLevel$1(severity);
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

var base = /*#__PURE__*/Object.freeze({
  __proto__: null,
  close: close,
  executeRuntimeScript: executeRuntimeScript,
  getGameConfig: getGameConfig,
  getServerTimestamp: getServerTimestamp,
  getVersion: getVersion,
  isConnected: isConnected,
  loginWithGameBackendCredentials: loginWithGameBackendCredentials,
  registerWithFacebookInstantGameSignature: registerWithFacebookInstantGameSignature,
  registerWithLineAccessToken: registerWithLineAccessToken,
  registerWithMobageCode: registerWithMobageCode,
  registerWithMobageGadgetIo: registerWithMobageGadgetIo,
  registerWithOkQueryString: registerWithOkQueryString,
  setGaTracking: setGaTracking,
  setLogLevel: setLogLevel
});

function isArray(value) {
  return Object.prototype.toString.call(value) === '[object Array]';
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-03.
 * Copyright © Softgames 2019 - present
 */

function validateCurrencyInputDataArray(currencies, allowEmpty = false) {
  if (!isArray(currencies)) {
    throw new GBCErrorValidationFailed(
      'Invalid "currencies" parameter. "currencies" must be a array of CurrencyInputData',
      [{ currencySlug: 'currency1', amount: 1 }],
    );
  }
  if (!allowEmpty && !currencies.length) {
    throw new GBCErrorValidationFailed(
      'Invalid "currencies" parameter. "currencies" must be a not empty array.',
      [{ currencySlug: 'currency1', amount: 1 }],
    );
  }
  if (
    currencies.find((currency) => {
      const { currencySlug, amount } = currency || {};
      if (!currencySlug || !isString$2(currencySlug) || !currencySlug.trim()) {
        return true;
      }
      return !amount || !isNumber$1(amount);
    })
  ) {
    throw new GBCErrorValidationFailed(
      'Invalid "currencies" parameter. "currencies" must be a not empty array of CurrencyInputData. At least one item does not follow the CurrencyInputData schema.',
      [{ currencySlug: 'currency1', amount: 1 }],
    );
  }
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-03.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * With this function you can add or subtract one or multiple currencies to/of the current users wallet.
 * The input is an array of CurrencyInputData. Which is a plain object containing the currencySlug and the amount.
 *
 * For TYPESCRIPT: We provide the CurrencyInputData interface.
 *
 * @example
 * import {addCurrencies} from '@sgorg/game-backend-client';
 *
 * async function handleCurrencyExchange({removeCurrencyName, removeCurrencyAmount, addCurrencyName, addCurrencyAmount}) {
 *   return addCurrencies({items: [
 *       {itemSlug: removeCurrencyName, amount: removeCurrencyAmount > 0 ? -1 * removeCurrencyAmount : removeCurrencyAmount},
 *       {itemSlug: addCurrencyName, amount: addCurrencyAmount > 0 ? addCurrencyAmount : -1 * addCurrencyAmount},
 *   ]});
 * }
 *
 * @param {Object} options
 * @param {Array<CurrencyInputData>} options.currencies List of CurrencyInputData. Consisting of unique slug of the currency to change and the amount. The CurrencyInputData must have been created in GB via ADM
 * @return {Promise}
 * @memberof module:economy
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const addCurrencies = wrapConnectionCheck(async ({ currencies } = {}) => {
  validateCurrencyInputDataArray(currencies);
  await connection.query(
    `
      mutation addCurrencies($currencies: [CurrencyInputData!]!) {
          addCurrencies(currencies: $currencies)
      }
  `,
    {
      currencies,
    },
  );
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-03.
 * Copyright © Softgames 2019 - present
 */

function validateItemInputDataArray(items, allowEmpty = false) {
  if (!isArray(items)) {
    throw new GBCErrorValidationFailed(
      'Invalid "items" parameter. "items" must be a array of ItemInputData',
      [{ itemSlug: 'item1', amount: 1 }],
    );
  }
  if (!allowEmpty && !items.length) {
    throw new GBCErrorValidationFailed(
      'Invalid "items" parameter. "items" must be a not empty array.',
      [{ itemSlug: 'item1', amount: 1 }],
    );
  }
  if (
    items.find((item) => {
      const { itemSlug, amount } = item || {};
      if (!itemSlug || !isString$2(itemSlug) || !itemSlug.trim()) {
        return true;
      }
      return !amount || !isNumber$1(amount) || amount % 1 !== 0;
    })
  ) {
    throw new GBCErrorValidationFailed(
      'Invalid "items" parameter. "items" must be a not empty array of ItemInputData. At least one item does not follow the ItemInputData schema.',
      [{ itemSlug: 'item1', amount: 1 }],
    );
  }
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-03.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * With this function you can add or subtract one or multiple items to/of the item pool of the current user.
 * The input is an array of ItemInputData. Which is a plain object containing the itemSlug and the amount.
 * The amount must be an integer.
 *
 * For TYPESCRIPT: We provide the ItemInputData interface.
 *
 * @example
 * import {addItems} from '@sgorg/game-backend-client';
 *
 * async function handleOneForOneItemExchange({removeItemName, addItemName}) {
 *   return addItems({items: [
 *       {itemSlug: removeItemName, amount: -1},
 *       {itemSlug: addItemName, amount: 1},
 *   ]});
 * }
 *
 * @param {Object} options
 * @param {Array<ItemInputData>} options.items List of ItemInputData. Consisting of unique slug of the item to change and the amount. The item must have been created in GB via ADM
 * @return {Promise}
 * @memberof module:economy
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const addItems = wrapConnectionCheck(async ({ items } = {}) => {
  validateItemInputDataArray(items);
  await connection.query(
    `
      mutation addItems($items: [ItemInputData!]!) {
          addItems(items: $items)
      }
  `,
    {
      items,
    },
  );
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-03.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * This function fetches all in GB configured currencies for the current game
 *
 * @example
 * import {getCurrencyCatalog} from '@sgorg/game-backend-client';
 *
 * async function fetchAndLogCurrencies() {
 *   const currencies = await getCurrencyCatalog();
 *   console.log(currencies);
 *   // [{
 *   //      id: '1',
 *   //      slug: 'currency1',
 *   //      displayName: 'Fancy Currency 1',
 *   //      metaData: {
 *   //        someConfigured: "dataForTheCurrency"
 *   //      },
 *   //      initialBalance: 0,
 *   //      rechargePerDay: 0,
 *   //      rechargeMax: 0,
 *   // }]
 *   return currencies;
 * }
 *
 * @returns {Promise<Array<Currency>>}
 * @memberof module:economy
 *
 * @throws {GBCErrorNoConnection}
 */
const getCurrencyCatalog = wrapConnectionCheck(async () => {
  const { data: { currencies = [] } = {} } = await connection.query(`
    query getCurrencies {
      currencies {
        id
        slug
        displayName
        metaData
        initialBalance
        rechargePerDay
        rechargeMax
      }
    }
  `);
  return currencies;
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-03.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * This function fetches all in GB configured items for this game
 *
 * @example
 * import {getItemCatalog} from '@sgorg/game-backend-client';
 *
 * async function fetchAndLogItems() {
 *   const items = await getItemCatalog();
 *   console.log(items);
 *   // [{
 *   //      id: '1',
 *   //      slug: 'item1',
 *   //      displayName: 'Fancy Item 1',
 *   //      metaData: {
 *   //        someConfigured: "dataForTheItem"
 *   //      }
 *   // }]
 *   return items;
 * }
 *
 * @returns {Promise<Array<Item>>}
 * @memberof module:economy
 *
 * @throws {GBCErrorNoConnection}
 */
const getItemCatalog = wrapConnectionCheck(async () => {
  const { data: { items = [] } = {} } = await connection.query(`
    query getItems {
      items {
        id
        slug
        displayName
        metaData
      }
    }
  `);
  return items;
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-03.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * With this function you can exchange one or multiple items for one or multiple currency or the other way around.
 * You should prefer this function. To adding and subtracting with addItems and addCurrency, since with this function you have one call and it is transaction save.
 * The input is an array of ItemInputData and an array of CurrencyInputData. See addCurrency and addItems.
 *
 * For TYPESCRIPT: We provide the ItemInputData interface and the CurrencyInputData interface.
 *
 * @example
 * import {transaction} from '@sgorg/game-backend-client';
 *
 * async function doFancyExchange() {
 *   return transaction({
 *     items: [
 *       {itemSlug: 'item1', amount: -1},
 *       {itemSlug: 'item2', amount: 1},
 *     ],
 *     currencies: [
 *       {itemSlug: 'currency1', amount: 1.23},
 *       {itemSlug: 'currency2, amount: -1},
 *     ],
 *   });
 * }
 *
 * @param {Object} options
 * @param {Array<ItemInputData>} options.items List of ItemInputData. Consisting of unique slug of the item to change and the amount. The item must have been created in GB via ADM
 * @param {Array<CurrencyInputData>} options.currencies List of CurrencyInputData. Consisting of unique slug of the currency to change and the amount. The CurrencyInputData must have been created in GB via ADM
 * @return {Promise<Boolean>}
 * @memberof module:economy
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const transaction = wrapConnectionCheck(
  async ({ items = [], currencies = [] } = {}) => {
    validateItemInputDataArray(items, true);
    validateCurrencyInputDataArray(currencies, true);
    if (!items.length && !currencies.length) {
      throw new GBCErrorValidationFailed(
        'For executing a transaction at least on of items or currencies must contain one value',
      );
    }
    const { data: { transaction: result } = {} } = await connection.query(
      `
      mutation transaction($items: [ItemInputData!], $currencies: [CurrencyInputData!]) {
          transaction(items: $items, currencies: $currencies)
      }
  `,
      {
        items,
        currencies,
      },
    );
    return result;
  },
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-03.
 * Copyright © Softgames 2019 - present
 */

var economy = /*#__PURE__*/Object.freeze({
  __proto__: null,
  addCurrencies: addCurrencies,
  addItems: addItems,
  getCurrencyCatalog: getCurrencyCatalog,
  getItemCatalog: getItemCatalog,
  transaction: transaction
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-10.
 * Copyright © Softgames 2019 - present
 */

function exposeEvent(event) {
  return {
    on: event.on.bind(event),
    once: event.once.bind(event),
  };
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-09.
 * Copyright © Softgames 2019 - present
 */

/**
 * @typedef {function} CloseEventListener
 * @param {Number} [code] is optional the closing code (default value 1000). https://tools.ietf.org/html/rfc6455#section-7.4.1
 * @param {String} [reason] is the optional reason that the socket is being closed. https://tools.ietf.org/html/rfc6455#section-7.1.6
 *
 * @memberof module:events
 */

/**
 * @constant closeEvent
 *
 * @description
 * Fired when the connection to the game backend is closed
 *
 * @example
 * import {closeEvent} from '@sgorg/game-backend-client';
 *
 * closeEvent.on((code, reason) => {
 *   console.log('Connection closed ', code, reason);
 * });
 *
 * @type {Event.<CloseEventListener>}
 * @memberof module:events
 */
const closeEvent = exposeEvent(onClose);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-09.
 * Copyright © Softgames 2019 - present
 */

/**
 * @typedef {function} ErrorEventListener
 * @param {Error} error
 *
 * @memberof module:events
 */

/**
 * @description
 * This event will be trigger when an error happens with the GB connection.
 *
 * @constant errorEvent
 * @example
 * import {errorEvent} from '@sgorg/game-backend-client';
 *
 * errorEvent.on(error => {
 *     console.log('The following error just happend', error);
 * });
 *
 * @type {Event.<ErrorEventListener>}
 * @memberof module:events
 */
const errorEvent = exposeEvent(onError);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-09.
 * Copyright © Softgames 2019 - present
 */

/**
 * @description
 * This event will be trigger when the login to the game-backend finished successfully.
 *
 * @constant loginEvent
 * @example
 * import {loginEvent} from '@sgorg/game-backend-client';
 *
 * loginEvent.on(() => {
 *     console.log('A login to GB just happened.');
 * });
 *
 * @type {Event.<function>}
 * @memberof module:events
 */
const loginEvent = exposeEvent(onLogin);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-09.
 * Copyright © Softgames 2019 - present
 */

/**
 * @typedef {function} MessageEventListener
 * @param {Message} message message object
 *
 * @memberof module:events
 */

/**
 * @description
 * Fired when a message is received from the GB.
 *
 * @constant messageEvent
 * @example
 * import {messageEvent} from '@sgorg/game-backend-client';
 *
 * messageEvent.on(message => {
 *   console.log('Message received', message);
 * });
 *
 * @type {Event.<MessageEventListener>}
 * @memberof module:events
 */
const messageEvent = exposeEvent(onMessage);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

var events = /*#__PURE__*/Object.freeze({
  __proto__: null,
  closeEvent: closeEvent,
  errorEvent: errorEvent,
  loginEvent: loginEvent,
  messageEvent: messageEvent
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function to fetch the all entries of the specified leaderbaord instance for you and all of your friends.
 * PLEASE NOTE: This is based on the friends you synchronized via "syncFriendListByPlatformPlayerId".
 *
 * @example
 * import {getFriendsScores} from '@sgorg/game-backend-client';
 *
 * async function fetchAndLogScore() {
 *   const friendsEntries = await getFriendsScores({instanceId:'default',leaderboardName:'default'});
 *   console.log(friendScores);
 *   // [{rank: 1, score: 123, user: {id: 'abc', handle: 'Max', isCurrentUser: true, playerId: '1ea12'}}]
 *   return friendScores;
 * }
 *
 * // This data has to be decorated in most cases with an avatarUrl and a refreshed handle
 * // hear is an example for Facebook
 *
 * async function fetchAndLogScore() {
 *   const [friends, leaderboardEntries] = await Promise.all([
 *      FBInstant.player.getConnectedPlayersAsync(),
 *      getFriendsScores({instanceId:'default',leaderboardName:'default'}),
 *   ]);
 *   const usersByPlayerId = [
 *     castPlayerToUser(FBInstant.player),
 *     ...friends.map(castPlayerToUser),
 *   ].reduce((m, user) => {
 *       return {
 *           ...m,
 *           [user.id]: user,
 *       };
 *   }. {});
 *   const decoratedResults = leaderboardEntries.map(entry => {
 *       const {user: entryUser} = entry;
 *       const {playerId, handle} = entryUser;
 *       const {name, avatarUrl} = usersByPlayerId[playerId] || {};
 *       return {
 *           ...entry,
 *           user: {
 *               ...entryUser,
 *               handle: name || handle || 'N/A',
 *               avatarUrl,
 *           },
 *       };
 *   });
 *   console.log(decoratedResults);
 *   // [{
 *   //   rank: 1,
 *   //   score: 123,
 *   //   user: {
 *   //     id: 'abc',
 *   //     handle: 'Max',
 *   //     avatarUrl: 'http://www.fb.de/image.jpg',
 *   //     isCurrentUser: true,
 *   //     playerId: '1ea12',
 *   //   }
 *   // }]
 *   return decoratedResults;
 * }
 *
 * function castPlayerToUser(player) {
 *   return {
 *     id: player.getID(),
 *     name: player.getName(),
 *     avatarUrl: player.getPhoto(),
 *   };
 * }
 *
 * @param {Object} options
 * @param {String} options.instanceId Specify which instance of given leaderboard you want to get scores from.
 * @param {String} options.leaderboardName Specify the leaderboard
 * @returns {Promise<Array<LeaderboardEntry>>}
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const getFriendsScores = wrapConnectionCheck(
  async ({ instanceId, leaderboardName } = {}) => {
    if (!isString$2(leaderboardName) || !leaderboardName.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "leaderboardName" parameter. "leaderboardName" should be not empty string',
        'main-leaderboard',
      );
    }
    if (!isString$2(instanceId) || !instanceId.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "instanceId" parameter. "instanceId" should be not empty string',
        'default',
      );
    }

    const { data: { leaderboard: { friendsEntries } = {} } = {} } =
      await connection.query(
        `
    query friendScores($leaderboardName: ID!, $instanceId: ID!){
      leaderboard(name: $leaderboardName) {
        friendsEntries(instanceId: $instanceId) {
          score
          rank
          user {
            id
            handle
            isCurrentUser
            playerId
          }
        }
      }
    }
  `,
        {
          leaderboardName,
          instanceId,
        },
      );

    return friendsEntries;
  },
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @typedef {Object} LeaderboardMetaData
 * @property {string} resetSchedule
 * @property {('BEST'|'SUM'|'SET')} aggregationMethod
 * @property {number} minScore
 * @property {number} maxScore
 * @property {boolean} ascending
 *
 * @memberof module:leaderboard
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function if you want to fetch the configuration of a specific leaderboard.
 *
 * @example
 * import { getLeaderboardMetaData } from '@sgorg/game-backend-client';
 * async fetchAndLogLeaderboard() {
 *   const leaderboardMeta = await getLeaderboardMetaData({leaderboardName:'default'});
 *   console.log(leaderboardMeta);
 *   // {
 *   //   resetSchedule: '0 0 1 * mon',
 *   //   aggregationMethod: 'BEST',
 *   //   minScore: 0,
 *   //   maxScore: 1000000,
 *   //   ascending: false
 *   // }
 *   return leaderboardMeta;
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName Specify the leaderboard
 * @returns {Promise<LeaderboardMetaData>} leader board object
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const getLeaderboardMetaData = wrapConnectionCheck(
  async ({ leaderboardName } = {}) => {
    if (!isString$2(leaderboardName) || !leaderboardName.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "leaderboardName" parameter. "leaderboardName" should be not empty string',
        'main-leaderboard',
      );
    }
    const { data: { leaderboard } = {} } = await connection.query(
      `
    query friendScores($leaderboardName: ID!){
      leaderboard(name: $leaderboardName) {
        resetSchedule
        aggregationMethod
        minScore
        maxScore
        ascending
      }
    }
  `,
      {
        leaderboardName,
      },
    );

    // TODO: fix after proper error from gb-game-server
    if (!leaderboard) throw new Error('No valid metadata result received');

    return leaderboard;
  },
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function to fetch the top scores of a leaderboard instance.
 *
 * @example
 * import { getTopScores } from '@sgorg/game-backend-client';
 * async function fetchAndLogTopScores() {
 *   const topScores = await getTopScores({leaderboardName:'default', instanceId:'default'});
 *   console.log(topScores);
 *   // [{rank: 1, score: 123, user : {id: 'abc', handle: 'Max', isCurrentUser: true, playerId: '1ea12'}}]
 *   return topScores;
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName  Specify the leaderboard.
 * @param {String} options.instanceId  Specify which instance of given leaderboard you want to get scores from.
 * @param {String} [options.limit] records limit
 * @param {String} [options.offset] offset
 * @returns {Promise<Array<LeaderboardEntry>>} top entries
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const getTopScores = wrapConnectionCheck(
  async ({ leaderboardName, instanceId, limit = 100, offset = 0 } = {}) => {
    if (!isString$2(leaderboardName) || !leaderboardName.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "leaderboardName" parameter. "leaderboardName" should be not empty string',
        'main-leaderboard',
      );
    }
    if (!isString$2(instanceId) || !instanceId.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "instanceId" parameter. "instanceId" should be not empty string',
        'default',
      );
    }

    const { data: { leaderboard: { topEntries } = {} } = {} } =
      await connection.query(
        `
    query friendScores($leaderboardName: ID!, $instanceId: ID!, $limit: Int!, $offset: Int!){
      leaderboard(name: $leaderboardName) {
        topEntries(instanceId: $instanceId, limit: $limit, offset: $offset) {
          score
          rank
          user {
            id
            handle
            isCurrentUser
            playerId
          }
        }
      }
    }
  `,
        {
          leaderboardName,
          limit,
          instanceId,
          offset,
        },
      );

    return topEntries;
  },
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function if you only want to get the current score of a specific user on a specific leaderboard instance
 *
 * @deprecated since version 4.3.0
 *
 * @example
 * import {getUsersEntry} from '@sgorg/game-backend-client';
 * async function fetchAndLogUsersEntry() {
 *   const user = await getUsersEntry({leaderboardName:'default',instanceId:'default',userId:'abcd123'});
 *   console.log(user);
 *   // { score: 17389, user: {id: 'abc', handle: 'Max', isCurrentUser: true, playerId: '1ea12'}}
 *   return user;
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName Specify the leaderboard.
 * @param {String} options.instanceId Specify which instance of given leaderboard you want to get scores from.
 * @param {String} options.userId user Id
 * @returns {Promise<UserWithScore>} users entry
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const getUsersEntry = wrapConnectionCheck(
  async ({ leaderboardName, instanceId, userId } = {}) => {
    if (!isString$2(leaderboardName) || !leaderboardName.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "leaderboardName" parameter. "leaderboardName" should be not empty string',
        'main-leaderboard',
      );
    }
    if (!isString$2(instanceId) || !instanceId.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "instanceId" parameter. "instanceId" should be not empty string',
        'default',
      );
    }
    if (!isString$2(userId) || !userId.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "userId" parameter. "userId" should be not empty string',
        '1687268',
      );
    }

    const { data: { leaderboard: { usersEntry } = {} } = {} } =
      await connection.query(
        `
    query getUsersEntry($leaderboardName: ID!, $instanceId: ID!, $userId: ID!) {
      leaderboard(name: $leaderboardName) {
        usersEntry(
          userId: $userId
          instanceId: $instanceId
        ){
          score
          user {
            id
            handle
            isCurrentUser
          }
        }
      }
    }
  `,
        {
          leaderboardName,
          instanceId,
          userId,
        },
      );

    return usersEntry;
  },
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function to fetch a leaderboard for a specific set of players from a specified leaderboard instance.
 * PLEASE NOTE: players is the list of playerIds not the GB user ids.
 *
 * @example
 * import {getUsersScores} from '@sgorg/game-backend-client';
 * async function fetchAndLogUsersScores() {
 *   const usersScore = await getUsersScores({leaderboardName:'default', instanceId: 'default', players: ['123kd2']});
 *   console.log(usersScore);
 *   // returns [{rank: 1, score: 123, user: {id: 'abc', handle: 'Max', isCurrentUser: true, playerId: '1ea12'}}]
 *   return usersScore;
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName Specify the leaderboard.
 * @param {String} options.instanceId Specify which instance of given leaderboard you want to get scores from.
 * @param {Array<String>} options.players players ids
 * @returns {Promise<Array<LeaderboardEntry>>}
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const getUsersScores = wrapConnectionCheck(
  async ({ leaderboardName, instanceId, players } = {}) => {
    if (!isString$2(leaderboardName) || !leaderboardName.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "leaderboardName" parameter. "leaderboardName" should be not empty string',
        'main-leaderboard',
      );
    }
    if (!isString$2(instanceId) || !instanceId.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "instanceId" parameter. "instanceId" should be not empty string',
        'default',
      );
    }
    if (!isArray(players)) {
      throw new GBCErrorValidationFailed(
        'Invalid "players" parameter. "players" should be array of users ids.',
        'default',
      );
    }

    const { data: { leaderboard: { usersEntries } = {} } = {} } =
      await connection.query(
        `
    query usersScores($leaderboardName: ID!, $instanceId: ID!, $players: [ID!]!){
      leaderboard(name: $leaderboardName) {
        usersEntries(instanceId: $instanceId, playerIds: $players) {
          score
          rank
          user {
            id
            handle
            isCurrentUser
            playerId
          }
        }
      }
    }
  `,
        {
          leaderboardName,
          instanceId,
          players,
        },
      );

    // TODO: fix after proper error from gb-game-server
    if (!usersEntries)
      throw new Error('No valid getUsersScores result received');

    return usersEntries;
  },
);

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2020-11-27.
 * Copyright © Softgames 2020 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function if you only want to get the current score of a specific user on a specific leaderboard instance
 *
 * @example
 * import {getLeaderboardUserEntry} from '@sgorg/game-backend-client';
 * async function fetchAndLogUsersEntry() {
 *   const user = await getLeaderboardUserEntry({leaderboardName:'default',instanceId:'default',userId:'abcd123'});
 *   console.log(user);
 *   // { score: 17389, user: {id: 'abc', handle: 'Max', isCurrentUser: true, playerId: '1ea12'}}
 *   return user;
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName Specify the leaderboard.
 * @param {String} options.instanceId Specify which instance of given leaderboard you want to get scores from.
 * @param {String} options.userId user Id
 * @returns {Promise<UserWithScore>} users entry
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const getLeaderboardUserEntry = wrapConnectionCheck(
  async ({ leaderboardName, instanceId, userId } = {}) => {
    if (!isString$2(leaderboardName) || !leaderboardName.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "leaderboardName" parameter. "leaderboardName" should be not empty string',
        'main-leaderboard',
      );
    }
    if (!isString$2(instanceId) || !instanceId.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "instanceId" parameter. "instanceId" should be not empty string',
        'default',
      );
    }
    if (!isString$2(userId) || !userId.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "userId" parameter. "userId" should be not empty string',
        '1687268',
      );
    }

    const { data: { leaderboard: { usersEntry } = {} } = {} } =
      await connection.query(
        `
    query getLeaderboardUserEntry($leaderboardName: ID!, $instanceId: ID!, $userId: ID!) {
      leaderboard(name: $leaderboardName) {
        usersEntry(
          userId: $userId
          instanceId: $instanceId
        ){
          score
          user {
            id
            handle
            isCurrentUser
          }
        }
      }
    }
  `,
        {
          leaderboardName,
          instanceId,
          userId,
        },
      );

    return usersEntry;
  },
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function to send the score of the current user to a specific leaderboard instance.
 *
 * @example
 * import {postScore} from '@sgorg/game-backend-client';
 * async function updateAndLogScore(){
 *   await postScore({leaderboardName: 'default', instanceId: 'default', score: 231});
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName Specify the leaderboard
 * @param {String} options.instanceId specify which instance of given leaderboard you want to get scores from.
 * @param {Number} options.score score to update
 * @return {Promise}
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const postScore = wrapConnectionCheck(
  async ({ leaderboardName, instanceId, score } = {}) => {
    if (!isString$2(leaderboardName) || !leaderboardName.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "leaderboardName" parameter. "leaderboardName" should be not empty string',
        'main-leaderboard',
      );
    }
    if (!isString$2(instanceId) || !instanceId.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "instanceId" parameter. "instanceId" should be not empty string',
        'default',
      );
    }
    if (!isNumber$1(score)) {
      throw new GBCErrorValidationFailed(
        'Invalid "score" parameter. "score" should be number',
        'default',
      );
    }
    await connection.query(
      `
    mutation postScore($leaderboardName: ID!, $instanceId: ID!, $score: Float!){
      postScore(leaderboardName: $leaderboardName, instanceId: $instanceId, score: $score)
    }
  `,
      {
        leaderboardName,
        instanceId,
        score,
      },
    );
  },
);

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2020-11-26.
 * Copyright © Softgames 2020 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function to remove score from all instances for a specific leaderboard.
 *
 * @example
 * import {removeLeaderboardScores} from '@sgorg/game-backend-client';
 * async function cleanupLeaderboardScores(){
 *   await removeLeaderboardScores({leaderboardName: 'default'});
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName Specify the leaderboard
 * @return {Promise}
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const removeLeaderboardScores = wrapConnectionCheck(
  async ({ leaderboardName } = {}) => {
    if (!isString$2(leaderboardName) || !leaderboardName.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "leaderboardName" parameter. "leaderboardName" should be not empty string',
        'main-leaderboard',
      );
    }

    await connection.query(
      `
        mutation removeScore($leaderboardName: ID!){
          removeScore(leaderboardName: $leaderboardName)
        }
      `,
      {
        leaderboardName,
      },
    );
  },
);

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2020-11-26.
 * Copyright © Softgames 2020 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function to remove score from a specific leaderboard instance.
 *
 * @example
 * import {removeLeaderboardInstanceScore} from '@sgorg/game-backend-client';
 * async function cleanupLeaderboardInstanceScore(){
 *   await removeLeaderboardInstanceScore({leaderboardName: 'default', instanceId: 'default'});
 * }
 *
 * @param {Object} options
 * @param {String} options.leaderboardName Specify the leaderboard
 * @param {String} options.instanceId Specify which instance of given leaderboard you want to remove score from.
 * @return {Promise}
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const removeLeaderboardInstanceScore = wrapConnectionCheck(
  async ({ leaderboardName, instanceId } = {}) => {
    if (!isString$2(leaderboardName) || !leaderboardName.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "leaderboardName" parameter. "leaderboardName" should be not empty string',
        'main-leaderboard',
      );
    }

    if (!isString$2(instanceId) || !instanceId.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "instanceId" parameter. "instanceId" should be not empty string',
        'default',
      );
    }

    await connection.query(
      `
        mutation removeScore($leaderboardName: ID!, $instanceId: ID){
          removeScore(leaderboardName: $leaderboardName, instanceId: $instanceId)
        }
      `,
      {
        leaderboardName,
        instanceId,
      },
    );
  },
);

// shim for using process in browser

// from https://github.com/kumavis/browser-process-hrtime/blob/master/index.js
var performance = global.performance || {};
performance.now        ||
  performance.mozNow     ||
  performance.msNow      ||
  performance.oNow       ||
  performance.webkitNow  ||
  function(){ return (new Date()).getTime() };

// Copyright Joyent, Inc. and other Node contributors.

function isBoolean(arg) {
  return typeof arg === 'boolean';
}

function isNumber(arg) {
  return typeof arg === 'number';
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-05-29.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function to subscribe or un-subscribe to leaderboards.
 * Per default all users are subscribed to leaderboards.
 * If a user un-subscribes to leaderboards all of his entries are deleted and to not show in the leaderboards anymore.
 * If a users re-subscribes he has to start over with new scores.
 *
 * @example
 * import { subscribeToLeaderboards } from '@sgorg/game-backend-client';
 *
 * async function unSubscribeToLeaderboards() {
 *   return subscribeToLeaderboards({subscribe: false});
 * }
 *
 * async function reSubscribeToLeaderboards() {
 *   return subscribeToLeaderboards({subscribe: true});
 * }
 *
 * @param {Object} options
 * @param {Boolean} options.subscribe Whether or not the user is subscribing (true) or un-subscribing (false)
 * @returns {Promise}
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const subscribeToLeaderboards = wrapConnectionCheck(
  async ({ subscribe } = {}) => {
    if (!isBoolean(subscribe)) {
      throw new GBCErrorValidationFailed(
        'Invalid "subscribe" parameter. "subscribe" must be a boolean',
        true,
      );
    }
    await connection.query(
      `
    mutation subscribeToLeaderboards($subscribe: Boolean!){
      subscribeToLeaderboards(subscribe: $subscribe)
    }
  `,
      {
        subscribe,
      },
    );
  },
);

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = _freeGlobal || freeSelf || Function('return this')();

var _root = root;

/** Built-in value references. */
var Symbol = _root.Symbol;

var _Symbol = Symbol;

/** Used for built-in method references. */
var objectProto$2 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$2.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$2.toString;

/** Built-in value references. */
var symToStringTag$1 = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty$1.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$1.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }
  return result;
}

var _getRawTag = getRawTag;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString.call(value);
}

var _objectToString = objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag && symToStringTag in Object(value))
    ? _getRawTag(value)
    : _objectToString(value);
}

var _baseGetTag = baseGetTag;

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

var _overArg = overArg;

/** Built-in value references. */
var getPrototype = _overArg(Object.getPrototypeOf, Object);

var _getPrototype = getPrototype;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike;

/** `Object#toString` result references. */
var objectTag = '[object Object]';

/** Used for built-in method references. */
var funcProto = Function.prototype,
    objectProto = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/** Used to infer the `Object` constructor. */
var objectCtorString = funcToString.call(Object);

/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @static
 * @memberOf _
 * @since 0.8.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 * }
 *
 * _.isPlainObject(new Foo);
 * // => false
 *
 * _.isPlainObject([1, 2, 3]);
 * // => false
 *
 * _.isPlainObject({ 'x': 0, 'y': 0 });
 * // => true
 *
 * _.isPlainObject(Object.create(null));
 * // => true
 */
function isPlainObject(value) {
  if (!isObjectLike_1(value) || _baseGetTag(value) != objectTag) {
    return false;
  }
  var proto = _getPrototype(value);
  if (proto === null) {
    return true;
  }
  var Ctor = hasOwnProperty.call(proto, 'constructor') && proto.constructor;
  return typeof Ctor == 'function' && Ctor instanceof Ctor &&
    funcToString.call(Ctor) == objectCtorString;
}

var isPlainObject_1 = isPlainObject;

function isValidPlainObject(obj, validator) {
  if (!isFunction(validator)) {
    throw new Error('Provided validator must be a function');
  }
  if (!obj) {
    return false;
  }
  if (!isPlainObject_1(obj)) {
    return false;
  }
  return Object.values(obj).findIndex((v) => !validator(v)) === -1;
}

function isValidArray(arr, validator) {
  if (!isFunction(validator)) {
    throw new Error('Provided validator must be a function');
  }
  if (!isArray(arr)) {
    return false;
  }
  return arr.findIndex((v) => !validator(v)) === -1;
}

/**
 * @async
 * @function
 *
 * @description
 * This function fetches the publicly available user data for all given players and keys.
 *
 * In the Game Backends Game titleData must be configured which keys should be publicly available
 *
 * @example
 * import {getPublicUserData} from '@sgorg/game-backend-client';
 * async function fetchPublicUserData() {
 *   const dataByPlayerIds = await getPublicUserData({
 *     playerIds: ['123', '456'],
 *     keys: ['userDataA', 'blob.propertyA'],
 *   });
 *   console.log(dataByPlayerIds);
 *   // {
 *   //   '123': {
 *   //     'userDataA': 'someValue',
 *   //     'blob.propertyA': 69,
 *   //   },
 *   //   '456': {
 *   //     'userDataA': 'someOtherValue',
 *   //     'blob.propertyA': 96,
 *   //   },
 *   // }
 *   return userData;
 * }
 *
 * @param {Object} options
 * @param {Array<String>} options.playerIds List of playerIds you want to get the public data off
 * @param {Array<String>} options.keys List of user data keys. For data of plain objects paths are allowed key.propertyName.subPropertyName
 * @returns {Promise<Object>}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 */
const getPublicUserData = wrapConnectionCheck(async ({ playerIds, keys }) => {
  if (!isValidArray(playerIds, isNonEmptyString$3)) {
    throw new GBCErrorValidationFailed(
      'Invalid "playerIds" parameter. "playerIds" should be an array of non-empty strings',
      "['45544','21312']",
    );
  }
  if (!isValidArray(keys, isNonEmptyString$3)) {
    throw new GBCErrorValidationFailed(
      'Invalid "keys" parameter. "keys" should be an array of non-empty strings',
      "['dataKeyA','dataKeyB']",
    );
  }
  const { data: { publicUserData } = {} } = await connection.query(
    `
    query publicUserData($playerIds: [ID!]!, $keys: [String!]!) {
      publicUserData(playerIds: $playerIds, keys: $keys) {
        playerId
        data
      }
    }
  `,
    { playerIds, keys },
  );
  return publicUserData.reduce(
    (m, { playerId, data }) => ({ ...m, [playerId]: data }),
    {},
  );
});

/**
 * @async
 * @function
 *
 * @description
 * Use this function to create a post processor for the Leaderboard Client
 * to extend each leaderboard entry by data of public user data
 *
 * @example
 * import { getLeaderboardPostProcessorForUserData } from '@sgorg/game-backend-client';
 * import { createLeaderboardClient } from '@sgorg/leaderboard-client';
 *
 * // This example is based under following assumptions
 * // a) There is a public user data key called "user"
 * // b) It contains an object with the properties
 * // b1) name
 * // b2) avatarUrl
 * // c) It has the values for userId 1  { name: 'Max', avatarUrl: 'https://s3/1.jpg' }
 * // d) It has the values for userId 2  { name: 'Maxi', avatarUrl: 'https://s3/2.jpg' }
 *
 * const postProcessor = getLeaderboardPostProcessorForUserData({
 *   userDataMapping: {
 *     username: 'user.name'
 *     userAvatarUrl: 'user.avatarUrl'
 *   }
 * });
 * const ldbClient = createLeaderboardClient({
 *   gameId: 'gb-test-game',
 *   postProcessor,
 * });
 *
 * async function getLeaderboardEntriesByInstanceId({ currentPlayerId, leaderboardInstanceId }) {
 *   const entries = await ldbClient.getEntries({
 *     userId: currentPlayerId,
 *     leaderboardInstanceId,
 *   });
 *   console.log(entries);
 *   // [{
 *   //   rank: 1,
 *   //   score: 123,
 *   //   userId: '1',
 *   //   username: 'Max',
 *   //   userAvatarUrl: 'https://s3/1.jpg',
 *   // },{
 *   //   rank: 2,
 *   //   score: 122,
 *   //   userId: '2',
 *   //   username: 'Maxi',
 *   //   userAvatarUrl: 'https://s3/2.jpg',
 *   // }]
 *   return entries;
 * }
 *
 * @param {Object} options
 * @param {Object<string, string>} options.userDataMapping - Mapping key = key in every entry / value = key + path to data in public user data (see getPublicUserData)
 * @returns {function(Array<Object>): Promise<Array<Object>>}
 * @memberof module:leaderboard
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const getLeaderboardPostProcessorForUserData = ({ userDataMapping }) => {
  if (!isValidPlainObject(userDataMapping, isNonEmptyString$3)) {
    throw new GBCErrorValidationFailed(
      'Invalid "userDataMapping" parameter. "userDataMapping" should be a plain-object where key and values must be a string.',
      `{username: 'user.name', avatar: 'user.avatarUrl'}`,
    );
  }
  const keys = Object.values(userDataMapping);
  const mappingEntries = Object.entries(userDataMapping);
  return wrapConnectionCheck(async (entries) => {
    try {
      const playerIds = entries.map(({ userId }) => userId);
      const data = await getPublicUserData({
        playerIds,
        keys,
      });
      return entries.map(({ userId, score, rank }) => {
        const userData = data[userId] || {};
        return {
          userId,
          score,
          rank,
          ...mappingEntries.reduce((m, [entryKey, dataKey]) => {
            m[entryKey] = userData[dataKey]; // eslint-disable-line no-param-reassign
            return m;
          }, {}),
        };
      });
    } catch (error) {
      throw new GBCUnknownError(
        'Error in GB client generated leaderboard post processor',
        error,
      );
    }
  });
};

var leaderboard = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getFriendsScores: getFriendsScores,
  getLeaderboardMetaData: getLeaderboardMetaData,
  getTopScores: getTopScores,
  getUsersEntry: getUsersEntry,
  getUsersScores: getUsersScores,
  getLeaderboardUserEntry: getLeaderboardUserEntry,
  postScore: postScore,
  removeLeaderboardScores: removeLeaderboardScores,
  removeLeaderboardInstanceScore: removeLeaderboardInstanceScore,
  subscribeToLeaderboards: subscribeToLeaderboards,
  getLeaderboardPostProcessorForUserData: getLeaderboardPostProcessorForUserData
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Confirms that all messages are received and processed and can be deleted from the GB.
 *
 * @example
 * import {acknowledgeAllMessages} from '@sgorg/game-backend-client';
 * async function acknowledgeAllMessagesAndLogResult(){
 *   await acknowledgeAllMessages();
 * }
 *
 * @return {Promise}
 * @memberof module:messaging
 *
 * @throws {GBCErrorNoConnection}
 */
const acknowledgeAllMessages = wrapConnectionCheck(async () => {
  await connection.query(`
    mutation acknowledgeAllMessages {
      acknowledgeAllPushMessages
    }
  `);
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Conform that a specific message is received and processed and can be deleted from the GB.
 *
 * @example
 * import {acknowledgeMessage} from '@sgorg/game-backend-client';
 *
 * async function acknowledgeMessageAndLog() {
 *   await acknowledgeMessage({messageId:'someId2321'});
 * }
 *
 * @param {Object} options
 * @param {String} options.messageId message Id
 * @return {Promise}
 * @memberof module:messaging
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const acknowledgeMessage = wrapConnectionCheck(async ({ messageId } = {}) => {
  if (!isString$2(messageId) || !messageId.trim()) {
    throw new GBCErrorValidationFailed(
      'Invalid "messageId" parameter. "messageId" should be not empty string',
      '45544',
    );
  }
  await connection.query(
    `
    mutation ackMessage($messageId: ID!){
      acknowledgePushMessage(messageId: $messageId)
    }
  `,
    {
      messageId,
    },
  );
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Fetches all messages for the current user from the GB which are not confirmed yet.
 *
 * @example
 * import {fetchMessages} from '@sgorg/game-backend-client';
 * async function fetchAndLogMessages() {
 *   const messages = await fetchMessages();
 *   // [{id: '12abd32m', recipientUserId: '83jd83', senderUserId: '08sdj7d7', payload: {}}]
 *   return messages;
 * }
 *
 * @returns {Promise<Array<Message>>}
 * @memberof module:messaging
 *
 * @throws {GBCErrorNoConnection}
 */
const fetchMessages = wrapConnectionCheck(async () => {
  const { data: { me: { messages = [] } = {} } = {} } = await connection.query(`
    query fetchMessages {
      me {
        messages {
          id
          recipientUserId
          senderUserId
          payload
        }
      }
    }
  `);
  return messages;
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Sends a message to a specific user
 * The GB userId is required here, not the platform player id.
 * On success the message is stored in the user's mailbox until he acknowledges the message.
 * When in the response sendToBroadcast = true then the message was also send via our messaging system (based on AMQP)
 * directly to the user if the user was online at the time.
 * This is no indicator that the user received the message just that there was the chance if he was online.
 *
 * @example
 * import {sendMessage} from '@sgorg/game-backend-client';
 * async function sendMessageAndLog() {
 *   await sendMessage({userId: 'ca1x2d', payload: {}})
 *     .then(({sendToMailbox, sendToBroadcast})=> {
 *        if (sendToMailbox) {
 *            console.log(`Message was stored in the mailbox of the receiving user`);
 *        }
 *        if (sendToBroadcast) {
 *            console.log(`Message was broadcast, if the user was online at the time he got the message straight away`);
 *        }
 *     })
 *     .catch(e=>console.error('failed'));
 * }
 *
 * @param {Object} options
 * @param {Object} options.payload payload
 * @param {String} options.userId user Id
 * @return {Promise<{sendToMailbox: Boolean, sendToBroadcast: Boolean}>}
 * @memberof module:messaging
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const sendMessage = wrapConnectionCheck(async ({ userId, payload } = {}) => {
  if (!isString$2(userId) || !userId.trim()) {
    throw new GBCErrorValidationFailed(
      'Invalid "userId" parameter. "userId" should be not empty string',
      '121290845544',
    );
  }
  if (!isPlainObject_1(payload)) {
    throw new GBCErrorValidationFailed(
      'Invalid "payload" parameter. "payload" should be Object',
      '{"title": "Short title"}}',
    );
  }

  const {
    data: {
      sendPushMessage: { sendToMailbox = true, sendToBroadcast = true } = {},
    } = {},
  } = await connection.query(
    `
    mutation sendMessage($userId: ID!, $payload: JSON!) {
      sendPushMessage(userId: $userId, payload: $payload)
    }
  `,
    {
      userId,
      payload,
    },
  );
  return {
    sendToMailbox,
    sendToBroadcast,
  };
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

var messaging = /*#__PURE__*/Object.freeze({
  __proto__: null,
  acknowledgeAllMessages: acknowledgeAllMessages,
  acknowledgeMessage: acknowledgeMessage,
  fetchMessages: fetchMessages,
  sendMessage: sendMessage
});

/**
 * @async
 * @function
 *
 * @description
 * Fetches all notifications for the current user from the GB which are not acknowledged yet.
 *
 * @example
 * import { fetchNotifications } from '@sgorg/game-backend-client';
 * async function fetchAndLogNotifications() {
 *   const messages = await fetchNotifications();
 *   // [{id: '12abd32m', payload: {}}]
 *   return messages;
 * }
 *
 * @returns {Promise<Array<Notification>>}
 * @memberof module:notification
 *
 * @throws {GBCErrorNoConnection}
 */
const fetchNotifications = wrapConnectionCheck(async () => {
  const { data: { me: { notifications = [] } = {} } = {} } =
    await connection.query(`
    query fetchNotifications {
      me {
        notifications {
          id
          payload
        }
      }
    }
  `);
  return notifications;
});

/**
 * @async
 * @function
 *
 * @description
 * Acknowledge that a specific notification is received and processed.
 *
 * @example
 * import {acknowledgeNotifications} from '@sgorg/game-backend-client';
 *
 * async function acknowledgeNotificationAndLog() {
 *   await acknowledgeNotifications({notificationIds:['someId2321']});
 * }
 *
 * @param {Object} options
 * @param {Array<String>} options.notificationIds list of notification Id
 * @return {Promise}
 * @memberof module:notification
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const acknowledgeNotifications = wrapConnectionCheck(
  async ({ notificationIds } = {}) => {
    if (!isValidArray(notificationIds, isString$2)) {
      throw new GBCErrorValidationFailed(
        'Invalid "notificationIds" parameter. "notificationIds" should be an array of notificationId',
        "['45544','21312']",
      );
    }
    await connection.query(
      `
      mutation acknowledgeNotifications($notificationIds: [ID!]){
        acknowledgeNotifications(notificationIds: $notificationIds)
      }
    `,
      {
        notificationIds,
      },
    );
  },
);

var notification = /*#__PURE__*/Object.freeze({
  __proto__: null,
  fetchNotifications: fetchNotifications,
  acknowledgeNotifications: acknowledgeNotifications
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2020-02-03.
 * Copyright © Softgames 2020 - present
 */

async function handleCurrencyPurchase$1({
  currencySlug,
  amount,
  providerSpecificData,
}) {
  await connection.query(
    `
      mutation purchaseCurrency($currencySlug: ID!, $amount: Float!, $providerSpecificData: JSON) {
          purchaseCurrency(currencySlug: $currencySlug, amount: $amount, providerSpecificData: $providerSpecificData)
      }
  `,
    {
      currencySlug,
      amount,
      providerSpecificData,
    },
  );
}

const OPENSOCIAL_ERROR_CODE = {
  APP_LOGIC_ERROR: 'appLogicError',
  APP_NETWORK_FAILURE: 'appNetworkFailure',
  INSUFFICIENT_MONEY: 'insufficientMoney',
  INVALID_TOKEN: 'invalidToken',
  MALFORMED_REQUEST: 'malformedRequest',
  NOT_IMPLEMENTED: 'notImplemented',
  OK: 'ok',
  PAYMENT_ERROR: 'paymentError',
  PAYMENT_PROCESSOR_ALREADY_OPENED: 'paymentProcessorAlreadyOpened',
  UNKNOWN_ERROR: 'unknownError',
  USER_CANCELLED: 'userCancelled',
  BAD_REQUEST: 'badRequest',
};

/**
 * Created by René Simon <rene.simon@softgames.de> on 2020-02-11.
 * Copyright © Softgames 2019 - present
 */

const {
  APP_LOGIC_ERROR,
  APP_NETWORK_FAILURE,
  INSUFFICIENT_MONEY,
  INVALID_TOKEN,
  MALFORMED_REQUEST,
  NOT_IMPLEMENTED,
  OK,
  PAYMENT_ERROR,
  PAYMENT_PROCESSOR_ALREADY_OPENED,
  UNKNOWN_ERROR,
  USER_CANCELLED,
  BAD_REQUEST,
} = OPENSOCIAL_ERROR_CODE;

function isMobageOpenSocial() {
  return !!window.opensocial && window.mbga;
}

async function executePayment({ skuId, name, price, imageUrl, amount }) {
  try {
    const { opensocial, mbga } = window;
    const {
      BillingItem: { Field: { SKU_ID, PRICE, COUNT } = {} } = {},
      Payment: { Field: { ITEMS, AMOUNT, ORDER_ID } = {} } = {},
      newBillingItem,
      requestPayment,
      newPayment,
    } = opensocial || {};
    const createNewBillingItem = newBillingItem.bind(opensocial);
    const createNewPayment = newPayment.bind(opensocial);
    const executePaymentRequest = requestPayment.bind(opensocial);
    const { BillingItem: { Field: { NAME, IMAGE_URL } = {} } = {} } =
      mbga || {};

    // [RS] We await here so we can catch the error to wrap it
    return await new Promise((resolve, reject) => {
      try {
        const item = createNewBillingItem({
          [SKU_ID]: skuId,
          [PRICE]: price,
          [COUNT]: amount,
          [NAME]: name,
          [IMAGE_URL]: imageUrl,
        });
        const payment = createNewPayment({
          [ITEMS]: [item],
          [AMOUNT]: price * amount,
        });
        executePaymentRequest(payment, (response) => {
          try {
            if (!response) {
              throw new Error(
                `No response provided from opensocial.requestPayment`,
              );
            }
            if (response.hadError()) {
              let errorCode = response.getErrorCode();
              // eslint-disable-next-line no-underscore-dangle
              if (errorCode === BAD_REQUEST && response.data_) {
                // eslint-disable-next-line no-underscore-dangle
                errorCode = response.data_.getField('responseCode');
              }

              throw mapError(errorCode);
            }

            const paymentResult = response.getData();
            const orderId = paymentResult.getField(ORDER_ID);
            if (!orderId) {
              throw new GBCPaymentError(
                ERROR_CODE.PURCHASE_UNKNOWN_ERROR,
                `Invalid orderId return by mobage opensocial api`,
              );
            }
            resolve({ orderId });
          } catch (error) {
            reject(error);
          }
        });
      } catch (error) {
        reject(error);
      }
    });
  } catch (error) {
    throw createError(error);
  }
}

function mapError(code) {
  switch (code) {
    case INSUFFICIENT_MONEY:
      return new GBCPaymentError(
        ERROR_CODE.PURCHASE_PROVIDER_INSUFFICIENT_BALANCE,
        `User does not have enough money according to mobage opensocial api`,
      );
    case USER_CANCELLED:
      return new GBCPaymentError(
        ERROR_CODE.PURCHASE_PROVIDER_CANCELED,
        `User canceled the purchase according to mobage opensocial api`,
      );
    case OK:
      return new GBCPaymentError(
        ERROR_CODE.PURCHASE_UNKNOWN_ERROR,
        `According to mobage opensocial api everything is fine but we got the error anyway`,
      );
    case APP_LOGIC_ERROR:
    case APP_NETWORK_FAILURE:
    case INVALID_TOKEN:
    case MALFORMED_REQUEST:
    case NOT_IMPLEMENTED:
    case PAYMENT_ERROR:
    case PAYMENT_PROCESSOR_ALREADY_OPENED:
    case UNKNOWN_ERROR:
      return new GBCPaymentError(
        ERROR_CODE.PURCHASE_UNKNOWN_ERROR,
        `Received error code ${code} from mobage opensocial api`,
      );
    default:
      return new GBCPaymentError(
        ERROR_CODE.PURCHASE_UNKNOWN_ERROR,
        `Received unknown error code ${code} from mobage opensocial api`,
      );
  }
}

function createError(error) {
  if (!error) {
    return new GBCPaymentError(
      ERROR_CODE.PURCHASE_UNKNOWN_ERROR,
      `Invalid error while processing mobage opensocial payment`,
    );
  }
  if (error instanceof GBCPaymentError) {
    return error;
  }
  return new GBCPaymentError(
    ERROR_CODE.PURCHASE_UNKNOWN_ERROR,
    `Unknown error while processing mobage opensocial payment: ${
      error.message || error
    }`,
    error,
  );
}

/** Used to match a single whitespace character. */
var reWhitespace = /\s/;

/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
 * character of `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the index of the last non-whitespace character.
 */
function trimmedEndIndex(string) {
  var index = string.length;

  while (index-- && reWhitespace.test(string.charAt(index))) {}
  return index;
}

var _trimmedEndIndex = trimmedEndIndex;

/** Used to match leading whitespace. */
var reTrimStart = /^\s+/;

/**
 * The base implementation of `_.trim`.
 *
 * @private
 * @param {string} string The string to trim.
 * @returns {string} Returns the trimmed string.
 */
function baseTrim(string) {
  return string
    ? string.slice(0, _trimmedEndIndex(string) + 1).replace(reTrimStart, '')
    : string;
}

var _baseTrim = baseTrim;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject;

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike_1(value) && _baseGetTag(value) == symbolTag);
}

var isSymbol_1 = isSymbol;

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol_1(value)) {
    return NAN;
  }
  if (isObject_1(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject_1(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = _baseTrim(value);
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

var toNumber_1 = toNumber;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0,
    MAX_INTEGER = 1.7976931348623157e+308;

/**
 * Converts `value` to a finite number.
 *
 * @static
 * @memberOf _
 * @since 4.12.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted number.
 * @example
 *
 * _.toFinite(3.2);
 * // => 3.2
 *
 * _.toFinite(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toFinite(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toFinite('3.2');
 * // => 3.2
 */
function toFinite(value) {
  if (!value) {
    return value === 0 ? value : 0;
  }
  value = toNumber_1(value);
  if (value === INFINITY || value === -INFINITY) {
    var sign = (value < 0 ? -1 : 1);
    return sign * MAX_INTEGER;
  }
  return value === value ? value : 0;
}

var toFinite_1 = toFinite;

/**
 * Converts `value` to an integer.
 *
 * **Note:** This method is loosely based on
 * [`ToInteger`](http://www.ecma-international.org/ecma-262/7.0/#sec-tointeger).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted integer.
 * @example
 *
 * _.toInteger(3.2);
 * // => 3
 *
 * _.toInteger(Number.MIN_VALUE);
 * // => 0
 *
 * _.toInteger(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toInteger('3.2');
 * // => 3
 */
function toInteger(value) {
  var result = toFinite_1(value),
      remainder = result % 1;

  return result === result ? (remainder ? result - remainder : result) : 0;
}

var toInteger_1 = toInteger;

/**
 * Checks if `value` is an integer.
 *
 * **Note:** This method is based on
 * [`Number.isInteger`](https://mdn.io/Number/isInteger).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an integer, else `false`.
 * @example
 *
 * _.isInteger(3);
 * // => true
 *
 * _.isInteger(Number.MIN_VALUE);
 * // => false
 *
 * _.isInteger(Infinity);
 * // => false
 *
 * _.isInteger('3');
 * // => false
 */
function isInteger(value) {
  return typeof value == 'number' && value == toInteger_1(value);
}

var isInteger_1 = isInteger;

/**
 * Created by René Simon<rene.simon@softgames.de> on 2020-02-12.
 * Copyright © Softgames 2020 - present
 */

// Consts according to documentation https://docs.mobage.com/display/JPPC/Billing+Items+With+Moba+Coin
const AMOUNT_LIMIT = 255;
const TOTAL_LIMIT = 50000;

async function createOrder({ skuId }) {
  const {
    data: { openIAPTransaction: token },
  } = await connection.query(
    `
      mutation openIAPTransaction($metaData: JSON!) {
          openIAPTransaction(metaData: $metaData)
      }
  `,
    {
      metaData: { skuId },
    },
  );
  if (!token || !isString$2(token) || !token.trim()) {
    throw new GBCPaymentError(
      ERROR_CODE.PURCHASE_UNKNOWN_ERROR,
      'Failed to retrieve token from game server in purchase process',
    );
  }
  const { orderId, userId } = getOrderAndUserIdByToken(token);
  return {
    token,
    orderId,
    userId,
  };
}

async function handleGenericPurchase({
  type,
  slug,
  amount,
  fetchInfo,
}) {
  if (!amount || !isInteger_1(amount) || amount < 0 || amount > AMOUNT_LIMIT) {
    throw new GBCErrorValidationFailed(
      `For mobage purchases the amount must be an integer between 1 and ${AMOUNT_LIMIT}`,
      123,
    );
  }
  const {
    id,
    displayName,
    skuId,
    price,
    imageUrl: rawImageUrl,
  } = await fetchInfo({
    slug,
  });
  if (!id) {
    throw new GBCConfigurationError(`No ${type} found with slug "${slug}"`);
  }
  if (!displayName) {
    throw new GBCConfigurationError(
      `No displayName configured for ${type} "${slug}"`,
    );
  }
  if (!skuId || !isInteger_1(skuId) || skuId < 0) {
    throw new GBCConfigurationError(
      `Configured mobage.skuId in ${type} "${slug}" meta data must be a positive integer`,
    );
  }
  if (!price || !isNumber$1(price) || price < 0) {
    throw new GBCConfigurationError(
      `Configured mobage.price in ${type} "${slug}" meta data must be positive number`,
    );
  }
  if (!rawImageUrl || !isUrl(rawImageUrl)) {
    throw new GBCConfigurationError(
      `Configured mobage.imageUrl in ${type} "${slug}" meta data must be a valid url`,
    );
  }
  const total = amount * price;
  if (total > TOTAL_LIMIT) {
    throw new GBCErrorValidationFailed(
      `For mobage purchases the amount (${amount}) * price (${price}) can not exceed ${TOTAL_LIMIT} (current: ${total})`,
      2345,
    );
  }

  const { token, orderId, userId } = await createOrder({
    skuId,
  });
  if (!orderId) {
    throw new GBCPaymentError(
      ERROR_CODE.PURCHASE_UNKNOWN_ERROR,
      'Failed to retrieve orderId from game server in purchase process',
    );
  }
  if (!userId) {
    throw new GBCPaymentError(
      ERROR_CODE.PURCHASE_UNKNOWN_ERROR,
      'Failed to retrieve userId from game server in purchase process',
    );
  }
  const imageUrl = prepareImageUrl({ url: rawImageUrl, token });
  const { orderId: purchaseOrderId } = await executePayment({
    skuId,
    name: displayName,
    price,
    imageUrl,
    amount,
  });
  if (!purchaseOrderId) {
    throw new GBCPaymentError(
      ERROR_CODE.PURCHASE_UNKNOWN_ERROR,
      'Failed to retrieve orderId from mobage in purchase process',
    );
  }
  if (purchaseOrderId !== orderId) {
    throw new GBCPaymentError(
      ERROR_CODE.PURCHASE_UNKNOWN_ERROR,
      'Game server and mobage orderId does not match',
    );
  }

  const { success } = await requestViaGadgetIo({
    url: getRestUrl({ extension: '/mobage/validatePurchase' }),
    body: {
      token,
      orderId,
      userId,
    },
  });
  if (!success) {
    throw new GBCPaymentError(
      ERROR_CODE.PURCHASE_UNKNOWN_ERROR,
      'Failed to validate purchase. Rolled back.',
    );
  }
}

function getOrderAndUserIdByToken(token) {
  const [uuid, userId] = token.split('_');
  return { userId, orderId: uuid.replace(/\W/g, '') };
}

function prepareImageUrl({ url, token }) {
  const [mainUrl] = url.split('#');
  return `${mainUrl}#${token}`;
}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2020-02-12.
 * Copyright © Softgames 2020 - present
 */

async function fetchItem$1({ slug }) {
  const {
    data: {
      currency: {
        id,
        displayName,
        metaData: { mobage: { skuId, price, imageUrl } = {} } = {},
      } = {},
    } = {},
  } = await connection.query(
    `
      query getCurrency($currencySlug: ID!) {
          currency(slug: $currencySlug) {
            id
            displayName
            metaData
          }
      }
  `,
    {
      currencySlug: slug,
    },
  );
  return { id, displayName, skuId, price, imageUrl };
}

async function handleCurrencyPurchase({ currencySlug, amount }) {
  await handleGenericPurchase({
    type: 'currency',
    slug: currencySlug,
    amount,
    fetchInfo: fetchItem$1,
  });
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-05-29.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * With this function you can execute a full currency purchase for a user.
 * The Game Backend will handle the purchase via the platform specific API and will add the amount of currency to the users wallet.
 * Since this is highly dependent on the platform, platform specific information and requirements follow. Only mentioned platforms are supported.
 *
 * #LINE
 * In LINE the purchase is using the users LINE COINS to proceed with the purchase.
 * For this the metaData of each in ADM for GB created currency must contain ```{line: {itemId: "string"}}``` where itemId is the id under which the currency is registered as item at LINE.
 * Furthermore you must provide the following data as providerSpecificData on each request
 * ```
 * {
 *     os: "string (IOS | AOS)",
      environment: "string (PRODUCTION | STAGING | SANDBOX) [check LINE API documentation for more information]",
      accessToken: "string (a valid accessToken as you already use for registerWithLineAccessToken)",
      title: "string (optional, just for tracking on LINE side)",
      memo: "string (optional, just for tracking on LINE side)"
 * }
 * ```
 *
 * @example
 * import {purchaseCurrency, ENVIRONMENT, OS} from '@sgorg/game-backend-client';
 *
 * // This example is based on LINE platform and must be adapted to your current platform
 * function getOs() {
 *     return isIOS() ? OS.IOS : OS.AOS;
 * }
 *
 * function getEnvironment() {
 *     if (isProduction()) {
 *         return ENVIRONMENT.PRODUCTION;
 *     }
 *     if (isStaging()) {
 *         return ENVIRONMENT.STAGING;
 *     }
 *     return ENVIRONMENT.SANDBOX;
 * }
 *
 * async function handleCurrencyPurchase({currencySlug, amount}) {
 *   const providerSpecificData = {
 *       os: getOs(),
 *       environment: getEnvironment(),
 *       accessToken: await getLineAccessTokenViaSomeWay(),
 *       title: "purchase-currency-${currencySlug}",
 *       memo: `Purchase of ${amount} times currency ${currencySlug}`
 *   };
 *   return purchaseCurrency({currencySlug, amount, providerSpecificData})
 * }
 *
 * @param {Object} options
 * @param {String} options.currencySlug Unique slug of the currency to purchase. Must have been created in GB via ADM
 * @param {Number} options.amount Amount of the specified currencySlug to purchase. Must be a positive number.
 * @param {Object} [options.providerSpecificData] Depending on your platform you have to provide specific addition information to execute a purchase
 * @return {Promise}
 * @memberof module:purchase
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const purchaseCurrency = wrapConnectionCheck(
  async ({ currencySlug, amount, providerSpecificData } = {}) => {
    if (!isString$2(currencySlug) || !currencySlug.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "currencySlug" parameter. "currencySlug" must be not empty string',
        'aCurrencyDisplayName',
      );
    }
    if (!amount || !isNumber(amount) || amount < 0) {
      throw new GBCErrorValidationFailed(
        'Invalid "amount" parameter. "amount" must be a number > 0',
        1,
      );
    }
    if (isMobageOpenSocial()) {
      await handleCurrencyPurchase({
        currencySlug,
        amount,
      });
      return;
    }
    await handleCurrencyPurchase$1({
      currencySlug,
      amount,
      providerSpecificData,
    });
  },
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2020-02-03.
 * Copyright © Softgames 2020 - present
 */

async function handleItemPurchase$1({
  itemSlug,
  amount,
  providerSpecificData,
}) {
  await connection.query(
    `
      mutation purchaseItem($itemSlug: ID!, $amount: Float!, $providerSpecificData: JSON) {
          purchaseItem(itemSlug: $itemSlug, amount: $amount, providerSpecificData: $providerSpecificData)
      }
  `,
    {
      itemSlug,
      amount,
      providerSpecificData,
    },
  );
}

/**
 * Created by René Simon<rene.simon@softgames.de> on 2020-02-12.
 * Copyright © Softgames 2020 - present
 */

async function fetchItem({ slug }) {
  const {
    data: {
      item: {
        id,
        displayName,
        metaData: { mobage: { skuId, price, imageUrl } = {} } = {},
      } = {},
    } = {},
  } = await connection.query(
    `
      query getItem($itemSlug: ID!) {
          item(slug: $itemSlug) {
            id
            displayName
            metaData
          }
      }
  `,
    {
      itemSlug: slug,
    },
  );
  return { id, displayName, skuId, price, imageUrl };
}

async function handleItemPurchase({ itemSlug, amount }) {
  await handleGenericPurchase({
    type: 'item',
    slug: itemSlug,
    amount,
    fetchInfo: fetchItem,
  });
}

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-05-29.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * With this function you can execute a full item purchase for a user.
 * The Game Backend will handle the purchase via the platform specific API and will add the amount of the item to the users item pool.
 * Since this is highly dependent on the platform, platform specific information and requirements follow. Only mentioned platforms are supported.
 *
 * #LINE
 * In LINE the purchase is using the users LINE COINS to proceed with the purchase.
 * For this the metaData of each in ADM for GB created item must contain ```{line: {itemId: "string"}}``` where itemId is the id under which the item is registered at LINE.
 * Furthermore you must provide the following data as providerSpecificData on each request
 * ```
 * {
 *     os: "string (IOS | AOS)",
      environment: "string (PRODUCTION | STAGING | SANDBOX) [check LINE API documentation for more information]",
      accessToken: "string (a valid accessToken as you already use for registerWithLineAccessToken)",
      title: "string (optional, just for tracking on LINE side)",
      memo: "string (optional, just for tracking on LINE side)"
 * }
 * ```
 *
 * @example
 * import {purchaseItem, ENVIRONMENT} from '@sgorg/game-backend-client';
 *
 * // This example is based on LINE platform and must be adapted to your current platform
 * function getOs() {
 *     return isIOS() ? 'IOS' : 'AOS';
 * }
 *
 * function getEnvironment() {
 *     if (isProduction()) {
 *         return ENVIRONMENT.PRODUCTION;
 *     }
 *     if (isStaging()) {
 *         return ENVIRONMENT.STAGING;
 *     }
 *     return ENVIRONMENT.SANDBOX;
 * }
 *
 * async function handleItemPurchase({itemSlug, amount}) {
 *   const providerSpecificData = {
 *       os: getOs(),
 *       environment: getEnvironment(),
 *       accessToken: await getLineAccessTokenViaSomeWay(),
 *       title: "purchase-item-${itemSlug}",
 *       memo: `Purchase of ${amount} times item ${itemSlug}`
 *   };
 *   return purchaseItem({itemSlug, amount, providerSpecificData})
 * }
 *
 * @param {Object} options
 * @param {String} options.itemSlug Unique slug of the item to purchase. Must have been created in GB via ADM
 * @param {Number} options.amount Amount of the specified item to purchase. Must be a positive number.
 * @param {Object} [options.providerSpecificData] Depending on your platform you have to provide specific addition information to execute a purchase
 * @return {Promise}
 * @memberof module:purchase
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const purchaseItem = wrapConnectionCheck(
  async ({ itemSlug, amount, providerSpecificData } = {}) => {
    if (!isString$2(itemSlug) || !itemSlug.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "itemSlug" parameter. "itemSlug" must be not empty string',
        'aItemDisplayName',
      );
    }
    if (!amount || !isNumber(amount) || amount < 0) {
      throw new GBCErrorValidationFailed(
        'Invalid "amount" parameter. "amount" must be a number > 0',
        1,
      );
    }
    if (isMobageOpenSocial()) {
      await handleItemPurchase({
        itemSlug,
        amount,
      });
      return;
    }
    await handleItemPurchase$1({
      itemSlug,
      amount,
      providerSpecificData,
    });
  },
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * With this function you can store data regarding a purchase in the Game Backend for the current user
 *
 * @example
 * import {registerPurchase} from '@sgorg/game-backend-client';
 * async function trackPurchase(someDataToStore) {
 *   return registerPurchase({payload: someDataToStore})
 * }
 *
 * @param {Object} options
 * @param {Object} options.payload Payload to store about the purchase
 * @return {Promise}
 * @memberof module:purchase
 *
 * @throws {GBCErrorNoConnection}
 */
const registerPurchase = wrapConnectionCheck(async ({ payload }) => {
  const token = JSON.stringify(payload);
  await connection.query(
    `
      mutation addIAPToken($token: String!) {
          addIAPToken(token: $token)
      }
  `,
    {
      token,
    },
  );
});

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-08-18.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * With this function you can open purchase transaction. To a transaction we can attach data regarding a purchase in the Game Backend for the current user
 *
 * @example
 * import {openPurchaseTransaction} from '@sgorg/game-backend-client';
 * async function openPurchase(someDataToStore) {
 *   const transactionToken = openPurchaseTransaction({payload: someDataToStore})
 * }
 *
 * @param {Object} options
 * @param {Object} options.payload Payload to store about the purchase
 * @return {Promise<string>} Transaction token
 * @memberof module:purchase
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const openPurchaseTransaction = wrapConnectionCheck(async ({ payload }) => {
  if (!isPlainObject_1(payload)) {
    throw new GBCErrorValidationFailed(
      'Invalid "payload" parameter. "payload" must be plain object',
      { purchaseData: 'fooBar' },
    );
  }
  const token = await connection.query(
    `
      mutation openIAPTransaction($metaData: JSON!) {
          openIAPTransaction(metaData: $metaData)
      }
  `,
    {
      metaData: payload,
    },
  );
  return token;
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

var purchase = /*#__PURE__*/Object.freeze({
  __proto__: null,
  purchaseCurrency: purchaseCurrency,
  purchaseItem: purchaseItem,
  registerPurchase: registerPurchase,
  openPurchaseTransaction: openPurchaseTransaction
});

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-07-08.
 * Copyright © Softgames 2019 - present
 */

const BOT_SERVICE_PROVIDER_MAPPING = {
  [PROVIDER.FACEBOOK_INSTANT_GAME]: 'fb-instant',
  [PROVIDER.ODNOKLASSNIKI]: 'ok-notification',
  [PROVIDER.LINE]: 'line',
};

const BOT_SERVICE_SUPPORTED_PROVIDERS = Object.keys(
  BOT_SERVICE_PROVIDER_MAPPING,
);

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-07-08.
 * Copyright © Softgames 2019 - present
 */

const ATTRIBUTES_FOR_ACTION = {
  get: {
    users: true,
    deleted: true,
    _id: true,
    messageId: true,
    environment: true,
    sender: true,
    scheduledFor: true,
    gameSlug: true,
    createDate: true,
    updates: false,
    locale: true,
    __v: false,
  },
  create: {
    users: true,
    _id: true,
    messageId: true,
    sender: true,
    environment: true,
    scheduledFor: true,
    gameSlug: true,
    createDate: true,
    locale: true,
    __v: false,
  },
  delete: {},
};

function validateResponse(response, action) {
  const missingRequiredAttributes = [];
  const missingNotRequiredAttributes = [];

  Object.keys(ATTRIBUTES_FOR_ACTION[action]).forEach((item) => {
    if (!Object.keys(response).includes(item)) {
      if (ATTRIBUTES_FOR_ACTION[action][item]) {
        missingRequiredAttributes.push(item);
      } else {
        missingNotRequiredAttributes.push(item);
      }
    }
  });

  const extraAttributes = Object.keys(response).filter((item) =>
    Object.keys(ATTRIBUTES_FOR_ACTION[action]).includes(item),
  );

  if (missingRequiredAttributes.length > 0) {
    return -1;
  }
  if (missingNotRequiredAttributes.length > 0 || extraAttributes.length > 0) {
    return 1;
  }
  return 0;
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-07-08.
 * Copyright © Softgames 2019 - present
 */

const MAPPING = {
  id: {
    key: '_id',
    defaultValue: undefined,
  },
  provider: {
    key: 'environment',
    defaultValue: undefined,
    converter: (valueToConvert) =>
      Object.keys(BOT_SERVICE_PROVIDER_MAPPING).find(
        (key) => BOT_SERVICE_PROVIDER_MAPPING[key] === valueToConvert,
      ),
  },
  playerId: {
    key: 'sender',
    defaultValue: undefined,
  },
  recipientIds: {
    key: 'users',
    defaultValue: undefined,
  },
  messageId: {
    key: 'messageId',
    defaultValue: undefined,
  },
  locale: {
    key: 'locale',
    defaultValue: undefined,
  },
  gameId: {
    key: 'gameSlug',
    defaultValue: undefined,
  },
  createDate: {
    key: 'createDate',
    defaultValue: undefined,
  },
  scheduledFor: {
    key: 'scheduledFor',
    defaultValue: undefined,
  },
  deleted: {
    key: 'deleted',
    defaultValue: false,
  },
};

function convert(input) {
  return Object.keys(MAPPING).reduce((obj, key) => {
    const item = MAPPING[key];
    return {
      ...obj,
      [key]: fetchValue({ item, input }),
    };
  }, {});
}

function fetchValue({ item: { key, defaultValue, converter }, input }) {
  if (!Object.keys(input).includes(key)) {
    return defaultValue;
  }
  return converter ? converter(input[key]) : input[key];
}

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-07-03.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function to create scheduled bot message. Before creating scheduled messages we have to configure message in Bot Service.
 * Use _id from response if you would like to delete scheduled message.
 *
 * @example
 * import {createScheduledBotMessage, PROVIDER} from '@sgorg/game-backend-client';
 * async function createScheduledMessage() {
 *   const playerInfo = await window.FBInstant.player.getSignedPlayerInfoAsync();
 *   const newScheduledMessage = await createScheduledBotMessage({
 *     playerId: playerInfo.getPlayerID(),
 *     provider: PROVIDER.FACEBOOK_INSTANT_GAME,
 *     inHours: 24,
 *     recipientIds: ["fb-friend-player-id-1", "fb-friend-player-id-2"],
 *     messageId: "test",
 *     messageData: {someCustomMessageData: "Hello"},
 *     locale: "en",
 *   });
 *   console.log(newScheduledMessage);
 *   // {
 *   //    id: "5b20cd9d2a54eb9aa962b650",
 *   //    provider: "fbig",
 *   //    playerId: "1409417839185403",
 *   //    recipientIds: ["1409417839185403", "6709417839186703"],
 *   //    messageId: "test",
 *   //    messageData: {someCustomMessageData: "Hello"},
 *   //    gameId: "fish-story",
 *   //    locale: "en",
 *   //    createDate: "2018-06-13T07:54:05.375Z",
 *   //    scheduledFor: "2018-06-13T08:54:05.367Z",
 *   //    deleted: false,
 *   // }
 *   return newScheduledMessage;
 * }
 *
 * @param {Object} options
 * @param {String} options.playerId Facebook player id
 * @param {PROVIDER} options.provider Valid values: fbig, ok, line
 * @param {Number} [options.inHours] Number of hours until the message will be sent
 * @param {Number} [options.inMinutes] Number of minutes until the message will be sent
 * @param {Number} [options.inSeconds] Number of seconds until the message will be sent
 * @param {Array} options.recipientIds An array of Facebook ids that should receive the message
 * @param {String} options.messageId The messageId property of the message that should be sent. messageId should be taken from Bot Service.
 * @param {Object} options.messageData The message data to replace massage placeholders. For message data structure refer to Bot Message API {@link https://confluence.softgames.de/display/DEV/Scheduled+Messages+API#ScheduledMessagesAPI-msgDataObj}
 * @param {String} options.locale A two character lowercase locale string
 * @returns {Promise<BotMessage>}
 * @memberof module:bots
 *
 * @throws {GBCError}
 * @throws {GBCErrorValidationFailed}
 * @throws {GBCBotServiceError}
 */

const createScheduledBotMessage = wrapConnectionCheck(
  async ({
    playerId,
    provider,
    inHours = 0,
    inMinutes = 0,
    inSeconds = 0,
    recipientIds,
    messageId,
    messageData,
    locale,
  }) => {
    if (!isString$2(playerId) || !playerId.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "playerId" parameter. "playerId" should be not empty string',
        '3751273517',
      );
    }

    if (!Object.values(BOT_SERVICE_SUPPORTED_PROVIDERS).includes(provider)) {
      throw new GBCErrorValidationFailed(
        `Invalid "provider" parameter. "provider" should be one from ${BOT_SERVICE_SUPPORTED_PROVIDERS}. Check more for PLATFORM.`,
        'fb-instant',
      );
    }

    if (!isInteger_1(inHours) || inHours < 0) {
      throw new GBCErrorValidationFailed(
        `Invalid "inHours" parameter. "inHours" should be a positive integer or 0 or undefined.`,
        1,
      );
    }

    if (!isInteger_1(inMinutes) || inMinutes < 0) {
      throw new GBCErrorValidationFailed(
        `Invalid "inMinutes" parameter. "inMinutes" should be a positive integer or 0 or undefined.`,
        2,
      );
    }

    if (!isInteger_1(inSeconds) || inSeconds < 0) {
      throw new GBCErrorValidationFailed(
        `Invalid "inSeconds" parameter. "inSeconds" should be a positive integer or 0 or undefined.`,
        3,
      );
    }

    if (!inHours && !inMinutes && !inSeconds) {
      throw new GBCErrorValidationFailed(
        `You have to provide a valid value for at least on of "inHours", "inMinutes", "inSeconds".`,
        3,
      );
    }

    if (!isArray(recipientIds)) {
      throw new GBCErrorValidationFailed(
        'Invalid "recipientIds" parameter. "recipientIds" should be array of player ids',
        ['1716312612', '2782168124', '7128367812'],
      );
    }

    if (!isString$2(messageId) || !messageId.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "messageId" parameter. "messageId" should be not empty string',
        '3751273517',
      );
    }

    if (
      !isString$2(locale) ||
      !locale.trim() ||
      locale.length !== 2 ||
      locale !== locale.toLowerCase()
    ) {
      throw new GBCErrorValidationFailed(
        'Invalid "locale" parameter. "locale" should be a two character lowercase locale string',
        'en',
      );
    }

    if (messageData && !isPlainObject_1(messageData)) {
      throw new GBCErrorValidationFailed(
        'Invalid messageData parameter. "messageData" should be plain object',
        { customMessageData: 'Hello' },
      );
    }
    let requestOptions = null;
    try {
      requestOptions = {
        method: 'post',
        url: getBotServiceCreateScheduledMessageUrl({
          environment: BOT_SERVICE_PROVIDER_MAPPING[provider],
        }),
        auth: getBotServiceAuth(),
        data: {
          inHours,
          inMinutes,
          inSeconds,
          sender: playerId,
          userIds: recipientIds,
          messageId,
          messageData,
          locale,
        },
        responseType: 'json',
        responseEncoding: 'utf8',
        timeout: CREATE_SCHEDULED_BOT_MESSAGE_REQUEST_TIMEOUT,
      };

      const response = await axios$1(requestOptions);
      const { data: res } = response || {};
      if (validateResponse(res, 'create') < 0) {
        throw new GBCBotServiceError('Invalid response from bot service.');
      }
      return convert(res);
    } catch (error) {
      if (error.response) {
        const {
          data: {
            error: errorResponse = 'No error message provided from bot service',
          } = {},
          status,
        } = error.response || {};
        if (
          status === 500 &&
          errorResponse.includes('Found no fb message with messageId')
        ) {
          throw new GBCError(
            ERROR_CODE.NOT_FOUND,
            `Facebook messageId ${messageId} not found. Check bot service configuration.`,
          );
        }
        throw new GBCBotServiceError(
          `Bot service response with: ${errorResponse}`,
        );
      } else if (error.request) {
        throw new GBCBotServiceError(
          `No bot service server contact with request options:"\n${JSON.stringify(
            requestOptions,
            null,
            '  ',
          )}`,
        );
      } else {
        throw new GBCBotServiceError(error.message);
      }
    }
  },
);

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-07-03.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function to delete scheduled bot message. ID of the message to delete you can find in response from function getScheduledBotMessages.
 * When you try to delete not existing message function will throw error GBCError with status code ERROR_CODE.NOT_FOUND.
 *
 * @example
 * import {deleteScheduledBotMessage} from '@sgorg/game-backend-client';
 * async function deleteScheduledMessage() {
 *   await deleteScheduledBotMessage({scheduledMessageId: "1637815317311131"});
 *   console.log("Scheduled bot message has been deleted");
 * }
 *
 * @param {Object} options
 * @param {String} options.scheduledMessageId
 * @returns {Promise}
 * @memberof module:bots
 *
 * @throws {GBCError}
 * @throws {GBCErrorValidationFailed}
 * @throws {GBCBotServiceError}
 */

const deleteScheduledBotMessage = wrapConnectionCheck(
  async ({ scheduledMessageId }) => {
    if (!isString$2(scheduledMessageId) || !scheduledMessageId.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "scheduledMessageId" parameter. "scheduledMessageId" should be not empty string',
        '3751273517',
      );
    }

    let requestOptions = null;
    try {
      requestOptions = {
        method: 'delete',
        url: getBotServiceDeleteScheduledMessageUrl({ scheduledMessageId }),
        auth: getBotServiceAuth(),
        responseType: 'json',
        responseEncoding: 'utf8',
        timeout: DELETE_SCHEDULED_BOT_MESSAGE_REQUEST_TIMEOUT,
      };

      await axios$1(requestOptions);
      return;
    } catch (error) {
      if (error.response) {
        const {
          data: {
            error: errorResponse = 'No error message provided from bot service',
          } = {},
        } = error.response || {};
        if (error.response.status === 404) {
          throw new GBCError(
            ERROR_CODE.NOT_FOUND,
            `Scheduled message id ${scheduledMessageId} has not been found`,
          );
        } else {
          throw new GBCBotServiceError(
            `Bot service response with: ${errorResponse}`,
          );
        }
      } else if (error.request) {
        throw new GBCBotServiceError(
          `No bot service server contact with request options:"\n${JSON.stringify(
            requestOptions,
            null,
            '  ',
          )}`,
        );
      } else {
        throw new GBCBotServiceError(error.message);
      }
    }
  },
);

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-07-03.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Fetches all scheduled bot messages for current user from bot service. Use _id from response if you would like to delete scheduled message.
 *
 * @example
 * import {getScheduledBotMessages} from '@sgorg/game-backend-client';
 *
 * const playerInfo = await window.FBInstant.player.getSignedPlayerInfoAsync();
 * async function fetcScheduledBotMessages() {
 *   const messages = await getScheduledBotMessages({playerId: playerInfo.getID()});
 *   console.log(messages);
 *   // [{
 *   //    id: "5b20cd9d2a54eb9aa962b650",
 *   //    provider: "fbig",
 *   //    playerId: "1409417839185403",
 *   //    recipientIds: ["1409417839185403", "6709417839186703"],
 *   //    messageId: "test",
 *   //    messageData: {someCustomMessageData: "Hello"},
 *   //    gameId: "fish-story",
 *   //    locale: "en",
 *   //    createDate: "2018-06-13T07:54:05.375Z",
 *   //    scheduledFor: "2018-06-13T08:54:05.367Z",
 *   //    deleted: false,
 *   // }]
 *   return messages;
 * }
 *
 * @returns {Promise<Array<BotMessage>>}
 * @memberof module:bots
 *
 * @throws {GBCErrorValidationFailed}
 * @throws {GBCBotServiceError}
 */

const getScheduledBotMessages = wrapConnectionCheck(async ({ playerId }) => {
  if (!isString$2(playerId) || !playerId.trim()) {
    throw new GBCErrorValidationFailed(
      'Invalid "playerId" parameter. "playerId" should be not empty string',
      '3751273517',
    );
  }
  let requestOptions = null;
  try {
    requestOptions = {
      method: 'get',
      url: getBotServiceGetScheduledMessageUrl({ playerId }),
      auth: getBotServiceAuth(),
      responseType: 'json',
      responseEncoding: 'utf8',
      timeout: GET_SCHEDULED_BOT_MESSAGE_REQUEST_TIMEOUT,
    };

    const response = await axios$1(requestOptions);

    const { data: res = [] } = response || [];

    return res.map((item) => {
      if (validateResponse(item, 'get') < 0) {
        throw new GBCBotServiceError('Invalid response from bot service.', res);
      }
      return convert(item);
    });
  } catch (error) {
    if (error.response) {
      const {
        data: {
          error: errorResponse = 'No error message provided from bot service',
        } = {},
      } = error.response || {};
      throw new GBCBotServiceError(
        `Bot service response with: ${errorResponse}`,
      );
    } else if (error.request) {
      throw new GBCBotServiceError(
        `No bot service server contact with request options:"\n${JSON.stringify(
          requestOptions,
          null,
          '  ',
        )}`,
      );
    } else {
      throw new GBCBotServiceError(error.message);
    }
  }
});

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-06-14.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function to track conversion for bot messages. Parameter messageId should be taken from entry point data.
 *
 * @example
 * import {trackBotMessageConversion} from '@sgorg/game-backend-client';
 * async function trackBotConversion() {
 *   try{
 *     const entryPointData = FBInstant.getEntryPointData();
 *     await trackBotMessageConversion({messageId: entryPointData.messageId});
 *   } catch(error) {
 *     console.log('Error message', error.message);
 *     console.log('Error code', error.getCode());
 *   }
 * }
 *
 * @param {Object} options
 * @param {String} options.messageId bot message id from entry point data 'messageId'
 * @returns {Promise}
 * @memberof module:bots
 *
 * @throws {GBCBotServiceError}
 * @throws {GBCErrorValidationFailed}
 */

const trackBotMessageConversion = wrapConnectionCheck(
  async ({ messageId } = {}) => {
    if (!isString$2(messageId) || !messageId.trim()) {
      throw new GBCErrorValidationFailed(
        'Invalid "messageId" parameter. "messageId" should be not empty string',
        '3751273517',
      );
    }

    let requestOptions = null;
    try {
      requestOptions = {
        method: 'post',
        url: getBotServiceConversionUrl(),
        data: {
          messageId,
        },
        auth: getBotServiceAuth(),
        responseType: 'json',
        responseEncoding: 'utf8',
        timeout: TRACK_BOT_MESSAGE_CONVERSION_REQUEST_TIMEOUT,
      };

      const response = await axios$1(requestOptions);
      // ToDo: [RS] Check what the response is and add it to the docu or not return it
      // ToDo: [RS] Better yet move it to the GB-Game-Server and make a clean graphQL request here
      return response.data;
    } catch (error) {
      if (error.response) {
        const {
          data: {
            message:
              errorResponse = 'No error message provided from bot service',
          } = {},
        } = error.response || {};
        throw new GBCBotServiceError(
          `Bot service response with: ${errorResponse}`,
        );
      } else if (error.request) {
        throw new GBCBotServiceError(
          `No bot service server contact with request options:"\n${JSON.stringify(
            requestOptions,
            null,
            '  ',
          )}`,
        );
      } else {
        throw new GBCBotServiceError(error.message);
      }
    }
  },
);

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-06-14.
 * Copyright © Softgames 2019 - present
 */

var bots = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createScheduledBotMessage: createScheduledBotMessage,
  deleteScheduledBotMessage: deleteScheduledBotMessage,
  getScheduledBotMessages: getScheduledBotMessages,
  trackBotMessageConversion: trackBotMessageConversion
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2020-01-27.
 * Copyright © Softgames 2020 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function to get the current user's friend list as it is in GB
 *
 * @example
 * import {getFriends} from '@sgorg/game-backend-client';
 *
 * async function getAndLogFriends() {
 *   const friends = await getFriends();
 *   console.log(friends);
 *   // [{
 *   //   id: '123',
 *   //   handle: 'Max Mustermann',
 *   //   locale: 'en-US',
 *   //   playerId: '123123312313'
 *   // }]
 *   return friends;
 * }
 *
 * @return {Promise<Array<Friend>>}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 */
const getFriends = wrapConnectionCheck(async () => {
  const { data: { me: { friendList = [] } = {} } = {} } =
    await connection.query(`
    query getUserProfile {
      me {
        friendList {
          id
          handle
          locale
          playerId
        }
      }
    }
  `);
  return friendList;
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-03.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * This function fetches the amount of currency the current user has
 *
 * @example
 * import {getUserCurrencies} from '@sgorg/game-backend-client';
 *
 * async function fetchAndLogUserCurrencies() {
 *   const currencies = await getUserCurrencies();
 *   console.log(currencies);
 *   // [{
 *   //      id: '1',
 *   //      slug: 'currency1',
 *   //      displayName: 'Fancy Currency 1',
 *   //      balance: 112.23,
 *   // }]
 *   return currencies;
 * }
 *
 * @returns {Promise<Array<UserCurrency>>}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 */
const getUserCurrencies = wrapConnectionCheck(async () => {
  const { data: { me: { currencies = [] } = {} } = {} } =
    await connection.query(`
    query userCurrencies {
      me {
        currencies {
          id
          slug
          displayName
          balance
        }
      }
    }
  `);
  return currencies;
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * This function fetches the values of the data of the current user for all keys.
 *
 * @example
 * import {getUserData} from '@sgorg/game-backend-client';
 * async function fetchAndLogUserData() {
 *   const userData = await getUserData();
 *   console.log(userData);
 *   // There is no log here because it is game specific
 *   return userData;
 * }
 *
 * @returns {Promise<Object>}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 */
const getUserData = wrapConnectionCheck(async () => {
  const { data: { me: { userData = {} } = {} } = {} } = await connection.query(`
    query userData {
      me {
        userData
      }
    }
  `);
  return userData;
});

/**
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2020-04-23.
 * Copyright © Softgames 2020 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * This function fetches the values of the data of the current user for provided keys.
 *
 * @example
 * import {getUserDataByKeys} from '@sgorg/game-backend-client';
 * async function fetchAndLogUserData() {
 *   const userData = await getUserDataByKeys(['key1', 'key2', 'key3', 'key4']);
 *   console.log(userData);
 *   // {
 *   //   key1: "String value of a key",
 *   //   key2: true,
 *   //   key3: 1890
 *   //   key4: {abc: 123, xyz: 987},
 *   // }
 *   return userData;
 * }
 *
 * @param {Object} options
 * @param {Object} options.keys keys to retrive
 * @returns {Promise<Object>}
 * @memberof module:user
 *
 * @throws {GBCBadResponseError}
 * @throws {GBCErrorNoConnection}
 */
const getUserDataByKeys = wrapConnectionCheck(async (keys) => {
  const { data: { me: { userDataByKeys = {} } = {} } = {} } =
    await connection.query(
      `
    query userDataByKeys($keys: [String!]!) {
      me {
        userDataByKeys(keys: $keys)
      }
    }`,
      {
        keys,
      },
    );

  if (!isPlainObject_1(userDataByKeys)) {
    throw new GBCBadResponseError(
      `Response from GB is not a plain object. Provided type: ${typeof userDatabyKeys}`,
    );
  }
  return userDataByKeys;
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-06-03.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * This function fetches the amount of items the current user has
 *
 * @example
 * import {getUserItems} from '@sgorg/game-backend-client';
 *
 * async function fetchAndLogUserItems() {
 *   const items = await getUserItems();
 *   console.log(items);
 *   // [{
 *   //      id: '1',
 *   //      slug: 'item1',
 *   //      displayName: 'Fancy Item 1',
 *   //      amount: 112,
 *   // }]
 *   return items;
 * }
 *
 * @returns {Promise<Array<UserItem>>}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 */
const getUserItems = wrapConnectionCheck(async () => {
  const { data: { me: { items = [] } = {} } = {} } = await connection.query(`
    query userItems {
      me {
        items {
          id
          slug
          displayName
          amount
        }
      }
    }
  `);
  return items;
});

/**
 * @async
 * @function
 *
 * @description
 * This function remove specified keys of the current user data.
 *
 * @example
 * import {removeUserDataByKeys} from '@sgorg/game-backend-client';
 * async function fetchAndLogUserData() {
 *   const result = await removeUserDataByKeys(['key1', 'key2']);
 *   console.log(userData);
 *   // {
 *   //   deleted: {
 *   //     key1: true,
 *   //     key2: true,
 *   //   }
 *   // }
 *   return result;
 * }
 *
 * @param {Object} options
 * @param {Object} options.keys keys to retrive
 * @returns {Promise<Object>}
 * @memberof module:user
 *
 * @throws {GBCBadResponseError}
 * @throws {GBCErrorNoConnection}
 */
const removeUserDataByKeys = wrapConnectionCheck(async (keys) => {
  const { data } = await connection.query(
    `
    mutation removeUserData($keys: [String!]!) {
      removeUserData(keys: $keys)
    }`,
    {
      keys,
    },
  );

  if (!isPlainObject_1(data)) {
    throw new GBCBadResponseError(
      `Response from GB is not a plain object. Provided type: ${typeof data}`,
    );
  }
  return data;
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * With this function you can store data for the current user.
 * The given patch must be a plain object and will be shallow merged with the already existing data for the user in the GB.
 *
 * PLEASE NOTE:
 * a) Please use keys (not store the whole user state und one key)
 * b) Please check how often you save (there is no unlimited resources)
 * c) Maybe use timestamps and or versions for rollback scenarios
 *
 * @example
 * import {setUserData} from '@sgorg/game-backend-client';
 * async function setUserDataAndLog() {
 *   await setUserData({name:'Ben',id: 'sdfas2w123'})
 *     .then(()=>console.log('success'))
 *     .catch((e)=>console.log(e))
 * }
 *
 * @param {Object} options
 * @param {Object} options.patch patch object
 * @return {Promise}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 */
const setUserData = wrapConnectionCheck(async (patch) => {
  await connection.query(
    `
    mutation setUserData($data: JSON!) {
      setUserData(patch: $data)
    }
  `,
    {
      data: patch,
    },
  );
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function to update user information in the GB.
 * Currently we store in the GB just the handle (Name) and the locale of the user.
 * It is recommended that you execute this on game start to keep the user profile on the servers up to date.
 *
 * @example
 * import {setUserProfile} from '@sgorg/game-backend-client';
 *
 * async function setUserProfileAndLog() {
 *   await setUserProfile({handle: 'max', locale: 'en'})
 *     .then(()=>console.log('success'))
 *     .catch((e)=>console.log('failed'))
 * }
 *
 * @param {Object} options
 * @param {String} options.handle username or nick name etc
 * @param {String} options.locale language code (EN)
 * @return {Promise}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const setUserProfile = wrapConnectionCheck(async ({ handle, locale } = {}) => {
  if (!isString$2(handle)) {
    throw new GBCErrorValidationFailed(
      'Invalid "handle" parameter. "handle" should be string',
      'Mark Wolter',
    );
  }
  if (!isString$2(locale)) {
    throw new GBCErrorValidationFailed(
      'Invalid "locale" parameter. "locale" should be string',
      'de_DE',
    );
  }

  await connection.query(
    `
    mutation setUserProfile($userProfile: UserProfileInput!) {
      setUserProfile(userProfile: $userProfile)
    }
  `,
    {
      userProfile: {
        handle,
        locale,
      },
    },
  );
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 *@description
 *Use this function to update the friends list of the current user on the GB.
 *This friends list is used to provide an easy and performant way
 *to retrieve friends scores of leaderboards.

 PLEASE NOTE:
 It is imperative that you evoke this function on game start,
 *if your game is using “getFriendsScores” or leaderboard related features.


 Why?

 The syncFriendListByPlatformPlayerId function sends an array of friend ids
 *received from Facebook or one of the other platforms to the gb-game-server.
 *There they get remapped to our internal platform agnostic ids and
 *then stored into a table, linking them to the current user’s id.
 *Now, when the getFriendsScores function is called, it can look up the
 *appropriate entries in this table (the ones that are linked to the current user’s id)
 *and then use the retrieved ids - which correspond to the current user’s
 *friends - to look up their scores.

 So if you want to use the getFriendsScores function, you have to sync the friend
 *list from your platform with the representation in the database beforehand and
 *that’s exactly what this function does. The best time to do this would be at
 *the start of the game. If you don’t do this, the server will NOT be able to fetch
 *the correct friend ids from the table and therefore the result of the
 *getFriendsScores/leaderboard function will be outdated or empty.


 * @example
 * import {syncFriendListByPlatformPlayerId} from '@sgorg/game-backend-client';
 * function syncFriendListByPlatformPlayerIdAndLog() {
 *   await syncFriendListByPlatformPlayerId({friendList:['f1','f2']});
 * }
 *
 * @param {Object} options
 * @param {Array<User>} options.friendList friend lists
 * @return {Promise}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 * @throws {GBCErrorValidationFailed}
 */
const syncFriendListByPlatformPlayerId = wrapConnectionCheck(
  async ({ friendList }) => {
    if (!isArray(friendList)) {
      throw new GBCErrorValidationFailed(
        'Invalid "friendList" parameter. "friendList" should be array of player ids',
        '["1312312", "36364563", "3180771]',
      );
    }
    await connection.query(
      `
    mutation syncFriendListByPlatformPlayerId($friendList: [ID!]!) {
      syncFbInstantFriendList(friendList: $friendList)
    }
  `,
      {
        friendList,
      },
    );
  },
);

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

/**
 * @async
 * @function
 *
 * @description
 * Use this function to get the current user profile stored in GB
 *
 * @example
 * import {getUserProfile} from '@sgorg/game-backend-client';
 *
 * async function getUserAndLog() {
 *   const user = await getUserProfile();
 *   console.log(user);
 *   // {
 *   //   id: '123',
 *   //   handle: 'Max Mustermann',
 *   //   locale: 'en-US',
 *   //   subscribedToLeaderboards: true
 *   // }
 *   return user;
 * }
 *
 * @return {Promise<User>}
 * @memberof module:user
 *
 * @throws {GBCErrorNoConnection}
 */
const getUserProfile = wrapConnectionCheck(async () => {
  const {
    data: { me: { id, handle, locale, subscribedToLeaderboards } = {} } = {},
  } = await connection.query(`
    query getUserProfile {
      me {
        id
        handle
        locale
        subscribedToLeaderboards
      }
    }
  `);
  return {
    id,
    handle,
    locale,
    subscribedToLeaderboards,
  };
});

var user = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getFriends: getFriends,
  getUserCurrencies: getUserCurrencies,
  getUserData: getUserData,
  getUserDataByKeys: getUserDataByKeys,
  getUserItems: getUserItems,
  removeUserDataByKeys: removeUserDataByKeys,
  setUserData: setUserData,
  setUserProfile: setUserProfile,
  syncFriendListByPlatformPlayerId: syncFriendListByPlatformPlayerId,
  getUserProfile: getUserProfile,
  getPublicUserData: getPublicUserData
});

/**
 * Created by René Simon <rene.simon@softgames.de> on 2019-04-01.
 * Created by Daniel Pochert <daniel.pochert@softgames.de> on 2019-04-01.
 * Created by Bartosz Wróbel <bartosz.wrobel@softgames.de> on 2019-04-01.
 * Created by Benhur Davies <benhur.davies@softgames.de> on 2019-04-01.
 * Copyright © Softgames 2019 - present
 */

var index = {
  ...base,
  ...economy,
  ...enums,
  ...errors,
  ...events,
  ...leaderboard,
  ...messaging,
  ...notification,
  ...purchase,
  ...bots,
  ...user,
};

export default index;
export { ENVIRONMENT, ERROR_CODE, GBCBadResponseError, GBCBotServiceError, GBCConfigurationError, GBCError, GBCErrorNoConnection, GBCErrorValidationFailed, GBCGraphQLError, GBCInternalError, GBCInvalidCallError, GBCKnownGraphQLError, GBCNotAuthorizedError, GBCPaymentError, GBCTimeoutError, GBCUnknownError, GBCUnknownGraphQLError, GBCWebsocketError, GBCWrongCredentialsError, OS, PROVIDER, SEVERITY$3 as SEVERITY, acknowledgeAllMessages, acknowledgeMessage, acknowledgeNotifications, addCurrencies, addItems, close, closeEvent, createScheduledBotMessage, deleteScheduledBotMessage, errorEvent, executeRuntimeScript, fetchMessages, fetchNotifications, getCurrencyCatalog, getFriends, getFriendsScores, getGameConfig, getItemCatalog, getLeaderboardMetaData, getLeaderboardPostProcessorForUserData, getLeaderboardUserEntry, getPublicUserData, getScheduledBotMessages, getServerTimestamp, getTopScores, getUserCurrencies, getUserData, getUserDataByKeys, getUserItems, getUserProfile, getUsersEntry, getUsersScores, getVersion, isConnected, loginEvent, loginWithGameBackendCredentials, messageEvent, openPurchaseTransaction, postScore, purchaseCurrency, purchaseItem, registerPurchase, registerWithFacebookInstantGameSignature, registerWithLineAccessToken, registerWithMobageCode, registerWithMobageGadgetIo, registerWithOkQueryString, removeLeaderboardInstanceScore, removeLeaderboardScores, removeUserDataByKeys, sendMessage, setGaTracking, setLogLevel, setUserData, setUserProfile, subscribeToLeaderboards, syncFriendListByPlatformPlayerId, trackBotMessageConversion, transaction };
